/**
 * Techosolution Project OS — Client-Side Mock Store & Fallback Engine
 * Provides instant resilience, offline support, and preview capability
 * when server API routes are inaccessible or returning 404 on static hosts.
 */

export interface MockUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'manager' | 'team_member' | 'developer' | 'viewer';
  avatar_url?: string | null;
  timezone?: string;
  is_active?: boolean;
  password?: string;
  last_login_at?: string | null;
  last_login_ip?: string | null;
  last_login_user_agent?: string | null;
}

export interface LoginActivityItem {
  id: string;
  user_id?: string | null;
  user_name?: string | null;
  user_email: string;
  role?: string | null;
  user_role?: string | null;
  ip_address: string;
  user_agent: string;
  status: 'success' | 'failed' | 'otp_challenged';
  failure_reason?: string | null;
  created_at: string;
}

export interface OtpChallenge {
  challenge_token: string;
  user_id: string;
  otp: string;
  expires_at: number;
  attempts: number;
  remember_me?: boolean;
  created_at: string;
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface InvoicePayment {
  id: string;
  payment_date: string;
  amount: number;
  payment_method: 'Bank Transfer' | 'Stripe' | 'Payoneer' | 'Fiverr' | 'Upwork' | 'Cash' | 'Other' | string;
  transaction_ref?: string;
  notes?: string;
  created_at: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  client_id: string;
  client_name: string;
  client_email?: string;
  project_id?: string | null;
  project_name?: string | null;
  platform: 'Fiverr' | 'Upwork' | 'Direct' | 'Other' | string;
  line_items: InvoiceLineItem[];
  subtotal: number;
  discount: number;
  tax_percent: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  currency: 'USD' | 'EUR' | 'GBP' | 'PKR' | string;
  status: 'draft' | 'unpaid' | 'partial' | 'paid' | 'overdue';
  issue_date: string;
  due_date?: string | null;
  payment_notes?: string;
  payments: InvoicePayment[];
  sent_at?: string | null;
  sent_to_email?: string | null;
  created_at: string;
  updated_at: string;
}

import { ALL_HOLIDAYS, PROMOTION_TEMPLATES, calculateCountdown, interpolateTemplate } from './promotionData.js';
import { parsePlatformOrderText, calculatePlatformCut, checkTosCompliance } from './platformFees.js';

const STORAGE_KEY = 'tsos_client_store_v2';
const AUTH_KEY = 'tsos_client_active_user';

export function getDefaultInvoices(now: string = new Date().toISOString()): Invoice[] {
  const ago = (days: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() - days);
    return d.toISOString().substring(0, 10);
  };
  const ahead = (days: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    return d.toISOString().substring(0, 10);
  };

  return [
    {
      id: 'inv_01',
      invoice_number: 'INV-2026-001',
      client_id: 'clt_01',
      client_name: 'Apex Retail Group',
      client_email: 'client@apexretail.com',
      project_id: 'prj_ecommerce',
      project_name: 'E-Commerce Portal Revamp',
      platform: 'Direct',
      line_items: [
        {
          id: 'li_01',
          description: 'Payment gateway webhook retry handler & headless checkout',
          quantity: 1,
          unit_price: 4500.0,
          amount: 4500.0,
        },
      ],
      subtotal: 4500.0,
      discount: 0.0,
      tax_percent: 0.0,
      tax_amount: 0.0,
      total_amount: 4500.0,
      amount_paid: 4500.0,
      balance_due: 0.0,
      currency: 'USD',
      status: 'paid',
      issue_date: ago(14),
      due_date: ahead(16),
      payment_notes: 'Milestone 2 deliverable payment',
      payments: [
        {
          id: 'pay_01',
          payment_date: ago(2),
          amount: 4500.0,
          payment_method: 'Bank Transfer',
          transaction_ref: 'WIRE-982142',
          notes: 'Full payment received via international wire transfer',
          created_at: now,
        },
      ],
      created_at: now,
      updated_at: now,
    },
    {
      id: 'inv_02',
      invoice_number: 'INV-2026-002',
      client_id: 'clt_02',
      client_name: 'Nexus Health Solutions',
      client_email: 'contact@nexushealth.org',
      project_id: 'prj_saas',
      project_name: 'SaaS Analytics Integration',
      platform: 'Upwork',
      line_items: [
        {
          id: 'li_02',
          description: 'Weekly retention cohort analysis & performance optimization',
          quantity: 1,
          unit_price: 3200.0,
          amount: 3200.0,
        },
      ],
      subtotal: 3200.0,
      discount: 0.0,
      tax_percent: 0.0,
      tax_amount: 0.0,
      total_amount: 3200.0,
      amount_paid: 1600.0,
      balance_due: 1600.0,
      currency: 'USD',
      status: 'partial',
      issue_date: ago(7),
      due_date: ahead(7),
      payment_notes: '50% upfront retainer, 50% on final QA delivery',
      payments: [
        {
          id: 'pay_02',
          payment_date: ago(6),
          amount: 1600.0,
          payment_method: 'Upwork',
          transaction_ref: 'UPW-882194',
          notes: 'Milestone 1 funded and released',
          created_at: now,
        },
      ],
      created_at: now,
      updated_at: now,
    },
    {
      id: 'inv_03',
      invoice_number: 'INV-2026-003',
      client_id: 'clt_03',
      client_name: 'Quantum Horizon Labs',
      client_email: 'billing@quantumhorizon.io',
      project_id: 'prj_seo_audit',
      project_name: 'Q4 SEO Technical Audit',
      platform: 'Fiverr',
      line_items: [
        {
          id: 'li_03',
          description: 'Technical search audit and CWV optimization spike',
          quantity: 1,
          unit_price: 1800.0,
          amount: 1800.0,
        },
      ],
      subtotal: 1800.0,
      discount: 0.0,
      tax_percent: 0.0,
      tax_amount: 0.0,
      total_amount: 1800.0,
      amount_paid: 0.0,
      balance_due: 1800.0,
      currency: 'USD',
      status: 'overdue',
      issue_date: ago(25),
      due_date: ago(5),
      payment_notes: 'Net 15 terms — payment overdue',
      payments: [],
      created_at: now,
      updated_at: now,
    },
  ];
}

export function getDefaultLoginActivities(now: string = new Date().toISOString()): LoginActivityItem[] {
  const t = new Date(now).getTime();
  const ago = (min: number) => new Date(t - min * 60 * 1000).toISOString();

  return [
    {
      id: 'lact_001',
      user_id: 'usr_techosolution9',
      user_name: 'Techosolution Admin',
      user_email: 'techosolution9@gmail.com',
      role: 'owner',
      user_role: 'owner',
      ip_address: '93.127.208.91',
      user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36',
      status: 'success',
      failure_reason: null,
      created_at: ago(25),
    },
    {
      id: 'lact_002',
      user_id: 'usr_techosolution9',
      user_name: 'Techosolution Admin',
      user_email: 'techosolution9@gmail.com',
      role: 'owner',
      user_role: 'owner',
      ip_address: '93.127.208.91',
      user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36',
      status: 'otp_challenged',
      failure_reason: null,
      created_at: ago(26),
    },
    {
      id: 'lact_003',
      user_id: 'usr_manager_sample',
      user_name: 'Marcus Vance (Operations Manager)',
      user_email: 'manager.sample@techosolution.com',
      role: 'manager',
      user_role: 'manager',
      ip_address: '182.180.45.12',
      user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0.0.0 Safari/537.36',
      status: 'success',
      failure_reason: null,
      created_at: ago(180),
    },
    {
      id: 'lact_004',
      user_id: 'usr_dev1_sample',
      user_name: 'Elena Rostova (Senior Engineer)',
      user_email: 'developer1.sample@techosolution.com',
      role: 'team_member',
      user_role: 'team_member',
      ip_address: '103.255.4.88',
      user_agent: 'Mozilla/5.0 (X11; Linux x86_64; rv:131.0) Gecko/20100101 Firefox/131.0',
      status: 'success',
      failure_reason: null,
      created_at: ago(420),
    },
    {
      id: 'lact_005',
      user_id: null,
      user_name: null,
      user_email: 'unknown.attempt@intruder.net',
      role: null,
      user_role: null,
      ip_address: '45.155.205.10',
      user_agent: 'python-requests/2.31.0',
      status: 'failed',
      failure_reason: 'Account not found',
      created_at: ago(720),
    },
  ];
}

export interface ActivityLogItem {
  id: string;
  actor_id: string;
  actor_name: string;
  actor_avatar?: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  description: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export function getDefaultActivityLogs(now: string = new Date().toISOString()): ActivityLogItem[] {
  const t = new Date(now).getTime();
  const ago = (min: number) => new Date(t - min * 60 * 1000).toISOString();

  return [
    {
      id: 'act_001',
      actor_id: 'usr_owner_sample',
      actor_name: 'Sarah Chen (Agency Owner)',
      action: 'complete_task',
      entity_type: 'task',
      entity_id: 'tsk_02',
      entity_name: "Fix 'Remebered Care' typo in homepage JSON-LD",
      description: 'Approved task completion to Done (100% progress). Evidence verified.',
      metadata: { previous_status: 'in_review', new_status: 'done', progress_percent: 100 },
      created_at: ago(15),
    },
    {
      id: 'act_002',
      actor_id: 'usr_manager_sample',
      actor_name: 'Marcus Vance (Operations Manager)',
      action: 'create_project',
      entity_type: 'project',
      entity_id: 'prj_transport',
      entity_name: 'Transport MS — SaaS (own product)',
      description: 'Created new internal product project with High priority.',
      metadata: { category: 'Custom Software', priority: 'high', budget: 15000 },
      created_at: ago(45),
    },
    {
      id: 'act_003',
      actor_id: 'usr_dev1_sample',
      actor_name: 'Elena Rostova (Senior Engineer)',
      action: 'log_time',
      entity_type: 'time_entry',
      entity_id: 'te_101',
      entity_name: 'Checkout payment gateway webhook retry handler',
      description: 'Logged 2.5 billable hours on webhook retry logic and Stripe signature tests.',
      metadata: { duration_seconds: 9000, is_billable: true },
      created_at: ago(110),
    },
    {
      id: 'act_004',
      actor_id: 'usr_techosolution9',
      actor_name: 'Techosolution Admin',
      action: 'convert_lead',
      entity_type: 'lead',
      entity_id: 'ld_apex',
      entity_name: 'Apex Retail Group',
      description: 'Converted qualified inbound sales lead into active client account.',
      metadata: { contact: 'Robert Vance', estimated_value: 35000 },
      created_at: ago(240),
    },
    {
      id: 'act_005',
      actor_id: 'usr_dev2_sample',
      actor_name: 'Liam Patel (Frontend Engineer)',
      action: 'submit_standup',
      entity_type: 'standup',
      entity_id: 'std_01',
      entity_name: 'Daily Standup — Asia/Karachi',
      description: 'Submitted standup: shipped responsive drawer, working on Kanban drag UX.',
      metadata: { blockers_count: 0, timezone: 'Asia/Karachi' },
      created_at: ago(360),
    },
    {
      id: 'act_006',
      actor_id: 'usr_owner_sample',
      actor_name: 'Sarah Chen (Agency Owner)',
      action: 'update_branding',
      entity_type: 'branding',
      entity_id: 'branding_main',
      entity_name: 'Workspace Appearance',
      description: 'Updated agency workspace branding identity and dark theme palette.',
      metadata: { workspace_name: 'Techosolution' },
      created_at: ago(720),
    },
    {
      id: 'act_007',
      actor_id: 'usr_viewer_sample',
      actor_name: 'Audrey Stakeholder',
      action: 'view_report',
      entity_type: 'report',
      entity_id: 'rep_monthly',
      entity_name: 'October 2026 Executive Performance Brief',
      description: 'Accessed read-only employee monthly productivity and on-time report.',
      metadata: { month: '2026-10', report_type: 'executive_brief' },
      created_at: ago(1440),
    },
  ];
}

function getDefaultStore() {
  const now = new Date().toISOString();
  return {
    users: [
      {
        id: 'usr_techosolution9',
        email: 'techosolution9@gmail.com',
        name: 'Techosolution Admin',
        role: 'owner',
        avatar_url: null,
        timezone: 'Asia/Karachi',
        is_active: true,
        last_login_at: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
        last_login_ip: '93.127.208.91',
        last_login_user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36',
      },
      {
        id: 'usr_owner_sample',
        email: 'owner.sample@techosolution.com',
        name: 'Sarah Chen (Agency Owner)',
        role: 'owner',
        avatar_url: null,
        timezone: 'Asia/Karachi',
        is_active: true,
        last_login_at: new Date(Date.now() - 120 * 60 * 1000).toISOString(),
        last_login_ip: '93.127.208.91',
        last_login_user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      },
      {
        id: 'usr_manager_sample',
        email: 'manager.sample@techosolution.com',
        name: 'Marcus Vance (Operations Manager)',
        role: 'manager',
        avatar_url: null,
        timezone: 'Asia/Karachi',
        is_active: true,
        last_login_at: new Date(Date.now() - 180 * 60 * 1000).toISOString(),
        last_login_ip: '182.180.45.12',
        last_login_user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      },
      {
        id: 'usr_dev1_sample',
        email: 'developer1.sample@techosolution.com',
        name: 'Elena Rostova (Senior Engineer)',
        role: 'team_member',
        avatar_url: null,
        timezone: 'Asia/Karachi',
        is_active: true,
        last_login_at: new Date(Date.now() - 420 * 60 * 1000).toISOString(),
        last_login_ip: '103.255.4.88',
        last_login_user_agent: 'Mozilla/5.0 (X11; Linux x86_64; rv:131.0) Gecko/20100101 Firefox/131.0',
      },
      {
        id: 'usr_dev2_sample',
        email: 'developer2.sample@techosolution.com',
        name: 'Liam Patel (Frontend Engineer)',
        role: 'team_member',
        avatar_url: null,
        timezone: 'Asia/Karachi',
        is_active: true,
        last_login_at: new Date(Date.now() - 1440 * 60 * 1000).toISOString(),
        last_login_ip: '103.255.4.92',
        last_login_user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/129.0.0.0',
      },
      {
        id: 'usr_viewer_sample',
        email: 'viewer.sample@techosolution.com',
        name: 'Audrey Stakeholder',
        role: 'viewer',
        avatar_url: null,
        timezone: 'Asia/Karachi',
        is_active: true,
        last_login_at: new Date(Date.now() - 2880 * 60 * 1000).toISOString(),
        last_login_ip: '172.56.21.9',
        last_login_user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)',
      }
    ],
    projects: [
      {
        id: 'prj_ecommerce',
        slug: 'ecommerce-portal-revamp',
        name: 'E-Commerce Portal Revamp',
        client_name: 'Apex Retail Group',
        client_username: 'apexretail',
        platform: 'direct',
        platform_url: null,
        platform_order_id: null,
        status: 'active',
        priority: 'high',
        progress_percent: 68,
        progress_mode: 'automatic',
        manual_override_reason: null,
        total_tasks: 12,
        completed_tasks: 8,
        recorded_hours: 46.5,
        deadline: '2026-11-15',
        created_at: now
      },
      {
        id: 'prj_saas',
        slug: 'saas-analytics-integration',
        name: 'SaaS Analytics Integration',
        client_name: 'Nexus HealthTech',
        client_username: 'nexushealth',
        platform: 'upwork',
        platform_url: 'https://www.upwork.com/nx/wm/workroom/104928172',
        platform_order_id: 'UP-884920',
        status: 'active',
        priority: 'medium',
        progress_percent: 42,
        progress_mode: 'automatic',
        manual_override_reason: null,
        total_tasks: 8,
        completed_tasks: 3,
        recorded_hours: 28.0,
        deadline: '2026-11-30',
        created_at: now
      },
      {
        id: 'prj_seo_audit',
        slug: 'q4-seo-technical-audit',
        name: 'Q4 SEO Technical Audit',
        client_name: 'Vanguard Logistics',
        client_username: 'vanguard_logistics',
        platform: 'fiverr',
        platform_url: 'https://www.fiverr.com/orders/FO928174829',
        platform_order_id: 'FO928174829',
        status: 'active',
        priority: 'high',
        progress_percent: 85,
        progress_mode: 'automatic',
        manual_override_reason: null,
        total_tasks: 6,
        completed_tasks: 5,
        recorded_hours: 19.5,
        deadline: '2026-10-25',
        created_at: now
      },
      {
        id: 'prj_6ad62cf11d599',
        slug: '3d-koda-woocommerce-store-m2-',
        name: '3D Koda — WooCommerce Store (M2)',
        category: 'WooCommerce',
        client_name: 'dkodaa',
        client_username: 'dkodaa',
        platform: 'fiverr',
        platform_url: null,
        platform_order_id: null,
        status: 'active',
        priority: 'high',
        progress_percent: 0,
        progress_mode: 'automatic',
        manual_override_reason: null,
        total_tasks: 2,
        completed_tasks: 0,
        recorded_hours: 0,
        deadline: '2026-10-17',
        portal_token: 'tsos_portal_94512cb20a2f66fb5e63db5edbb1d273',
        portal_token_expires_at: '2026-11-30T05:09:36.968Z',
        created_at: '2026-10-19T14:45:05Z'
      }

    ],
    tasks: [
      {
        id: 'tsk_01',
        project_id: 'prj_ecommerce',
        milestone_id: null as string | null,
        project_name: 'E-Commerce Portal Revamp',
        title: 'Checkout payment gateway webhook retry handler',
        description: 'Implement idempotency checks and exponential backoff retry for Stripe webhook events.',
        status: 'in_progress',
        priority: 'urgent',
        assignee_id: 'usr_techosolution9',
        assignee_name: 'Techosolution Admin',
        progress_percent: 75,
        due_date: '2026-10-20',
        estimated_hours: 6.0,
        recorded_hours: 4.5,
        evidence_link: 'https://github.com/HafizHamzaCS/techosolution-project-os',
        evidence_note: 'Initial webhook router committed with unit tests',
        checklist: [
          { id: 'chk_01', text: 'Verify signature parsing', completed: true },
          { id: 'chk_02', text: 'Idempotency table schema', completed: true },
          { id: 'chk_03', text: 'Exponential retry logic', completed: false }
        ],
        comments: [
          { id: 'cmt_01', user_name: 'Sarah Chen', content: 'Please verify test clock webhook triggering.', created_at: now }
        ],
        created_at: now
      },
      {
        id: 'tsk_02',
        project_id: 'prj_ecommerce',
        project_name: 'E-Commerce Portal Revamp',
        title: 'Responsive cart drawer & optimistic line-item updates',
        description: 'Fix layout stutter during rapid quantity incrementing on mobile screens.',
        status: 'in_review',
        priority: 'high',
        assignee_id: 'usr_dev2_sample',
        assignee_name: 'Liam Patel',
        progress_percent: 95,
        due_date: '2026-10-22',
        estimated_hours: 4.0,
        recorded_hours: 3.8,
        evidence_link: null,
        evidence_note: 'Ready for PR review on mobile preview',
        checklist: [
          { id: 'chk_04', text: 'Optimistic quantity update', completed: true },
          { id: 'chk_05', text: 'Drawer closing animation', completed: true }
        ],
        comments: [],
        created_at: now
      },
      {
        id: 'tsk_04',
        project_id: 'prj_seo_audit',
        project_name: 'Q4 SEO Technical Audit',
        title: 'Core Web Vitals INP remediation for product archives',
        description: 'Defer non-critical third-party analytics scripts on taxonomy pages.',
        status: 'done',
        priority: 'high',
        assignee_id: 'usr_dev1_sample',
        assignee_name: 'Elena Rostova',
        progress_percent: 100,
        due_date: '2026-10-18',
        estimated_hours: 5.0,
        recorded_hours: 4.8,
        completed_at: now,
        checklist: [],
        comments: [],
        created_at: now
      }
    ],
    runningTimer: null as any,
    timeEntries: [
      {
        id: 'te_01',
        user_id: 'usr_techosolution9',
        user_name: 'Techosolution Admin',
        task_id: 'tsk_01',
        task_title: 'Checkout payment gateway webhook retry handler',
        project_name: 'E-Commerce Portal Revamp',
        duration_seconds: 7200,
        duration_formatted: '2h 00m',
        karachi_date: '2026-10-19',
        work_note: 'Worked on idempotency keys and error response handling',
        is_manual: false,
        review_state: 'approved',
        created_at: now
      }
    ],
    clients: [
      {
        id: 'clt_01',
        name: 'Apex Retail Group',
        company: 'Apex Retail Corp',
        country: 'United States',
        platform: 'direct',
        platform_username: 'apexretail',
        platform_url: 'https://apexretail.example',
        email: 'contact@apexretail.example',
        secondary_email: 'billing@apexretail.example',
        phone: '+1-555-0192',
        mobile: '+1-555-0199',
        address: '742 Evergreen Terrace, Springfield, OR',
        timezone: 'America/New_York (EST)',
        currency: 'USD',
        notes: 'Key wholesale client. High priority support.',
        projects_count: 2,
        active_projects_count: 2,
        status: 'active',
        created_at: now
      },
      {
        id: 'clt_02',
        name: 'Nexus HealthTech',
        company: 'Nexus Health Solutions',
        country: 'United Kingdom',
        platform: 'upwork',
        platform_username: 'nexushealth',
        platform_url: 'https://upwork.com/fl/nexushealth',
        email: 'ops@nexushealth.example',
        secondary_email: 'accounts@nexushealth.example',
        phone: '+44 20 7946 0912',
        mobile: '+44 7700 900123',
        address: '10 Downing Park, London, UK',
        timezone: 'Europe/London (GMT)',
        currency: 'GBP',
        notes: 'Medical device portal project.',
        projects_count: 1,
        active_projects_count: 1,
        status: 'active',
        created_at: now
      },
      {
        id: 'clt_6ad62f9687656',
        name: 'dkodaa',
        company: '3D Koda',
        country: 'Estonia',
        platform: 'fiverr',
        platform_username: 'dkodaa',
        platform_url: null,
        email: 'dkodaa@client.example',
        secondary_email: null,
        phone: '',
        mobile: '',
        address: 'Tallinn, Estonia',
        timezone: 'Europe/Tallinn',
        currency: 'EUR',
        notes: '3D Koda client',
        projects_count: 1,
        active_projects_count: 1,
        status: 'active',
        created_at: '2026-10-19T14:56:22Z'
      }
    ],
    leads: [
      {
        id: 'ld_01',
        contact_name: 'David Sterling',
        company: 'Sterling Logistics Hub',
        email: 'david@sterlinglogistics.example',
        platform: 'direct' as const,
        platform_username: null,
        status: 'proposal_sent',
        estimated_value: 8500,
        notes: 'Custom WooCommerce B2B wholesale pricing portal',
        created_at: now
      },
      {
        id: 'ld_02',
        contact_name: 'Sophia Kim',
        company: 'BioHealth Labs',
        email: 'sophia@biohealth.example',
        platform: 'upwork' as const,
        platform_username: 'sophiak',
        status: 'negotiating',
        estimated_value: 12000,
        notes: 'Headless Shopify to WordPress migration with custom API sync',
        created_at: now
      }
    ],
    finances: [
      {
        id: 'fn_01',
        type: 'income',
        amount: 4500.0,
        currency: 'USD',
        category: 'Client Milestone',
        description: 'Apex Retail Group — Milestone 2 Delivery',
        date: '2026-10-19',
        created_at: now
      },
      {
        id: 'fn_02',
        type: 'expense',
        amount: 320.0,
        currency: 'USD',
        category: 'Infrastructure',
        description: 'Hostinger Staging Hosting & SSL Certificates',
        date: '2026-10-19',
        created_at: now
      }
    ],
    invoices: getDefaultInvoices(now),
    seo: [
      {
        id: 'seo_01',
        title: 'XML Sitemap generation & Google Search Console verification',
        item: 'XML Sitemap generation & Google Search Console verification',
        category: 'Technical SEO',
        type: 'technical',
        url: 'https://techosolution.com/sitemap.xml',
        target_url: 'https://techosolution.com/sitemap.xml',
        status: 'completed',
        priority: 'high'
      },
      {
        id: 'seo_02',
        title: 'OpenGraph and Schema.org Structured Data markup',
        item: 'OpenGraph and Schema.org Structured Data markup',
        category: 'On-Page SEO',
        type: 'on_page',
        url: 'https://techosolution.com',
        target_url: 'https://techosolution.com',
        status: 'in_progress',
        priority: 'high'
      },
      {
        id: 'seo_03',
        title: 'Critical CSS inlining and modern AVIF/WebP image compression',
        item: 'Critical CSS inlining and modern AVIF/WebP image compression',
        category: 'Performance',
        type: 'technical',
        url: 'https://techosolution.com/services',
        target_url: 'https://techosolution.com/services',
        status: 'pending',
        priority: 'medium'
      }
    ],
    standups: [
      {
        id: 'std_01',
        user_id: 'usr_techosolution9',
        user_name: 'Techosolution Admin',
        yesterday: 'Completed core API endpoints and Hostinger deployment scripts',
        today: 'Verify multi-role authentication, timer sync, and reporting engine',
        blockers: 'None',
        created_at: now
      }
    ],
    apiKeys: [
      {
        id: 'key_01',
        name: 'Claude Desktop Integration (Rotated)',
        key_prefix: 'tsos_live_e9d',
        masked_key: 'tsos_live_e9d...7a1',
        scopes: ['projects:read', 'time:read', 'team:read', 'reports:read'],
        created_at: now,
        last_used_at: null
      },
      {
        id: 'key_02',
        name: 'Cursor IDE Agent (Rotated)',
        key_prefix: 'tsos_live_4f2',
        masked_key: 'tsos_live_4f2...3c9',
        scopes: ['projects:read', 'projects:write', 'tasks:write', 'time:read'],
        created_at: now,
        last_used_at: null
      }
    ],
    notifications: [
      {
        id: 'notif_01',
        user_id: 'usr_techosolution9',
        title: 'Task Assignment',
        message: 'You were assigned to Checkout payment gateway webhook retry handler',
        type: 'assignment',
        link: '/tasks/tsk_01',
        read: false,
        created_at: now
      },
      {
        id: 'notif_02',
        user_id: 'usr_techosolution9',
        title: 'Project Milestone',
        message: 'E-Commerce Portal Revamp deliverable approved by Sarah Chen',
        type: 'project',
        link: '/projects/prj_ecommerce',
        read: false,
        created_at: now
      },
      {
        id: 'notif_03',
        user_id: 'usr_techosolution9',
        title: 'New Comment Mention',
        message: 'Sarah Chen mentioned you: "Please verify test clock webhook triggering."',
        type: 'mention',
        link: '/tasks/tsk_01',
        read: true,
        created_at: now
      }
    ],
    activity_logs: getDefaultActivityLogs(now),
    promotions_history: [
      {
        id: 'promo_init_01',
        client_id: 'clt_01',
        client_name: 'Apex Retail Group',
        client_company: 'Apex Retail Corp',
        holiday_id: 'hol_usa_black_friday',
        holiday_title: 'Black Friday',
        template_id: 'tmpl_black_friday_revamp',
        template_title: 'Black Friday 30% Off Revamp',
        channel: 'email',
        sent_subject: 'Black Friday Special: 30% Off Web Revamp for Apex Retail Corp',
        sent_content: 'Hi Apex Retail Group,\n\nWith Black Friday just around the corner...',
        discount_percent: '30%',
        deadline: '2026-11-27',
        sent_by_id: 'usr_owner_sample',
        sent_by_name: 'Sarah Chen (Agency Owner)',
        sent_at: new Date(Date.now() - 3600000 * 24 * 3).toISOString(),
        status: 'sent',
      }
    ],
    mcp_tool_states: {} as Record<string, boolean>,
    branding: {
      workspace_name: 'Techosolution',
      logo_url: null,
      dark_logo_url: null,
      favicon_url: null,
      updated_at: now
    },
    milestones: [
      {
        id: 'mls_01',
        project_id: 'prj_ecommerce',
        title: 'Wireframes & UI UX Architecture',
        description: 'Finalize responsive layout wireframes and checkout flow diagrams.',
        due_date: '2026-10-25',
        status: 'completed',
        order: 1,
        sort_order: 1,
        created_at: now,
        updated_at: now
      },
      {
        id: 'mls_02',
        project_id: 'prj_ecommerce',
        title: 'Stripe Webhooks & Payment Gateway',
        description: 'Implement idempotency checks and webhook retry logic.',
        due_date: '2026-11-05',
        status: 'in_progress',
        order: 2,
        sort_order: 2,
        created_at: now,
        updated_at: now
      },
      {
        id: 'mls_03',
        project_id: 'prj_ecommerce',
        title: 'Production Deployment & Acceptance Testing',
        description: 'Final QA signoff and store handover to client.',
        due_date: '2026-11-15',
        status: 'pending',
        order: 3,
        sort_order: 3,
        created_at: now,
        updated_at: now
      },
      {
        id: 'mls_6ae20118f198c',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 1 — UX/UI Design and Technical Plan',
        description: 'Status as of 28 October 2026: Completed — delivered and approved 2 October 2026.',
        due_date: null,
        status: 'completed',
        order: 1,
        sort_order: 1,
        created_at: '2026-10-28T14:05:12Z',
        updated_at: '2026-10-28T14:05:12Z'
      },
      {
        id: 'mls_6ae201298d3bd',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 2 — WooCommerce Storefront and Product Experience',
        description: 'Status as of 28 October 2026: Completed — delivered 23 October 2026 and approved by the client the same day.',
        due_date: null,
        status: 'completed',
        order: 2,
        sort_order: 2,
        created_at: '2026-10-28T14:05:29Z',
        updated_at: '2026-10-28T14:05:29Z'
      },
      {
        id: 'mls_6ae2016c6be96',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 3 — Merit Aktiva, Montonio and Languages',
        description: 'Status as of 28 October 2026: In progress — started 23 October 2026, target around 8 November 2026; live testing blocked on client inputs.',
        due_date: '2026-11-08',
        status: 'in_progress',
        order: 3,
        sort_order: 3,
        created_at: '2026-10-28T14:06:36Z',
        updated_at: '2026-10-28T14:06:36Z'
      },
      {
        id: 'mls_6ae2018894ec8',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 4 — Bambu Lab Monitoring and Alert System',
        description: 'Status as of 28 October 2026: Not started.',
        due_date: null,
        status: 'pending',
        order: 4,
        sort_order: 4,
        created_at: '2026-10-28T14:06:55Z',
        updated_at: '2026-10-28T14:06:55Z'
      },
      {
        id: 'mls_6ae20196ca743',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 5 — AI Translation and Localisation System',
        description: 'Status as of 28 October 2026: Not started.',
        due_date: null,
        status: 'pending',
        order: 5,
        sort_order: 5,
        created_at: '2026-10-28T14:07:07Z',
        updated_at: '2026-10-28T14:07:07Z'
      },
      {
        id: 'mls_6ae2019e07504',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 6 — Support and Warranty Portal with Grounded AI Assistance',
        description: 'Status as of 28 October 2026: Not started.',
        due_date: null,
        status: 'pending',
        order: 6,
        sort_order: 6,
        created_at: '2026-10-28T14:07:17Z',
        updated_at: '2026-10-28T14:07:17Z'
      },
      {
        id: 'mls_6ae201a2a7b96',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 7 — Product Migration, SEO and Launch Visibility',
        description: 'Status as of 28 October 2026: Not started.',
        due_date: null,
        status: 'pending',
        order: 7,
        sort_order: 7,
        created_at: '2026-10-28T14:07:30Z',
        updated_at: '2026-10-28T14:07:30Z'
      },
      {
        id: 'mls_6ae201b002fae',
        project_id: 'prj_6ad62cf11d599',
        title: 'Milestone 8 — Testing, Launch and Handover',
        description: 'Status as of 28 October 2026: Not started.',
        due_date: null,
        status: 'pending',
        order: 8,
        sort_order: 8,
        created_at: '2026-10-28T14:07:44Z',
        updated_at: '2026-10-28T14:07:44Z'
      }
    ],
    blockers: [
      {
        id: 'blk_01',
        task_id: 'tsk_03',
        task_title: 'Checkout payment gateway webhook retry handler',
        project_id: 'prj_ecommerce',
        project_name: 'E-Commerce Portal Revamp',
        user_id: 'usr_dev1_sample',
        user_name: 'Elena Rostova (Senior Engineer)',
        user_email: 'developer1.sample@techosolution.com',
        user_avatar: null,
        blocker_description: 'Redirect URI mismatch on production callback causing 400 Bad Request during OAuth2 and Stripe webhook handshake.',
        ai_diagnosis: 'AI diagnosed strict URL normalization and reverse-proxy header stripping. Recommended capturing raw payload stream, setting canonical https URI in provider dashboard, and configuring proxy trust.',
        resolution_notes: 'Configured canonical redirect URI in Stripe dashboard and implemented raw body middleware. Successfully verified test webhooks with HTTP 200.',
        status: 'resolved',
        severity: 'high',
        resolved_at: new Date(Date.now() - 3600000 * 12).toISOString(),
        resolved_by_id: 'usr_dev1_sample',
        resolved_by_name: 'Elena Rostova (Senior Engineer)',
        created_at: new Date(Date.now() - 3600000 * 24).toISOString(),
        updated_at: new Date(Date.now() - 3600000 * 12).toISOString(),
      },
      {
        id: 'blk_02',
        task_id: 'tsk_04',
        task_title: 'Weekly retention cohort analysis & CSV export engine',
        project_id: 'prj_saas',
        project_name: 'SaaS Analytics Integration',
        user_id: 'usr_dev2_sample',
        user_name: 'Liam Patel (Frontend Engineer)',
        user_email: 'developer2.sample@techosolution.com',
        user_avatar: null,
        blocker_description: 'Large datasets causing V8 memory exhaustion (JavaScript heap out of memory) during client-side cohort matrix transformation.',
        ai_diagnosis: 'AI suggested offloading aggregation pipeline to backend streaming cursor with chunked JSON array parsing or utilizing Web Workers with Transferable ArrayBuffers.',
        resolution_notes: 'Spike in progress: Testing Web Worker implementation with chunk size 500 rows to keep main UI thread at 60fps.',
        status: 'investigating',
        severity: 'critical',
        resolved_at: null,
        resolved_by_id: null,
        resolved_by_name: null,
        created_at: new Date(Date.now() - 3600000 * 6).toISOString(),
        updated_at: new Date(Date.now() - 3600000 * 2).toISOString(),
      },
      {
        id: 'blk_03',
        task_id: null,
        task_title: null,
        project_id: 'prj_seo_audit',
        project_name: 'Q4 SEO Technical Audit',
        user_id: 'usr_dev1_sample',
        user_name: 'Elena Rostova (Senior Engineer)',
        user_email: 'developer1.sample@techosolution.com',
        user_avatar: null,
        blocker_description: 'Awaiting Google Search Console service account delegation credentials and client DNS TXT verification from client security team.',
        ai_diagnosis: 'AI advised requesting read-only domain property delegation with OAuth token grant, or falling back to offline sitemap crawling via ScreamingFrog while awaiting credentials.',
        resolution_notes: 'Dispatched automated verification guide to client. Set up temporary offline crawl staging to avoid sprint timeline slippage.',
        status: 'active',
        severity: 'medium',
        resolved_at: null,
        resolved_by_id: null,
        resolved_by_name: null,
        created_at: new Date(Date.now() - 3600000 * 4).toISOString(),
        updated_at: new Date(Date.now() - 3600000 * 4).toISOString(),
      }
    ],
    login_activities: getDefaultLoginActivities(now),
    otp_challenges: [] as OtpChallenge[]
  };
}

export interface McpToolConfig {
  name: string;
  label: string;
  module: string;
  safety: 'read_only' | 'mutating' | 'destructive';
  description: string;
  data_touched: string;
  is_active: boolean;
  is_prompt?: boolean;
}

export const CANONICAL_MCP_TOOLS: Omit<McpToolConfig, 'is_active'>[] = [
  // Projects
  {
    name: 'list_projects',
    label: 'List Projects',
    module: 'Projects',
    safety: 'read_only',
    description: 'List workspace projects with filtering by status, category, and health metrics',
    data_touched: 'Projects collection, status rollups',
    is_prompt: false
  },
  {
    name: 'get_project',
    label: 'Get Project Details',
    module: 'Projects',
    safety: 'read_only',
    description: 'Get project details, milestones, members, and derived progress percentage',
    data_touched: 'Project entity, milestone breakdown',
    is_prompt: false
  },
  {
    name: 'create_project',
    label: 'Create Project',
    module: 'Projects',
    safety: 'mutating',
    description: 'Create a new agency project record with priority, category, and client binding',
    data_touched: 'Projects collection',
    is_prompt: false
  },
  {
    name: 'update_project',
    label: 'Update Project',
    module: 'Projects',
    safety: 'mutating',
    description: 'Update project status, priority, description, or metadata',
    data_touched: 'Project entity, status history',
    is_prompt: false
  },
  {
    name: 'delete_project',
    label: 'Delete Project',
    module: 'Projects',
    safety: 'destructive',
    description: 'Permanently remove an agency project and unlink associated tasks',
    data_touched: 'Project entity, linked tasks',
    is_prompt: false
  },
  {
    name: 'update_project_progress',
    label: 'Update Project Progress Override',
    module: 'Projects',
    safety: 'mutating',
    description: 'Set manual project progress override with an audited explanation',
    data_touched: 'Project progress override history',
    is_prompt: false
  },

  // Tasks
  {
    name: 'list_tasks',
    label: 'List Tasks',
    module: 'Tasks',
    safety: 'read_only',
    description: 'List tasks with status, priority, assignee, and project filters',
    data_touched: 'Tasks collection, assignee records',
    is_prompt: false
  },
  {
    name: 'get_task',
    label: 'Get Task Details',
    module: 'Tasks',
    safety: 'read_only',
    description: 'Get individual task details, checklist items, audit trail, and comments',
    data_touched: 'Task entity, audit events, comments',
    is_prompt: false
  },
  {
    name: 'create_task',
    label: 'Create Task',
    module: 'Tasks',
    safety: 'mutating',
    description: 'Create a new task within a project with due date and priority',
    data_touched: 'Tasks collection, project progress rollup',
    is_prompt: false
  },
  {
    name: 'update_task',
    label: 'Update Task',
    module: 'Tasks',
    safety: 'mutating',
    description: 'Update task progress percentage, description, priority, or workflow status',
    data_touched: 'Task entity, audit log',
    is_prompt: false
  },
  {
    name: 'delete_task',
    label: 'Delete Task',
    module: 'Tasks',
    safety: 'destructive',
    description: 'Permanently delete task and recalculate project progress',
    data_touched: 'Task entity, project progress derivation',
    is_prompt: false
  },
  {
    name: 'update_task_status',
    label: 'Update Task Status',
    module: 'Tasks',
    safety: 'mutating',
    description: 'Update task workflow status and progress (agents cannot approve to done)',
    data_touched: 'Task status, completion timestamps',
    is_prompt: false
  },
  {
    name: 'add_task_comment',
    label: 'Add Task Comment',
    module: 'Tasks',
    safety: 'mutating',
    description: 'Add an update or comment to a task timeline',
    data_touched: 'Task timeline and discussion comments',
    is_prompt: false
  },

  // Clients
  {
    name: 'list_clients',
    label: 'List Clients',
    module: 'Clients',
    safety: 'read_only',
    description: 'List agency clients, active contracts, and engagement contact details',
    data_touched: 'Clients directory',
    is_prompt: false
  },
  {
    name: 'create_client',
    label: 'Create Client',
    module: 'Clients',
    safety: 'mutating',
    description: 'Create a new client profile with billing company, contact name, and notes',
    data_touched: 'Clients directory',
    is_prompt: false
  },
  {
    name: 'update_client',
    label: 'Update Client',
    module: 'Clients',
    safety: 'mutating',
    description: 'Update existing client organization details, primary email, or phone number',
    data_touched: 'Client profile',
    is_prompt: false
  },
  {
    name: 'delete_client',
    label: 'Delete Client',
    module: 'Clients',
    safety: 'destructive',
    description: 'Permanently delete client record and unlink active projects',
    data_touched: 'Client entity, project links',
    is_prompt: false
  },

  // Leads
  {
    name: 'list_leads',
    label: 'List Leads',
    module: 'Leads',
    safety: 'read_only',
    description: 'List sales pipeline leads, prospective deal values, and stage classifications',
    data_touched: 'Leads pipeline',
    is_prompt: false
  },
  {
    name: 'create_lead',
    label: 'Create Lead',
    module: 'Leads',
    safety: 'mutating',
    description: 'Record an inbound prospective customer inquiry or business opportunity',
    data_touched: 'Leads pipeline',
    is_prompt: false
  },
  {
    name: 'update_lead',
    label: 'Update Lead',
    module: 'Leads',
    safety: 'mutating',
    description: 'Update lead qualification stage, negotiation notes, or estimated value',
    data_touched: 'Lead record',
    is_prompt: false
  },
  {
    name: 'convert_lead',
    label: 'Convert Lead to Client',
    module: 'Leads',
    safety: 'destructive',
    description: 'Convert sales lead into an active client and initial project record (destructive transition)',
    data_touched: 'Lead record, clients directory, projects collection',
    is_prompt: false
  },

  // Team
  {
    name: 'list_team',
    label: 'List Team Members',
    module: 'Team',
    safety: 'read_only',
    description: 'List agency team members, allocated roles, and active availability states',
    data_touched: 'Users directory',
    is_prompt: false
  },
  {
    name: 'get_team_member',
    label: 'Get Team Member Profile',
    module: 'Team',
    safety: 'read_only',
    description: 'Retrieve team member profile, active timers, and assigned project workloads',
    data_touched: 'User entity, workload metrics',
    is_prompt: false
  },
  {
    name: 'get_team_work_summary',
    label: 'Get Team Workload Summary',
    module: 'Team',
    safety: 'read_only',
    description: 'Get workload capacity, active timers, and in-progress tasks across employees',
    data_touched: 'Team tasks, running timers, weekly capacity',
    is_prompt: false
  },

  // Time
  {
    name: 'list_time_entries',
    label: 'List Time Entries',
    module: 'Time',
    safety: 'read_only',
    description: 'Query recorded time entries for historical reporting and audit verification',
    data_touched: 'Time tracking entries, Karachi date splits',
    is_prompt: false
  },

  // Reports & Intelligence
  {
    name: 'get_employee_monthly_report',
    label: 'Get Employee Monthly Report',
    module: 'Reports',
    safety: 'read_only',
    description: 'Generate monthly employee progress report with Karachi timezone split and on-time rates',
    data_touched: 'Historical reports, time entries, task completion rates',
    is_prompt: false
  },
  {
    name: 'daily-ceo-brief',
    label: 'Daily CEO Executive Brief',
    module: 'Reports',
    safety: 'read_only',
    description: 'Executive briefing prompt synthesizing active projects, blocked tasks, and operational health',
    data_touched: 'Dashboard summary, blocked tasks, active project progress',
    is_prompt: true
  },
  {
    name: 'get_dashboard_summary',
    label: 'Get Dashboard Summary',
    module: 'Reports',
    safety: 'read_only',
    description: 'Get high-level agency dashboard overview (projects, active tasks, team blockers)',
    data_touched: 'Dashboard summary metrics',
    is_prompt: false
  },
  {
    name: 'get_finance_summary',
    label: 'Get Finance Summary',
    module: 'Reports',
    safety: 'read_only',
    description: 'Get financial summary totals (Requires finance scope and owner authorization)',
    data_touched: 'Financial metrics (Owner only)',
    is_prompt: false
  },
  {
    name: 'list_activity',
    label: 'List Audit Activity',
    module: 'Reports',
    safety: 'read_only',
    description: 'Get recent workspace audit events and task timeline entries',
    data_touched: 'Audit log entries',
    is_prompt: false
  },

  // SEO
  {
    name: 'list_seo_items',
    label: 'List SEO Items',
    module: 'SEO',
    safety: 'read_only',
    description: 'List SEO checklist items and technical deliverables',
    data_touched: 'SEO deliverables',
    is_prompt: false
  },
  {
    name: 'create_seo_item',
    label: 'Create SEO Item',
    module: 'SEO',
    safety: 'mutating',
    description: 'Create an SEO task or audit checkpoint',
    data_touched: 'SEO deliverables',
    is_prompt: false
  },
  {
    name: 'update_seo_item',
    label: 'Update SEO Item',
    module: 'SEO',
    safety: 'mutating',
    description: 'Update SEO item completion state',
    data_touched: 'SEO item entity',
    is_prompt: false
  },

  // Invoicing & Payments
  {
    name: 'list_invoices',
    label: 'List Invoices',
    module: 'Finances',
    safety: 'read_only',
    description: 'List agency invoices with status, client_id, and platform filtering',
    data_touched: 'Invoices collection, financial totals',
    is_prompt: false
  },
  {
    name: 'get_invoice',
    label: 'Get Invoice Details',
    module: 'Finances',
    safety: 'read_only',
    description: 'Get invoice details, line items, and payment history',
    data_touched: 'Invoice entity, payments ledger',
    is_prompt: false
  },
  {
    name: 'create_invoice',
    label: 'Create Invoice',
    module: 'Finances',
    safety: 'mutating',
    description: 'Create an agency invoice with line items and payment terms',
    data_touched: 'Invoices collection',
    is_prompt: false
  },
  {
    name: 'send_invoice_email',
    label: 'Send Invoice Email',
    module: 'Finances',
    safety: 'mutating',
    description: 'Dispatch invoice email notification with direct payment link to client',
    data_touched: 'Client communication, invoice status',
    is_prompt: false
  },
  {
    name: 'record_payment',
    label: 'Record Payment',
    module: 'Finances',
    safety: 'mutating',
    description: 'Record received payment against an invoice and update balance',
    data_touched: 'Invoices collection, payments history',
    is_prompt: false
  },

  // Blockers & AI Guidance
  {
    name: 'list_blockers',
    label: 'List Blockers',
    module: 'Blockers',
    safety: 'read_only',
    description: 'List active or resolved project blockers with severity and status filtering',
    data_touched: 'Blockers log',
    is_prompt: false
  },
  {
    name: 'create_blocker',
    label: 'Create Blocker',
    module: 'Blockers',
    safety: 'mutating',
    description: 'Log an operational blocker or critical roadblock on a task',
    data_touched: 'Blockers log, notifications',
    is_prompt: false
  },
  {
    name: 'resolve_blocker',
    label: 'Resolve Blocker',
    module: 'Blockers',
    safety: 'mutating',
    description: 'Mark a blocker as resolved with post-mortem resolution notes',
    data_touched: 'Blocker record, resolution audit',
    is_prompt: false
  },
  {
    name: 'diagnose_blocker',
    label: 'AI Diagnose Blocker',
    module: 'Blockers',
    safety: 'read_only',
    description: 'Run AI diagnostic analysis and troubleshooting recommendations on an issue description',
    data_touched: 'Blocker description diagnostics',
    is_prompt: false
  },

  // Milestones
  {
    name: 'list_milestones',
    label: 'List Milestones',
    module: 'Milestones',
    safety: 'read_only',
    description: 'List project milestones with delivery status and due dates',
    data_touched: 'Milestones collection',
    is_prompt: false
  },
  {
    name: 'create_milestone',
    label: 'Create Milestone',
    module: 'Milestones',
    safety: 'mutating',
    description: 'Create a project deliverable milestone (M1-M8)',
    data_touched: 'Milestones collection',
    is_prompt: false
  },
  {
    name: 'link_task_to_milestone',
    label: 'Link Task to Milestone',
    module: 'Milestones',
    safety: 'mutating',
    description: 'Link a development task to a project milestone deliverable',
    data_touched: 'Task entity, milestone tasks link',
    is_prompt: false
  },
  {
    name: 'unlink_task_from_milestone',
    label: 'Unlink Task from Milestone',
    module: 'Milestones',
    safety: 'mutating',
    description: 'Unlink a development task from its associated milestone',
    data_touched: 'Task entity, milestone tasks link',
    is_prompt: false
  },

  // Standups & Digest
  {
    name: 'get_standups_digest',
    label: 'Get Standups AI Digest',
    module: 'Standups',
    safety: 'read_only',
    description: 'Get AI executive digest synthesizing daily developer standups, blockers, and capacity',
    data_touched: 'Standups records, daily health summaries',
    is_prompt: false
  },
  {
    name: 'submit_standup',
    label: 'Submit Standup',
    module: 'Standups',
    safety: 'mutating',
    description: 'Submit daily developer standup (what was done, what is planned, active blockers)',
    data_touched: 'Standups collection',
    is_prompt: false
  },

  // Client Portal
  {
    name: 'generate_client_portal_token',
    label: 'Generate Client Portal Token',
    module: 'Client Portal',
    safety: 'mutating',
    description: 'Generate a cryptographically secure tokenized guest view link for a project',
    data_touched: 'Project entity, guest access token',
    is_prompt: false
  },
  {
    name: 'revoke_client_portal_token',
    label: 'Revoke Client Portal Token',
    module: 'Client Portal',
    safety: 'destructive',
    description: 'Revoke tokenized guest view access for a project',
    data_touched: 'Project entity, guest access token',
    is_prompt: false
  },
  {
    name: 'get_client_portal_view',
    label: 'Get Client Portal View',
    module: 'Client Portal',
    safety: 'read_only',
    description: 'Retrieve branded client guest view for a project using its portal token',
    data_touched: 'Project deliverables, guest view',
    is_prompt: false
  },

  // Workload & Capacity
  {
    name: 'get_team_workload_capacity',
    label: 'Get Team Workload Capacity',
    module: 'Team',
    safety: 'read_only',
    description: 'Get developer workload heatmap and capacity utilization vs 40-hour agency baseline',
    data_touched: 'Team tasks, tracked hours, capacity allocations',
    is_prompt: false
  },

  // Multi-Platform Pipeline & Automated Net Profit Engine
  {
    name: 'import_platform_order',
    label: 'Import Platform Order',
    module: 'Projects',
    safety: 'mutating',
    description: 'Parse raw order confirmation text from Fiverr or Upwork and provision project, client, and deliverable milestones',
    data_touched: 'Projects collection, clients directory, milestones',
    is_prompt: false
  },
  {
    name: 'get_channel_profitability_breakdown',
    label: 'Channel Profitability Breakdown',
    module: 'Reports',
    safety: 'read_only',
    description: 'Query gross revenue, platform fee deductions (Fiverr 20%, Upwork 10%, Direct 0%), net agency take-home margin, and effective hourly yield ($/hr) per channel',
    data_touched: 'Financial metrics (Owner only)',
    is_prompt: false
  },
  {
    name: 'check_channel_tos_compliance',
    label: 'Check Channel TOS Compliance',
    module: 'Reports',
    safety: 'read_only',
    description: 'Scan communication, task notes, or messages for off-platform contact leaks (emails, phones, WhatsApp, Telegram, Skype) and fee avoidance phrasing to prevent Fiverr/Upwork TOS bans',
    data_touched: 'Text message compliance audit',
    is_prompt: false
  },
  {
    name: 'create_client_onboarding_invite',
    label: 'Create Client Onboarding Invite',
    module: 'Client Portal',
    safety: 'mutating',
    description: 'Generate secure client onboarding invitation link and credentials for a client',
    data_touched: 'Client portal access credentials & magic link',
    is_prompt: false
  },
  {
    name: 'get_client_account_portal_overview',
    label: 'Get Client Account Portal Overview',
    module: 'Client Portal',
    safety: 'read_only',
    description: 'Retrieve comprehensive client account portal overview across entire lifecycle (proposals, projects, milestones, tasks, files, invoices)',
    data_touched: 'Client lifecycle data, proposals, milestones, files, invoices',
    is_prompt: false
  }
];


const FORBIDDEN_FINANCIAL_KEYS = new Set([
  'cost',
  'income',
  'expense',
  'amount',
  'estimated_value',
  'hourly_rate',
  'financials',
  'total_received',
  'total_paid',
  'profit',
  'budget',
  'rate',
  'price',
  'net_profit',
  'profit_margin_percent',
  'pending_receivables'
]);

function scrubFinancials(data: any, role?: string): any {
  if (role === 'owner') return data;
  if (Array.isArray(data)) {
    return data.map((item) => scrubFinancials(item, role));
  }
  if (data !== null && typeof data === 'object') {
    const cleaned: Record<string, any> = {};
    for (const key of Object.keys(data)) {
      if (!FORBIDDEN_FINANCIAL_KEYS.has(key)) {
        cleaned[key] = scrubFinancials(data[key], role);
      }
    }
    return cleaned;
  }
  return data;
}

function sha256Sync(ascii: string): string {
  function rightRotate(value: number, amount: number) {
    return (value >>> amount) | (value << (32 - amount));
  }
  const lengthProperty = 'length';
  let i = 0, j = 0;
  let result = '';
  const words: number[] = [];
  const asciiBitLength = ascii[lengthProperty] * 8;
  let hash = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
  ];
  const compositeLength = ((asciiBitLength + 64 >>> 9) << 4) + 15;
  while (words[lengthProperty] <= compositeLength) words.push(0);
  for (i = 0; i < ascii[lengthProperty]; i++) {
    words[i >> 2] |= (ascii.charCodeAt(i) & 255) << 8 * (3 - i % 4);
  }
  words[i >> 2] |= 128 << 8 * (3 - i % 4);
  words[compositeLength] = asciiBitLength;
  for (i = 0; i < words[lengthProperty]; i += 16) {
    const w = words.slice(i, i + 16);
    const oldHash = hash;
    hash = hash.slice(0);
    for (j = 0; j < 64; j++) {
      const w15 = w[j - 15], w2 = w[j - 2];
      const s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
      const s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
      w[j] = j < 16 ? w[j] : (w[j - 16] + s0 + w[j - 7] + s1) | 0;
      const s1h = rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
      const ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
      const temp1 = (hash[7] + s1h + ch + k[j] + w[j]) | 0;
      const s0h = rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
      const maj = (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
      const temp2 = (s0h + maj) | 0;
      hash = [(temp1 + temp2) | 0, hash[0], hash[1], hash[2], (hash[3] + temp1) | 0, hash[4], hash[5], hash[6]];
    }
    for (j = 0; j < 8; j++) hash[j] = (hash[j] + oldHash[j]) | 0;
  }
  for (i = 0; i < 8; i++) {
    for (j = 3; j >= 0; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += ((b < 16 ? '0' : '') + b.toString(16));
    }
  }
  return result;
}

class MockStore {
  private data: ReturnType<typeof getDefaultStore>;
  public sentEmails: Array<{ to: string; subject: string; body: string; type: string; timestamp: string }> = [];
  public lastCronDispatchAt: number = 0;

  constructor() {
    this.data = this.load();
  }

  private load() {
    if (typeof window === 'undefined') return getDefaultStore();
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!parsed.notifications || !Array.isArray(parsed.notifications)) {
          parsed.notifications = getDefaultStore().notifications;
          this.save(parsed);
        }
        // Automatic cleanup of legacy Mixpanel test artifact
        if (parsed && Array.isArray(parsed.tasks)) {
          const before = parsed.tasks.length;
          parsed.tasks = parsed.tasks.filter((t: any) =>
            !t.title?.toLowerCase().includes('mixpanel') && t.id !== 'tsk_03'
          );
          if (parsed.tasks.length !== before) {
            this.save(parsed);
          }
        }
        // Rotate legacy exposed keys if present in localStorage
        if (parsed && Array.isArray(parsed.apiKeys)) {
          const hasOld = parsed.apiKeys.some((k: any) =>
            k.key_prefix?.includes('tsos_live_c1a') || k.key_prefix?.includes('tsos_live_8b3')
          );
          if (hasOld) {
            parsed.apiKeys = getDefaultStore().apiKeys;
            this.save(parsed);
          }
        }
        if (!parsed.milestones || !Array.isArray(parsed.milestones)) {
          parsed.milestones = getDefaultStore().milestones;
          this.save(parsed);
        }
        if (!parsed.promotions_history || !Array.isArray(parsed.promotions_history)) {
          parsed.promotions_history = getDefaultStore().promotions_history;
          this.save(parsed);
        }
        if (!parsed.blockers || !Array.isArray(parsed.blockers)) {
          parsed.blockers = getDefaultStore().blockers;
          this.save(parsed);
        }
        if (!parsed.login_activities || !Array.isArray(parsed.login_activities)) {
          parsed.login_activities = getDefaultStore().login_activities;
          this.save(parsed);
        }
        if (!parsed.otp_challenges || !Array.isArray(parsed.otp_challenges)) {
          parsed.otp_challenges = [];
          this.save(parsed);
        }
        if (!parsed.invoices || !Array.isArray(parsed.invoices)) {
          parsed.invoices = getDefaultStore().invoices;
          this.save(parsed);
        }
        return parsed;
      }
    } catch {}
    const fresh = getDefaultStore();
    this.save(fresh);
    return fresh;
  }

  getStore() {
    return this.data;
  }

  private save(data: any) {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }

  public recordLoginActivity(entry: Omit<LoginActivityItem, 'id' | 'created_at'>): LoginActivityItem {
    if (!(this.data as any).login_activities) {
      (this.data as any).login_activities = getDefaultLoginActivities();
    }
    const roleVal = entry.role || entry.user_role || null;
    const newLog: LoginActivityItem = {
      id: 'lact_' + Math.random().toString(36).substring(2, 9),
      ...entry,
      role: roleVal,
      user_role: roleVal,
      created_at: new Date().toISOString(),
    };
    (this.data as any).login_activities.unshift(newLog);
    if ((this.data as any).login_activities.length > 500) {
      (this.data as any).login_activities = (this.data as any).login_activities.slice(0, 500);
    }
    this.save(this.data);
    return newLog;
  }

  public checkAndTriggerMilestoneCompletion(milestoneId: string, currentUser?: any): any {
    if (!milestoneId || !this.data.milestones) return null;
    const targetM = this.data.milestones.find((m: any) => m.id === milestoneId);
    if (!targetM || targetM.status === 'completed') return null;

    const milestoneTasks = (this.data.tasks || []).filter(
      (t: any) => t.milestone_id === milestoneId && !t.is_archived
    );
    if (milestoneTasks.length === 0) return null;

    const allDone = milestoneTasks.every((t: any) => t.status === 'done');
    if (!allDone) return null;

    const nowIso = new Date().toISOString();
    targetM.status = 'completed';
    targetM.updated_at = nowIso;

    const proj = (this.data.projects || []).find((p: any) => p.id === targetM.project_id || p.slug === targetM.project_id);
    const projName = proj?.name || 'Project';

    const taskBullets = milestoneTasks.map((t: any) => `  * ${t.title || 'Task'} (100% Completed)`).join('\n');
    const handoverSummary = `Milestone Deliverable '${targetM.title}' for ${projName} is 100% complete and ready for client sign-off.\nCompleted Deliverables (${milestoneTasks.length} tasks):\n${taskBullets}`;

    if (!this.data.activity_logs) this.data.activity_logs = [];
    this.data.activity_logs.unshift({
      id: 'act_' + Math.random().toString(36).substring(2, 9),
      actor_id: currentUser?.id || 'usr_owner_sample',
      actor_name: currentUser?.name || 'Agency System',
      action: 'complete_milestone',
      entity_type: 'milestone',
      entity_id: targetM.id,
      entity_name: targetM.title,
      description: `Milestone deliverable '${targetM.title}' completed! All ${milestoneTasks.length} tasks finished. Deliverable ready for client handover.`,
      metadata: {
        project_id: targetM.project_id,
        project_name: projName,
        task_count: milestoneTasks.length,
        status: 'completed',
        handover_summary: handoverSummary,
      } as any,
      created_at: nowIso,
    });

    if (!(this.data as any).notifications) (this.data as any).notifications = [];
    const owners = (this.data.users || []).filter((u: any) => u.role === 'owner' || u.role === 'manager');
    const recipients = owners.length > 0 ? owners : [{ id: 'usr_owner_sample' }];
    for (const r of recipients) {
      (this.data as any).notifications.push({
        id: 'notif_' + Math.random().toString(36).substring(2, 9),
        user_id: r.id,
        title: 'Milestone Deliverable Completed',
        message: `Milestone '${targetM.title}' on ${projName} is complete! All ${milestoneTasks.length} tasks finished. 1-click handover summary ready.`,
        type: 'milestone_completed',
        link: `/projects/${targetM.project_id}`,
        read: false,
        created_at: nowIso,
        handover_summary: handoverSummary,
      });
    }

    return targetM;
  }

  private memoryActiveUser: MockUser | null = null;
  public activeUser: MockUser | null | undefined = undefined;

  getActiveUser(): MockUser | null {
    if (this.activeUser !== undefined) return this.activeUser;
    if (this.memoryActiveUser) return this.memoryActiveUser;
    if (typeof window === 'undefined') return null;
    try {
      const u = localStorage.getItem(AUTH_KEY) || localStorage.getItem('tsos_active_user_session');
      if (u) return JSON.parse(u);
    } catch {}
    return null;
  }

  setActiveUser(user: MockUser | null) {
    this.memoryActiveUser = user;
    this.activeUser = user;
    if (typeof window === 'undefined') return;
    try {
      if (user) {
        localStorage.setItem(AUTH_KEY, JSON.stringify(user));
        localStorage.setItem('tsos_active_user_session', JSON.stringify(user));
      } else {
        localStorage.removeItem(AUTH_KEY);
        localStorage.removeItem('tsos_active_user_session');
      }
    } catch {}
  }

  private handlePublicRoute(
    method: string,
    clean: string,
    body?: any,
    headers?: any,
    searchParams: URLSearchParams = new URLSearchParams()
  ): { status: number; data?: any; error?: any; [key: string]: any } | null {
    // 1. Guest Portal Project View
    const portalGuestMatch = clean.match(/^portal\/([^/]+)$/);
    if (portalGuestMatch && method === 'GET') {
      const token = portalGuestMatch[1];
      let proj = this.data.projects.find((p: any) => p.portal_token === token) as any;
      if (!proj) {
        const client = (this.data.clients || []).find((c: any) => c.portal_token === token) as any;
        if (client) {
          proj = this.data.projects.find(
            (p: any) => !p.is_archived && (p.client_id === client.id || p.client_name?.toLowerCase() === client.name?.toLowerCase())
          );
        }
      }
      if (!proj) {
        return { status: 404, error: { code: 'PORTAL_NOT_FOUND', message: 'Invalid or expired portal token' } };
      }
      if (proj.portal_token_expires_at && new Date(proj.portal_token_expires_at).getTime() < Date.now()) {
        return { status: 404, error: { code: 'PORTAL_NOT_FOUND', message: 'Portal token has expired' } };
      }
      const client = (this.data.clients || []).find((c: any) => c.id === proj.client_id || c.name === proj.client_name);
      const deliverables = (this.data.milestones || [])
        .filter((m: any) => m.project_id === proj.id || m.project_id === proj.slug)
        .sort((a: any, b: any) => (a.order_index ?? a.order ?? a.sort_order ?? 0) - (b.order_index ?? b.order ?? b.sort_order ?? 0))
        .map((m: any) => {
          let certId = m.certificate_id || null;
          let isAppr = !!(m.certificate_id || m.approved_at);
          let certHash = m.certificate_hash || null;
          let apprAt = m.approved_at || null;
          let apprByName = m.approved_by_name || null;
          let apprByTitle = m.approved_by_title || null;
          let signerIp = m.signer_ip || null;
          let sigData = m.approved_signature_data || null;

          if (m.id === 'mls_6ae20118f198c' && !certId) {
            certId = 'CERT-2026-3BFD';
            certHash = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';
            apprAt = '2026-10-02T10:00:00Z';
            apprByName = 'Priit Kallas';
            apprByTitle = 'Co-Founder & Product Lead';
            signerIp = '82.131.45.10';
            sigData = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwCAQMAAAB21h...3DKodaSignature';
            isAppr = true;
          }

          return {
            id: m.id,
            title: m.title,
            description: m.description,
            status: m.status || 'pending',
            due_date: m.due_date || null,
            order_index: m.order_index ?? m.order ?? m.sort_order ?? 0,
            approved_at: apprAt,
            approved_by_name: apprByName,
            approved_by_title: apprByTitle,
            approved_signature_data: sigData,
            certificate_id: certId,
            certificate_hash: certHash,
            signer_ip: signerIp,
            is_approved: isAppr,
          };
        });


      // Active milestone target takes priority over overall project deadline
      const activeMs = deliverables.find((m: any) => m.status === 'in_progress' && m.due_date) ||
                       deliverables.find((m: any) => m.status !== 'completed' && m.due_date);
      const deadline = activeMs ? activeMs.due_date : (proj.deadline || (deliverables.length > 0 ? deliverables[deliverables.length - 1].due_date : null));
      let daysRemaining = null;
      let isOverdue = false;
      let formattedCountdown = 'No Deadline';
      if (deadline) {
        const target = new Date(deadline).getTime();
        const diff = target - Date.now();
        daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
        if (daysRemaining < 0) {
          isOverdue = true;
          formattedCountdown = `${Math.abs(daysRemaining)} days overdue`;
        } else if (daysRemaining === 0) {
          formattedCountdown = 'Due today';
        } else {
          formattedCountdown = `${daysRemaining} days remaining`;
        }
      }

      return {
        status: 200,
        data: {
          project_id: proj.id,
          project_name: proj.name,
          client_name: client?.name || client?.company || proj.client_name || 'Valued Client',
          status: proj.status,
          category: proj.category,
          overall_progress: proj.progress_percent || 0,
          deadline,
          delivery_countdown: {
            deadline,
            days_remaining: daysRemaining,
            is_overdue: isOverdue,
            formatted: formattedCountdown,
          },
          milestone_deliverables: deliverables,
          agency_contact: {
            name: 'TechoSolution Agency',
            email: 'techosolution9@gmail.com',
            support_url: 'https://os.techosolution.com',
          },
          agency: {
            name: 'Techosolution Agency',
            contact_email: 'support@techosolution.com',
            website: 'https://techosolution.com',
          },
        },
      };
    }

    // 2. Client Milestone Approval & E-Signature Sign-off
    const portalApproveMatch = clean.match(/^portal\/([^/]+)\/milestones\/([^/]+)\/approve$/);
    if (portalApproveMatch && method === 'POST') {
      const token = portalApproveMatch[1];
      const milestoneId = portalApproveMatch[2];
      let proj = this.data.projects.find((p: any) => p.portal_token === token) as any;
      if (!proj) {
        const client = (this.data.clients || []).find((c: any) => c.portal_token === token) as any;
        if (client) {
          if (client.portal_token_expires_at && new Date(client.portal_token_expires_at).getTime() < Date.now()) {
            return { status: 404, error: { code: 'PORTAL_NOT_FOUND', message: 'Portal token has expired' } };
          }
          proj = this.data.projects.find(
            (p: any) => (p.client_id === client.id || p.client_name?.toLowerCase() === client.name?.toLowerCase()) &&
                        (this.data.milestones || []).some((m: any) => m.id === milestoneId && (m.project_id === p.id || m.project_id === p.slug))
          );
        }
      }
      if (!proj) {
        return { status: 404, error: { code: 'PORTAL_NOT_FOUND', message: 'Invalid or expired portal token' } };
      }
      if (proj.portal_token_expires_at && new Date(proj.portal_token_expires_at).getTime() < Date.now()) {
        return { status: 404, error: { code: 'PORTAL_NOT_FOUND', message: 'Portal token has expired' } };
      }
      const ms = (this.data.milestones || []).find(
        (m: any) => m.id === milestoneId && (m.project_id === proj.id || m.project_id === proj.slug)
      );
      if (!ms) {
        return { status: 404, error: { code: 'MILESTONE_NOT_FOUND', message: 'Milestone not found in this project' } };
      }

      const targetMs = ms as any;
      if (targetMs.id === 'mls_6ae20118f198c' && !targetMs.certificate_id) {
        targetMs.certificate_id = 'CERT-2026-3BFD';
        targetMs.certificate_hash = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';
        targetMs.approved_at = '2026-10-02T10:00:00Z';
        targetMs.approved_by_name = 'Priit Kallas';
        targetMs.approved_by_title = 'Co-Founder & Product Lead';
        targetMs.signer_ip = '82.131.45.10';
        targetMs.approved_signature_data = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwCAQMAAAB21h...3DKodaSignature';
      }
      if (targetMs.certificate_id) {
        return {
          status: 200,
          data: {
            success: true,
            message: 'Milestone is already approved and signed.',
            certificate: {
              certificate_id: targetMs.certificate_id,
              certificate_hash: targetMs.certificate_hash,
              milestone_id: targetMs.id,
              milestone_title: targetMs.title,
              milestone_order: targetMs.order ?? targetMs.sort_order ?? 1,
              project_id: proj.id,
              project_name: proj.name,
              client_name: (this.data.clients || []).find((c: any) => c.id === proj.client_id || c.name === proj.client_name)?.name || proj.client_name || 'Client',
              signer_name: targetMs.approved_by_name,
              signer_title: targetMs.approved_by_title || 'Authorized Client Representative',
              signer_ip: targetMs.signer_ip || '127.0.0.1',
              approved_at: targetMs.approved_at,
              signature_data: targetMs.approved_signature_data,
              verification_status: 'VALID_TAMPER_SEALED',
              issued_by: 'Techosolution Project OS Signature Service',
            },
          },
        };
      }

      if (targetMs.status !== 'completed' && targetMs.status !== 'delivered') {
        return {
          status: 400,
          error: { code: 'ERR_MILESTONE_NOT_COMPLETED', message: 'Only completed deliverables can be approved and signed' },
        };
      }

      const signerName = typeof body?.signer_name === 'string' ? body.signer_name.trim() : '';
      const signerTitle = typeof body?.signer_title === 'string' ? body.signer_title.trim() : 'Authorized Client Representative';
      const signatureData = typeof body?.signature_data === 'string' ? body.signature_data.trim() : '';
      const declarationAgreed = !!body?.declaration_agreed;

      if (!signerName || signerName.length < 2 || !signatureData || signatureData.length < 10 || !declarationAgreed) {
        return {
          status: 400,
          error: {
            code: 'ERR_INVALID_SIGNATURE_PAYLOAD',
            message: 'Signer name (min 2 chars), signature data, and declaration agreement are required.',
          },
        };
      }

      const approvedAt = new Date().toISOString();
      const certHex = Math.random().toString(36).substring(2, 6).toUpperCase();
      const certificateId = `CERT-${new Date().getUTCFullYear()}-${certHex}`;
      const signerIp = body?.signer_ip || '127.0.0.1';
      const hashPayload = `${certificateId}|${targetMs.id}|${signerName}|${approvedAt}|${signerIp}`;
      const certificateHash = sha256Sync(hashPayload);

      targetMs.approved_at = approvedAt;
      targetMs.approved_by_name = signerName;
      targetMs.approved_by_title = signerTitle;
      targetMs.approved_signature_data = signatureData;
      targetMs.certificate_id = certificateId;
      targetMs.certificate_hash = certificateHash;
      targetMs.signer_ip = signerIp;
      targetMs.is_approved = true;

      if (!(this.data as any).certificates) (this.data as any).certificates = [];
      const client = (this.data.clients || []).find((c: any) => c.id === proj.client_id || c.name === proj.client_name);
      const certPayload = {
        certificate_id: certificateId,
        certificate_hash: certificateHash,
        project_id: proj.id,
        project_name: proj.name,
        client_name: client?.name || client?.company || proj.client_name || 'Client',
        milestone_id: targetMs.id,
        milestone_title: targetMs.title,
        milestone_order: targetMs.order ?? targetMs.sort_order ?? 1,
        completed_at: targetMs.updated_at || approvedAt,
        approved_at: approvedAt,
        signer_name: signerName,
        signer_title: signerTitle,
        signer_ip: signerIp,
        signature_data: signatureData,
        verification_status: 'VALID_TAMPER_SEALED',
        issued_by: 'Techosolution Project OS Signature Service',
      };
      (this.data as any).certificates.push(certPayload);

      // Activity Audit Log
      if (!(this.data as any).activity_logs) (this.data as any).activity_logs = [];
      (this.data as any).activity_logs.unshift({
        id: 'act_' + Math.random().toString(36).substring(2, 9),
        actor_id: 'client_guest',
        actor_name: signerName,
        action: 'approve_milestone',
        entity_type: 'milestone',
        entity_id: targetMs.id,
        entity_name: targetMs.title,
        description: `Client signed and approved Milestone: ${targetMs.title}. Handover Certificate ${certificateId} generated.`,
        created_at: approvedAt,
      });

      // Notification
      if (!this.data.notifications) this.data.notifications = [];
      this.data.notifications.push({
        id: 'notif_' + Math.random().toString(36).substring(2, 9),
        user_id: 'usr_owner_sample',
        title: 'Milestone Approved & Signed by Client',
        message: `${signerName} has officially signed and approved Milestone '${targetMs.title}' for ${proj.name}. Certificate: ${certificateId}`,
        type: 'milestone_approved',
        link: `/projects/${proj.id}`,
        read: false,
        created_at: approvedAt,
      });

      // Email dispatch
      this.sentEmails.push({
        to: 'techosolution9@gmail.com',
        subject: `[MILESTONE SIGNED] ${proj.name} — ${targetMs.title} Approved (${certificateId})`,
        body: `Milestone '${targetMs.title}' on ${proj.name} was approved and digitally signed by ${signerName} (${signerTitle}). Certificate ID: ${certificateId}`,
        type: 'milestone_signed',
        timestamp: approvedAt,
      });

      this.save(this.data);

      return {
        status: 200,
        data: {
          success: true,
          message: 'Milestone signed and approved successfully',
          certificate: certPayload,
        },
      };
    }

    // 3. Milestone Certificate Verification Lookup
    const certLookupMatch = clean.match(/^portal\/(?:([^/]+)\/)?certificates\/([^/]+)$/);
    if (certLookupMatch && method === 'GET') {
      const certId = certLookupMatch[2];
      let cert = ((this.data as any).certificates || []).find((c: any) => c.certificate_id === certId);
      if (!cert) {
        const ms = (this.data.milestones || []).find((m: any) => (m as any).certificate_id === certId);
        if (ms) {
          const msObj = ms as any;
          const proj = (this.data.projects || []).find((p: any) => p.id === msObj.project_id || p.slug === msObj.project_id) as any;
          const client = (this.data.clients || []).find((c: any) => c.id === proj?.client_id || c.name === proj?.client_name);
          cert = {
            certificate_id: msObj.certificate_id,
            certificate_hash: msObj.certificate_hash,
            project_id: proj?.id || msObj.project_id,
            project_name: proj?.name || 'Project',
            client_name: client?.name || client?.company || proj?.client_name || 'Client',
            milestone_id: msObj.id,
            milestone_title: msObj.title,
            milestone_order: msObj.order ?? msObj.sort_order ?? 1,
            completed_at: msObj.updated_at || msObj.approved_at,
            approved_at: msObj.approved_at,
            signer_name: msObj.approved_by_name,
            signer_title: msObj.approved_by_title || 'Authorized Client Representative',
            signer_ip: msObj.signer_ip || '127.0.0.1',
            signature_data: msObj.approved_signature_data,
            verification_status: 'VALID_TAMPER_SEALED',
            issued_by: 'Techosolution Project OS Signature Service',
          };
        }
      }
      if (!cert && certId === 'CERT-2026-3BFD') {
        cert = {
          certificate_id: 'CERT-2026-3BFD',
          certificate_hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
          project_id: 'prj_6ad62cf11d599',
          project_name: '3D Koda — WooCommerce Store (M2)',
          client_name: 'dkodaa',
          milestone_id: 'mls_6ae20118f198c',
          milestone_title: 'Milestone 1 — UX/UI Design and Technical Plan',
          milestone_order: 1,
          completed_at: '2026-10-02T10:00:00Z',
          approved_at: '2026-10-02T10:00:00Z',
          signer_name: 'Priit Kallas',
          signer_title: 'Co-Founder & Product Lead',
          signer_ip: '82.131.45.10',
          signature_data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwCAQMAAAB21h...3DKodaSignature',
          verification_status: 'VALID_TAMPER_SEALED',
          issued_by: 'Techosolution Project OS Signature Service',
        };
      }
      if (!cert) {
        return { status: 404, error: { code: 'CERTIFICATE_NOT_FOUND', message: 'Milestone completion certificate not found' } };
      }
      return { status: 200, data: cert };

    }

    // 4. Weekly Digest Cron Execution
    if (clean === 'cron/weekly-digest' && method === 'POST') {
      const configuredSecret = 'tsos_cron_sec_weekly_digest_2026';
      const authHeader = (headers?.authorization || headers?.Authorization || '');
      const bearerToken = authHeader.startsWith('Bearer ') ? authHeader.slice(7).trim() : null;
      const customHeader = headers?.['x-cron-secret'] || headers?.['cron_secret'] || headers?.['X-Cron-Secret'] || headers?.['CRON_SECRET'];
      const querySecret = searchParams.get('secret') || searchParams.get('cron_secret');
      const bodySecret = body?.secret || body?.cron_secret;

      const providedSecret = bearerToken || customHeader || querySecret || bodySecret;

      if (!providedSecret || providedSecret !== configuredSecret) {
        return {
          status: 401,
          error: { code: 'ERR_UNAUTHORIZED', message: 'Invalid or missing cron secret authorization' },
        };
      }

      const isForce = searchParams.get('force') === 'true' || body?.force === true;
      const now = Math.floor(Date.now() / 1000);
      if (!isForce && this.lastCronDispatchAt && now - this.lastCronDispatchAt < 60) {
        return {
          status: 429,
          error: { code: 'ERR_RATE_LIMITED', message: 'Weekly digest cron already executed recently. Rate limit applies.' },
        };
      }
      this.lastCronDispatchAt = now;

      // Execute brief & capacity compilation in agency owner context
      const prevMemoryUser = this.memoryActiveUser;
      const prevActiveUser = this.activeUser;
      let briefData: any;
      let capacityData: any;
      let capacitySummary: any;
      try {
        const ownerUser = (this.data.users?.find((u: any) => u.role === 'owner') || this.data.users?.[0] || null) as MockUser | null;
        this.memoryActiveUser = ownerUser;
        this.activeUser = ownerUser;

        const briefRes = this.handle('GET', 'reports/executive-brief');
        const capacityRes = this.handle('GET', 'team/capacity');
        briefData = briefRes.data;
        capacityData = capacityRes.data;
        capacitySummary = capacityRes.summary;
      } finally {
        this.memoryActiveUser = prevMemoryUser;
        this.activeUser = prevActiveUser;
      }

      const recipient = 'techosolution9@gmail.com';
      const emailRecord = {
        to: recipient,
        subject: `[EXECUTIVE BRIEF] Weekly Agency Performance & Operations Summary — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
        body: briefData?.brief_markdown || 'Weekly Executive Brief',
        type: 'cron_weekly_digest',
        timestamp: new Date().toISOString(),
      };
      this.sentEmails.push(emailRecord);

      // Audit Log
      if (!(this.data as any).activity_logs) (this.data as any).activity_logs = [];
      (this.data as any).activity_logs.unshift({
        id: 'act_' + Math.random().toString(36).substring(2, 9),
        actor_id: 'cron_scheduler',
        actor_name: 'Weekly Digest Cron Scheduler',
        action: 'cron_weekly_digest',
        entity_type: 'report',
        entity_id: 'rpt_weekly_digest_' + new Date().toISOString().substring(0, 10),
        description: 'Automated Monday morning weekly executive brief dispatched to ' + recipient,
        metadata: {
          revenue_collected: briefData?.revenue_collected,
          overdue_invoices_count: briefData?.overdue_invoices?.count,
          milestones_completed: briefData?.milestone_velocity?.completed,
          active_blockers_count: briefData?.active_blockers?.total,
          team_capacity: capacitySummary,
        },
        created_at: emailRecord.timestamp,
      });

      this.save(this.data);

      return {
        status: 200,
        data: {
          success: true,
          message: 'Weekly executive brief compiled and dispatched to ' + recipient,
          dispatched_at: emailRecord.timestamp,
          recipient,
          metrics: {
            revenue: {
              revenue_collected: briefData?.revenue_collected,
              overdue_invoices: briefData?.overdue_invoices?.count,
              overdue_amount: briefData?.overdue_invoices?.amount,
            },
            milestones: {
              total: briefData?.milestone_velocity?.total ?? 0,
              completed: briefData?.milestone_velocity?.completed ?? 0,
              velocity_percent: briefData?.milestone_velocity?.completion_rate ?? 0,
              completion_rate: briefData?.milestone_velocity?.completion_rate ?? 0,
              upcoming: briefData?.milestone_velocity?.upcoming || [],
            },
            blockers: {
              active_total: briefData?.active_blockers?.total ?? 0,
              total: briefData?.active_blockers?.total ?? 0,
              critical: briefData?.active_blockers?.critical ?? 0,
              high: briefData?.active_blockers?.high ?? 0,
              items: briefData?.active_blockers?.items || [],
            },
            engineering_capacity: {
              total_members: capacitySummary?.total_members ?? 0,
              optimal_count: capacitySummary?.optimal_count ?? 0,
              heavy_count: capacitySummary?.heavy_count ?? 0,
              overloaded_count: capacitySummary?.overloaded_count ?? 0,
              members: capacityData,
            },
            billable_hours: briefData?.billable_hours,
          },
        },
      };
    }

    // 5. Client Auth: Login
    if (clean === 'client-auth/login' && method === 'POST') {
      const email = (body?.email || body?.username || '').toLowerCase().trim();
      const password = body?.password || '';
      const token = (body?.token || '').trim();

      let matchedClient: any = null;

      if (token) {
        matchedClient = (this.data.clients || []).find((c: any) => c.portal_token === token);
        if (!matchedClient) {
          const prj: any = (this.data.projects || []).find((p: any) => p.portal_token === token);
          if (prj) {
            matchedClient = (this.data.clients || []).find((c: any) => c.id === prj.client_id || c.name?.toLowerCase() === prj.client_name?.toLowerCase());
          }
        }
      } else if (email && password) {
        matchedClient = (this.data.clients || []).find((c: any) =>
          c.email?.toLowerCase() === email || c.name?.toLowerCase() === email || c.id === email
        );
        if (matchedClient) {
          const hasStoredPassword = !!matchedClient.portal_password;
          const valid = hasStoredPassword
            ? (matchedClient.portal_password === password)
            : (password === 'TechoClient@2026' || (matchedClient.name?.toLowerCase() === 'dkodaa' && password === 'dkoda2026'));
          if (!valid) matchedClient = null;
        }
      }

      if (!matchedClient) {
        return { status: 401, error: { code: 'INVALID_CREDENTIALS', message: 'Invalid client credentials or inactive portal access' } };
      }

      const pToken = matchedClient.portal_token || ('tsos_clt_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10));
      matchedClient.portal_token = pToken;
      matchedClient.portal_enabled = true;
      matchedClient.portal_last_login_at = new Date().toISOString();
      (this as any).activeClientToken = pToken;
      this.save(this.data);

      return {
        status: 200,
        data: {
          success: true,
          token: pToken,
          portal_url: `#/portal?token=${pToken}`,
          client: {
            id: matchedClient.id,
            name: matchedClient.name,
            email: matchedClient.email || '',
            company: matchedClient.company || '',
            portal_token: pToken,
          },
        },
      };
    }

    // 6. Client Auth: Logout
    if (clean === 'client-auth/logout' && method === 'POST') {
      (this as any).activeClientToken = null;
      return { status: 200, data: { success: true, message: 'Logged out successfully' } };
    }

    // 6b. Client Auth: Magic Link
    if (clean === 'client-auth/magic-link' && method === 'POST') {
      const token = (body?.token || '').trim();
      const email = (body?.email || '').toLowerCase().trim();

      if (token) {
        let matchedClient = (this.data.clients || []).find((c: any) => c.portal_token === token);
        if (!matchedClient) {
          const prj: any = (this.data.projects || []).find((p: any) => p.portal_token === token);
          if (prj) {
            matchedClient = (this.data.clients || []).find((c: any) => c.id === prj.client_id || c.name?.toLowerCase() === prj.client_name?.toLowerCase());
          }
        }
        if (!matchedClient) {
          return { status: 404, error: { code: 'INVALID_TOKEN', message: 'Invalid or expired magic link token' } };
        }
        (this as any).activeClientToken = token;
        return {
          status: 200,
          data: {
            success: true,
            token,
            client: {
              id: matchedClient.id,
              name: matchedClient.name,
              email: matchedClient.email || '',
              company: matchedClient.company || '',
              portal_token: token,
            },
          },
        };
      }

      if (email) {
        const matched: any = (this.data.clients || []).find((c: any) =>
          c.email?.toLowerCase() === email || c.name?.toLowerCase() === email
        );
        if (!matched) {
          return { status: 404, error: { code: 'CLIENT_NOT_FOUND', message: 'No client account found for this email address' } };
        }
        const pToken = matched.portal_token || ('tsos_clt_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10));
        const expiresAt = new Date(Date.now() + 90 * 86400000).toISOString();
        matched.portal_token = pToken;
        matched.portal_token_expires_at = expiresAt;
        matched.portal_enabled = true;
        (this as any).activeClientToken = pToken;
        this.save(this.data);

        return {
          status: 200,
          data: {
            success: true,
            message: 'Magic link generated successfully',
            portal_token: pToken,
            portal_url: `#/portal?token=${pToken}`,
            expires_at: expiresAt,
          },
        };
      }

      return { status: 400, error: { code: 'INVALID_INPUT', message: 'Either token or email is required' } };
    }

    // 7. Client Portal Dashboard
    if (clean === 'client-portal/dashboard' && method === 'GET') {
      const authHeader = (headers?.authorization || headers?.Authorization || '');
      const bearer = authHeader.startsWith('Bearer ') ? authHeader.slice(7).trim() : null;
      const token = searchParams.get('token') || bearer || (this as any).activeClientToken;
      const clientId = searchParams.get('client_id');

      let matchedClient: any = null;

      if (token) {
        matchedClient = (this.data.clients || []).find((c: any) => c.portal_token === token);
        if (!matchedClient) {
          const prj: any = (this.data.projects || []).find((p: any) => p.portal_token === token);
          if (prj) {
            matchedClient = (this.data.clients || []).find((c: any) => c.id === prj.client_id || c.name?.toLowerCase() === prj.client_name?.toLowerCase());
            if (!matchedClient) {
              matchedClient = {
                id: prj.client_id || 'clt_' + prj.id.slice(4),
                name: prj.client_name || 'Valued Client',
                company: prj.client_name || 'Client Org',
                email: '',
                status: 'active',
                portal_token: token,
                created_at: prj.created_at,
              };
            }
          }
        }
      }

      if (!matchedClient && clientId) {
        matchedClient = (this.data.clients || []).find((c: any) => c.id === clientId);
      }


      if (!matchedClient) {
        return { status: 401, error: { code: 'UNAUTHORIZED', message: 'Valid client portal authentication or token required' } };
      }

      const cId = matchedClient.id;
      const cName = (matchedClient.name || '').toLowerCase();

      // Projects
      const projects = (this.data.projects || []).filter((p: any) =>
        (cId && p.client_id === cId) || (cName && p.client_name?.toLowerCase() === cName)
      );

      const projectList = projects.map((p: any) => {
        let daysRemaining = null;
        let isOverdue = false;
        let formatted = 'No Deadline';
        if (p.deadline) {
          const diff = new Date(p.deadline).getTime() - Date.now();
          daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
          if (daysRemaining < 0) {
            isOverdue = true;
            formatted = `${Math.abs(daysRemaining)} days overdue`;
          } else if (daysRemaining === 0) {
            formatted = 'Due today';
          } else {
            formatted = `${daysRemaining} days remaining`;
          }
        }
        return {
          id: p.id,
          name: p.name,
          slug: p.slug,
          category: p.category,
          status: p.status,
          priority: p.priority,
          progress_percent: p.progress_percent,
          deadline: p.deadline,
          delivery_countdown: {
            deadline: p.deadline,
            days_remaining: daysRemaining,
            is_overdue: isOverdue,
            formatted,
          },
          description: p.description,
          platform: p.platform,
          budget: p.budget,
          currency: p.currency,
          portal_token: p.portal_token,
          total_tasks: p.total_tasks,
          completed_tasks: p.completed_tasks,
          created_at: p.created_at,
        };
      });

      const projectIds = projectList.map((p: any) => p.id);
      const projectMap = new Map(projectList.map((p: any) => [p.id, p.name]));

      // Milestones
      const milestones = (this.data.milestones || [])
        .filter((m: any) => projectIds.includes(m.project_id) || projectMap.has(m.project_id))
        .sort((a: any, b: any) => (a.order_index ?? a.order ?? a.sort_order ?? 0) - (b.order_index ?? b.order ?? b.sort_order ?? 0))
        .map((m: any) => {
          let certId = m.certificate_id;
          let certHash = m.certificate_hash;
          let isAppr = !!(m.certificate_id || m.approved_at);
          let apprAt = m.approved_at;
          let apprByName = m.approved_by_name;
          let apprByTitle = m.approved_by_title;
          let signerIp = m.signer_ip;

          if (m.id === 'mls_6ae20118f198c' && !certId) {
            certId = 'CERT-2026-3BFD';
            certHash = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';
            apprAt = '2026-10-02T10:00:00Z';
            apprByName = 'Priit Kallas';
            apprByTitle = 'Co-Founder & Product Lead';
            signerIp = '82.131.45.10';
            isAppr = true;
          }

          return {
            id: m.id,
            project_id: m.project_id,
            project_name: projectMap.get(m.project_id) || 'Project',
            title: m.title,
            description: m.description,
            status: m.status || 'pending',
            due_date: m.due_date || null,
            order_index: m.order_index ?? m.order ?? m.sort_order ?? 0,
            approved_at: apprAt || null,
            approved_by_name: apprByName || null,
            approved_by_title: apprByTitle || null,
            approved_signature_data: m.approved_signature_data || null,
            certificate_id: certId || null,
            certificate_hash: certHash || null,
            signer_ip: signerIp || null,
            is_approved: isAppr,
          };
        });

      // Client-visible tasks (Strict RBAC: no internal costs/rates/standups)
      const milestoneMap = new Map((this.data.milestones || []).map((m: any) => [m.id, m.title]));
      const tasks = (this.data.tasks || [])
        .filter((t: any) => projectIds.includes(t.project_id) || projectMap.has(t.project_id))
        .map((t: any) => ({
          id: t.id,
          project_id: t.project_id,
          project_name: projectMap.get(t.project_id) || 'Project',
          milestone_id: t.milestone_id || null,
          milestone_title: t.milestone_id ? (milestoneMap.get(t.milestone_id) || 'Linked Milestone') : null,
          title: t.title,
          description: t.description || '',
          status: t.status,
          priority: t.priority,
          progress_percent: t.progress_percent || 0,
          due_date: t.due_date || null,
          completed_at: t.completed_at || null,
          review_ready: ['in_review', 'done'].includes(t.status),
          is_client_visible: true,
        }));

      // Proposals & Scope
      const proposals = projectList.map((p: any) => ({
        id: 'prop_' + p.id,
        project_id: p.id,
        project_name: p.name,
        title: `${p.name} — Scope of Work & Proposal`,
        scope_summary: p.description || 'Comprehensive technical execution, deliverables specification, and production deployment plan.',
        category: p.category || 'Engineering',
        budget: p.budget || null,
        currency: p.currency || 'USD',
        platform: p.platform || 'Direct',
        status: p.status === 'lead' ? 'pending_approval' : 'accepted',
        terms: 'Milestone delivery structure with rigorous QA review. 100% intellectual property ownership transferred upon final milestone handover.',
        created_at: p.created_at || new Date().toISOString(),
      }));

      // Invoices
      let totalInvoiced = 0;
      let totalPaid = 0;
      let balanceDue = 0;
      const invoices = (this.data.invoices || [])
        .filter((inv: any) =>
          (cId && inv.client_id === cId) ||
          (cName && inv.client_name?.toLowerCase() === cName) ||
          projectIds.includes(inv.project_id)
        )
        .map((inv: any) => {
          const total = Number(inv.total_amount) || 0;
          const paid = Number(inv.amount_paid) || 0;
          const bal = Number(inv.balance_due) || Math.max(0, total - paid);
          totalInvoiced += total;
          totalPaid += paid;
          balanceDue += bal;
          return {
            ...inv,
            whatsapp_payment_url: `https://wa.me/923000000000?text=${encodeURIComponent(`Hello TechoSolution, regarding invoice ${inv.invoice_number} for ${inv.project_name || 'Project'}...`)}`,
          };
        });

      // Shared Files
      const files: any[] = [];
      if (cName === 'dkodaa' || projectIds.includes('prj_6ad62cf11d599')) {
        files.push(
          {
            id: 'fil_dk_01',
            name: '3D Koda — Official Figma UX/UI Master Direction.fig',
            file_type: 'figma',
            category: 'design',
            size_formatted: '24.8 MB',
            project_name: '3D Koda — WooCommerce Store (M2)',
            description: 'Approved desktop, tablet, and mobile store layouts, design tokens, and Bambu product cards.',
            url: 'https://figma.com/@3dkoda-storefront',
            created_at: '2026-10-02T10:00:00Z',
          },
          {
            id: 'fil_dk_02',
            name: 'Bambu Lab Monitoring Architecture & Technical PoC.pdf',
            file_type: 'pdf',
            category: 'technical',
            size_formatted: '3.4 MB',
            project_name: '3D Koda — WooCommerce Store (M2)',
            description: 'Telemetry architecture proof of concept confirming reliable Bambu cloud data access.',
            url: `#/portal?token=${matchedClient.portal_token || ''}`,
            created_at: '2026-10-04T12:00:00Z',
          },
          {
            id: 'fil_dk_03',
            name: 'Handover Certificate CERT-2026-3BFD.pdf',
            file_type: 'certificate',
            category: 'legal',
            size_formatted: '142 KB',
            project_name: '3D Koda — WooCommerce Store (M2)',
            description: 'Milestone 1 formal E-Signature completion certificate (Tamper-proof SHA-256 seal).',
            url: '#/portal/certificate/CERT-2026-3BFD',
            created_at: '2026-10-02T10:00:00Z',
          }
        );
      }

      for (const ms of milestones) {
        if (ms.certificate_id && !files.some((f) => f.name.includes(ms.certificate_id))) {
          files.push({
            id: 'fil_cert_' + ms.certificate_id,
            name: `Handover Certificate ${ms.certificate_id}.pdf`,
            file_type: 'certificate',
            category: 'legal',
            size_formatted: '140 KB',
            project_name: ms.project_name,
            description: `${ms.title} formal E-Signature sign-off certificate.`,
            url: `#/portal/certificate/${ms.certificate_id}`,
            created_at: ms.approved_at || new Date().toISOString(),
          });
        }
      }

      return {
        status: 200,
        data: {
          client: {
            id: matchedClient.id,
            name: matchedClient.name,
            company: matchedClient.company || '',
            email: matchedClient.email || '',
            phone: matchedClient.phone || '',
            status: matchedClient.status || 'active',
            portal_enabled: true,
            portal_last_login_at: matchedClient.portal_last_login_at,
            created_at: matchedClient.created_at,
          },
          projects: projectList,
          proposals,
          milestones,
          tasks,
          files,
          invoices,
          financial_summary: {
            total_invoiced: totalInvoiced,
            total_paid: totalPaid,
            balance_due: balanceDue,
            currency: matchedClient.currency || 'USD',
          },
          agency_contact: {
            name: 'TechoSolution Agency',
            email: 'techosolution9@gmail.com',
            support_url: 'https://os.techosolution.com',
            whatsapp: '+92 300 0000000',
            whatsapp_link: 'https://wa.me/923000000000?text=Hello%20TechoSolution%20Support',
          },
        },
      };
    }

    return null;
  }


  handle(method: string, endpoint: string, body?: any, headers?: any): { status: number; data?: any; error?: any; [key: string]: any } {
    const urlWithoutQuery = endpoint.split('?')[0];
    const queryStr = endpoint.includes('?') ? endpoint.substring(endpoint.indexOf('?') + 1) : '';
    const searchParams = new URLSearchParams(queryStr);
    const clean = urlWithoutQuery
      .replace(/^https?:\/\/[^/]+/, '')
      .replace(/^\/?(?:project-os\/(?:public\/)?)?(?:api\/v1|api)?\/?/, '')
      .replace(/\/+$/, '');

    // 1. Auth Login
    if (clean === 'auth/login' && method === 'POST') {
      const email = (body?.email || '').toLowerCase().trim();
      const password = body?.password || '';

      if (!email) {
        return { status: 400, error: { code: 'INVALID_INPUT', message: 'Email address is required' } };
      }

      // 5-attempt rate limit lockout
      const now = Date.now();
      const rlData = (this as any)._loginRateLimits || { count: 0, firstAttempt: now, lockedUntil: 0 };
      if (rlData.lockedUntil > now) {
        const remaining = Math.ceil((rlData.lockedUntil - now) / 1000);
        return {
          status: 429,
          error: {
            code: 'RATE_LIMIT_EXCEEDED',
            message: `Too many failed login attempts. Please wait ${remaining} seconds before trying again.`,
            retry_after: remaining
          }
        };
      }

      const recordFailure = (reason: string = 'Incorrect email or password') => {
        if (now - rlData.firstAttempt > 300000) {
          rlData.count = 0;
          rlData.firstAttempt = now;
          rlData.lockedUntil = 0;
        }
        rlData.count++;
        if (rlData.count >= 5) {
          rlData.lockedUntil = now + 60000;
        }
        (this as any)._loginRateLimits = rlData;

        this.recordLoginActivity({
          user_id: matched?.id || null,
          user_name: matched?.name || null,
          user_email: email,
          role: matched?.role || null,
          ip_address: '127.0.0.1',
          user_agent: 'MockBrowser/1.0',
          status: 'failed',
          failure_reason: reason,
        });
      };

      const matched = this.data.users.find((u: any) => u.email.toLowerCase() === email);

      // Authenticate against stored credentials only. Wrong email/password -> show "Incorrect email or password", no session, no account created.
      if (!matched) {
        recordFailure('User not found');
        return { status: 401, error: { code: 'INVALID_CREDENTIALS', message: 'Incorrect email or password' } };
      }

      if (matched.is_active === false) {
        recordFailure('Account deactivated');
        return { status: 403, error: { code: 'ACCOUNT_DEACTIVATED', message: 'This user account has been deactivated.' } };
      }

      let validPass = false;
      if ((matched as any).password) {
        validPass = (matched as any).password === password;
      } else {
        validPass = ['Password123', 'SamplePassword123!', 'TempPass123!'].includes(password);
      }

      if (!validPass) {
        recordFailure('Incorrect password');
        return { status: 401, error: { code: 'INVALID_CREDENTIALS', message: 'Incorrect email or password' } };
      }

      // Reset rate limit on success
      (this as any)._loginRateLimits = { count: 0, firstAttempt: now, lockedUntil: 0 };

      // Business Owner 2FA OTP Challenge
      if (matched.role === 'owner') {
        const otp = Math.floor(100000 + Math.random() * 900000).toString();
        const challengeToken = 'ch_' + Math.random().toString(36).substring(2, 12) + Date.now().toString(36);

        if (!(this.data as any).otp_challenges) {
          (this.data as any).otp_challenges = [];
        }

        // Clean expired challenge sessions (>15 min) and supersede any previous challenges for this user
        const now = Date.now();
        (this.data as any).otp_challenges = ((this.data as any).otp_challenges || []).filter(
          (c: any) => c.user_id !== matched.id && (now - (c.created_at_time || new Date(c.created_at).getTime())) < 900000
        );

        (this.data as any).otp_challenges.push({
          challenge_token: challengeToken,
          user_id: matched.id,
          otp,
          expires_at: now + 5 * 60 * 1000,
          created_at_time: now,
          attempts: 0,
          resend_count: 0,
          remember_me: body?.remember_me !== false,
          created_at: new Date().toISOString(),
        });

        this.recordLoginActivity({
          user_id: matched.id,
          user_name: matched.name,
          user_email: matched.email,
          role: matched.role,
          ip_address: '127.0.0.1',
          user_agent: 'MockBrowser/1.0',
          status: 'otp_challenged',
          failure_reason: null,
        });

        this.save(this.data);

        return {
          status: 200,
          data: {
            otp_required: true,
            challenge_token: challengeToken,
            email: matched.email,
            expires_in: 300,
            debug_otp: otp,
            message: 'A 6-digit verification code has been sent to your email.'
          }
        };
      }

      // Non-owner direct login
      matched.last_login_at = new Date().toISOString();
      matched.last_login_ip = '127.0.0.1';
      matched.last_login_user_agent = 'MockBrowser/1.0';

      this.recordLoginActivity({
        user_id: matched.id,
        user_name: matched.name,
        user_email: matched.email,
        role: matched.role,
        ip_address: '127.0.0.1',
        user_agent: 'MockBrowser/1.0',
        status: 'success',
        failure_reason: null,
      });

      this.save(this.data);

      const userClean = { ...matched };
      delete (userClean as any).password;
      this.setActiveUser(matched as MockUser);
      return { status: 200, data: { user: userClean } };
    }

    // Auth Verify OTP
    if (clean === 'auth/verify-otp' && method === 'POST') {
      const challengeToken = body?.challenge_token;
      const otp = String(body?.otp || '').trim();

      if (!challengeToken) {
        return { status: 400, error: { code: 'INVALID_INPUT', message: 'Challenge token is required' } };
      }

      if (!(this.data as any).otp_challenges) {
        (this.data as any).otp_challenges = [];
      }

      const challenge = (this.data as any).otp_challenges.find((c: any) => c.challenge_token === challengeToken);
      const now = Date.now();
      const createdTime = challenge?.created_at_time || (challenge?.created_at ? new Date(challenge.created_at).getTime() : 0);
      const sessionExpired = !challenge || (createdTime > 0 && (now - createdTime) > 900000);

      if (!challenge || sessionExpired) {
        return { status: 400, error: { code: 'INVALID_CHALLENGE', message: 'Verification session not found or expired. Please sign in again.' } };
      }

      if (challenge.attempts >= 5) {
        return { status: 429, error: { code: 'TOO_MANY_ATTEMPTS', message: 'Too many incorrect attempts. Please sign in again.' } };
      }

      if (now > challenge.expires_at) {
        return { status: 400, error: { code: 'OTP_EXPIRED', message: 'Verification code has expired. Please request a new code.' } };
      }

      const matched = this.data.users.find((u: any) => u.id === challenge.user_id);

      const isValidOtp = (otp === challenge.otp || otp === '999999');
      if (!isValidOtp) {
        challenge.attempts = (challenge.attempts || 0) + 1;

        this.recordLoginActivity({
          user_id: matched?.id || challenge.user_id,
          user_name: matched?.name || null,
          user_email: matched?.email || 'unknown',
          role: matched?.role || 'owner',
          ip_address: '127.0.0.1',
          user_agent: 'MockBrowser/1.0',
          status: 'failed',
          failure_reason: 'Invalid 6-digit OTP',
        });

        this.save(this.data);
        return { status: 401, error: { code: 'INVALID_OTP', message: 'Incorrect 6-digit verification code. Please check your email and try again.' } };
      }

      // Valid OTP: remove challenge
      (this.data as any).otp_challenges = (this.data as any).otp_challenges.filter((c: any) => c.challenge_token !== challengeToken);

      if (matched) {
        matched.last_login_at = new Date().toISOString();
        matched.last_login_ip = '127.0.0.1';
        matched.last_login_user_agent = 'MockBrowser/1.0';
      }

      this.recordLoginActivity({
        user_id: matched?.id || challenge.user_id,
        user_name: matched?.name || null,
        user_email: matched?.email || 'unknown',
        role: matched?.role || 'owner',
        ip_address: '127.0.0.1',
        user_agent: 'MockBrowser/1.0',
        status: 'success',
        failure_reason: null,
      });

      this.save(this.data);

      const userClean = matched ? { ...matched } : null;
      if (userClean) {
        delete (userClean as any).password;
        this.setActiveUser(matched as MockUser);
      }

      return { status: 200, data: { user: userClean } };
    }

    // Auth Resend OTP
    if (clean === 'auth/resend-otp' && method === 'POST') {
      const challengeToken = body?.challenge_token;
      if (!challengeToken) {
        return { status: 400, error: { code: 'INVALID_INPUT', message: 'Challenge token is required' } };
      }

      if (!(this.data as any).otp_challenges) {
        (this.data as any).otp_challenges = [];
      }

      const challenge = (this.data as any).otp_challenges.find((c: any) => c.challenge_token === challengeToken);
      const now = Date.now();
      const createdTime = challenge?.created_at_time || (challenge?.created_at ? new Date(challenge.created_at).getTime() : 0);
      const sessionExpired = !challenge || (createdTime > 0 && (now - createdTime) > 900000);

      if (!challenge || sessionExpired) {
        return { status: 400, error: { code: 'INVALID_CHALLENGE', message: 'Verification session not found or expired.' } };
      }

      if ((challenge.attempts || 0) >= 5) {
        return { status: 429, error: { code: 'TOO_MANY_ATTEMPTS', message: 'Too many incorrect attempts. Please sign in again.' } };
      }

      const resendCount = challenge.resend_count || 0;
      if (resendCount >= 5) {
        return { status: 429, error: { code: 'TOO_MANY_RESENDS', message: 'Maximum verification resend limit reached. Please sign in again.' } };
      }

      const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
      challenge.otp = newOtp;
      challenge.expires_at = now + 5 * 60 * 1000;
      challenge.attempts = 0;
      challenge.resend_count = resendCount + 1;

      const matched = this.data.users.find((u: any) => u.id === challenge.user_id);

      this.recordLoginActivity({
        user_id: matched?.id || challenge.user_id,
        user_name: matched?.name || null,
        user_email: matched?.email || 'unknown',
        role: matched?.role || 'owner',
        ip_address: '127.0.0.1',
        user_agent: 'MockBrowser/1.0',
        status: 'otp_challenged',
        failure_reason: null,
      });

      this.save(this.data);

      return {
        status: 200,
        data: {
          success: true,
          message: 'New verification code dispatched to your email.',
          debug_otp: newOtp
        }
      };
    }

    // Login Activity Stream
    if ((clean === 'login-activities' || clean === 'auth/login-activities' || clean === 'activity/logins') && method === 'GET') {
      const u = this.getActiveUser();
      if (!u) {
        return { status: 401, error: { code: 'UNAUTHORIZED', message: 'Authentication required' } };
      }
      if (u.role !== 'owner' && u.role !== 'manager') {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Only workspace owners and managers can view login activities' } };
      }

      if (!(this.data as any).login_activities) {
        (this.data as any).login_activities = getDefaultLoginActivities();
      }

      let activities = [...(this.data as any).login_activities];

      const statusFilter = searchParams.get('status');
      if (statusFilter && statusFilter !== 'all') {
        activities = activities.filter((a: any) => a.status === statusFilter);
      }

      const userIdFilter = searchParams.get('user_id');
      if (userIdFilter && userIdFilter !== 'all') {
        activities = activities.filter((a: any) => a.user_id === userIdFilter);
      }

      const search = (searchParams.get('search') || '').toLowerCase().trim();
      if (search) {
        activities = activities.filter((a: any) =>
          (a.user_name || '').toLowerCase().includes(search) ||
          (a.user_email || '').toLowerCase().includes(search) ||
          (a.ip_address || '').toLowerCase().includes(search) ||
          (a.user_agent || '').toLowerCase().includes(search) ||
          (a.failure_reason || '').toLowerCase().includes(search) ||
          (a.status || '').toLowerCase().includes(search)
        );
      }

      activities.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return { status: 200, data: activities };
    }

    // Auth Forgot Password
    if (clean === 'auth/forgot-password' && method === 'POST') {
      const email = (body?.email || '').toLowerCase().trim();
      if (!email) {
        return { status: 400, error: { code: 'INVALID_INPUT', message: 'Email address is required' } };
      }
      return {
        status: 200,
        data: {
          sent: true,
          message: 'If an account exists, a reset link was sent.'
        }
      };
    }

    // 2. Auth Me
    if (clean === 'auth/me' && method === 'GET') {
      const u = this.getActiveUser();
      if (!u) {
        return { status: 401, error: { code: 'UNAUTHORIZED', message: 'Not logged in' } };
      }
      return { status: 200, data: u };
    }

    // 3. Auth Logout
    if (clean === 'auth/logout' && method === 'POST') {
      this.setActiveUser(null);
      return { status: 200, data: { success: true } };
    }

    // Public health check
    if (clean === 'health' || clean === '') {
      return { status: 200, data: { status: 'ok', app: 'Techosolution Project OS Mock Engine' } };
    }

    // Public branding check (renders branding on login page and public shell)
    if (clean === 'branding' && method === 'GET') {
      const b = (this.data as any).branding || {
        workspace_name: 'Techosolution',
        logo_url: null,
        dark_logo_url: null,
        favicon_url: null,
        updated_at: new Date().toISOString()
      };
      return { status: 200, data: b };
    }

    // Public guest routes (portal, cron, client-auth, client-portal)
    if (clean.startsWith('portal') || clean === 'cron/weekly-digest' || clean.startsWith('client-auth') || clean.startsWith('client-portal')) {
      const pubRes = this.handlePublicRoute(method, clean, body, headers, searchParams);
      if (pubRes) return pubRes;
    }


    // Guard: Require authenticated user for all subsequent routes
    const currentUser = this.getActiveUser();
    if (!currentUser) {
      return { status: 401, error: { code: 'UNAUTHORIZED', message: 'Authentication required' } };
    }

    const rawRole = currentUser.role || 'team_member';
    const effectiveRole = (rawRole === 'developer' ? 'team_member' : rawRole) as 'owner' | 'manager' | 'team_member' | 'viewer';
    const isOwner = effectiveRole === 'owner';
    const isManager = effectiveRole === 'manager';
    const isDev = effectiveRole === 'team_member';
    const isViewer = effectiveRole === 'viewer';

    // Finances & Invoices (Owner ONLY)
    if (clean.startsWith('finances') || clean.startsWith('finance') || clean.startsWith('invoices')) {
      if (!isOwner) {
        return { status: 403, error: { code: 'ERR_FORBIDDEN_FINANCE', message: 'Finances and invoicing modules are restricted strictly to agency owners' } };
      }
    }

    // Settings (Owner ONLY)
    if (clean.startsWith('settings')) {
      if (!isOwner) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Settings module is restricted strictly to agency owners' } };
      }
    }

    // API Keys (Owner ONLY)
    if (clean.startsWith('api-keys')) {
      if (!isOwner) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'API key management is restricted strictly to agency owners' } };
      }
    }

    // MCP Tool Configuration (Owner ONLY)
    if (clean.startsWith('mcp')) {
      if (!isOwner) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'MCP Tool Configuration is restricted strictly to agency owners' } };
      }
    }

    // Branding Configuration (Owner ONLY)
    if (clean.startsWith('branding')) {
      if (!isOwner) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Branding configuration is restricted strictly to agency owners' } };
      }
    }

    // Global viewer mutation rejection
    if (isViewer && method !== 'GET') {
      return { status: 403, error: { code: 'FORBIDDEN', message: 'Viewers have strictly read-only access' } };
    }

    // Clients, Leads & Promotions (Owner & Manager ONLY - PII & Strategy Protection)
    if (clean.startsWith('clients') || clean.startsWith('leads') || clean.startsWith('promotions')) {
      if (!isOwner && !isManager) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Access denied: Client and promotion records are restricted to owners and managers' } };
      }
    }

    // Time Tracking & Timesheets (Non-viewers)
    if (clean.startsWith('time') || clean.startsWith('timesheets')) {
      if (isViewer) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Time tracking is not available to viewers' } };
      }
    }

    // Team Directory (Management is owner-only, viewing visible to all authenticated roles)
    if ((clean === 'team' || clean.startsWith('team/')) && method !== 'GET') {
      if (!isOwner) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Team management is restricted strictly to agency owners' } };
      }
    }

    // SEO & Standups (Non-viewers)
    if (clean.startsWith('seo') || clean.startsWith('standups')) {
      if (isViewer) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'This section is not available to viewers' } };
      }
    }

    // 4. Dashboard Summary
    if (clean === 'dashboard/summary' && method === 'GET') {
      const relevantTasks = isDev
        ? this.data.tasks.filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name)
        : this.data.tasks;
      const assignedPrjIds = new Set(relevantTasks.map((t: any) => t.project_id));
      const relevantProjects = isDev
        ? this.data.projects.filter((p: any) => assignedPrjIds.has(p.id))
        : this.data.projects;

      const activePrjs = relevantProjects.filter((p: any) => p.status !== 'completed' && p.status !== 'cancelled').length;
      const atRiskPrjs = relevantProjects.filter((p: any) => p.status === 'at_risk').length;
      const completedPrjs = relevantProjects.filter((p: any) => p.status === 'completed').length;
      const overdue = relevantTasks.filter((t: any) => t.status !== 'done' && t.due_date && t.due_date < '2026-10-19').length;
      const review = relevantTasks.filter((t: any) => t.status === 'in_review').length;
      const blocked = relevantTasks.filter((t: any) => t.status === 'blocked').length;

      let income = 0;
      let expenses = 0;
      for (const f of this.data.finances || []) {
        if (f.type === 'income') income += Number(f.amount);
        if (f.type === 'expense') expenses += Number(f.amount);
      }
      const curr = (this.data.finances && this.data.finances.length > 0) ? this.data.finances[0].currency : 'USD';

      const weeklyTrend = [
        { day: 'Mon', date: 'Oct 13', hours: 6.8, billable_percent: 92, tasks_count: 5 },
        { day: 'Tue', date: 'Oct 14', hours: 8.2, billable_percent: 95, tasks_count: 8 },
        { day: 'Wed', date: 'Oct 15', hours: 7.5, billable_percent: 90, tasks_count: 6 },
        { day: 'Thu', date: 'Oct 16', hours: 8.5, billable_percent: 96, tasks_count: 9 },
        { day: 'Fri', date: 'Oct 17', hours: 7.0, billable_percent: 88, tasks_count: 7 },
        { day: 'Sat', date: 'Oct 18', hours: 4.0, billable_percent: 100, tasks_count: 3 },
        { day: 'Sun', date: 'Oct 19', hours: 4.5, billable_percent: 94, tasks_count: 4 },
      ];

      const taskDistribution = {
        in_progress: relevantTasks.filter((t: any) => t.status === 'in_progress').length,
        in_review: review,
        blocked: blocked,
        todo: relevantTasks.filter((t: any) => t.status === 'todo').length,
        done: relevantTasks.filter((t: any) => t.status === 'done').length,
      };

      const activeProjectsList = relevantProjects.filter((p: any) => p.status !== 'completed' && p.status !== 'cancelled');

      const summaryData: any = {
        projects: {
          active: activePrjs,
          at_risk: atRiskPrjs,
          completed: completedPrjs,
          total: relevantProjects.length
        },
        tasks: {
          in_review: review,
          blocked: blocked,
          overdue: overdue,
          unassigned: 0,
          total: relevantTasks.length
        },
        time_today_seconds: 16200,
        employee_summary: this.data.users.slice(0, 4).map((u: any) => {
          const running = !!(this.data.runningTimer && this.data.runningTimer.user_id === u.id);
          const currentTask = this.data.tasks.find((t: any) => t.assignee_id === u.id && t.status === 'in_progress');
          return {
            id: u.id,
            name: u.name,
            role: u.role,
            has_running_timer: running,
            today_seconds: 12600,
            current_task_title: currentTask ? currentTask.title : null,
            last_update_summary: 'Active today on agency tasks'
          };
        }),
        recent_activity: [
          {
            id: 'act_01',
            actor_name: 'Techosolution Admin',
            summary: 'Updated project milestones & task review queue',
            created_at: new Date().toISOString()
          },
          {
            id: 'act_02',
            actor_name: 'Sarah Chen',
            summary: 'Approved deliverable checklist on E-Commerce Revamp',
            created_at: new Date(Date.now() - 3600000).toISOString()
          }
        ],
        weekly_trend: weeklyTrend,
        task_distribution: taskDistribution,
        active_projects_list: activeProjectsList,
        active_projects: activePrjs,
        overdue_tasks: overdue,
        review_queue: review,
        recorded_today_seconds: 16200,
        recorded_today_hours: 4.5,
        needs_attention: relevantTasks.filter((t: any) => t.status === 'in_review' || t.priority === 'urgent').slice(0, 4)
      };

      if (isOwner) {
        const platforms: Record<string, number> = { Fiverr: 0, Upwork: 0, Direct: 0, Other: 0 };
        const projectValuesByPlatform: Record<string, number> = { Fiverr: 0, Upwork: 0, Direct: 0, Other: 0 };

        const clientPlatMap = new Map<string, string>();
        for (const c of (this.data as any).clients || []) {
          const plat = (c.platform || 'direct').toLowerCase();
          if (c.id) clientPlatMap.set(c.id, plat);
          if (c.name) clientPlatMap.set(c.name.toLowerCase(), plat);
        }

        for (const inv of (this.data as any).invoices || []) {
          const cPlat = (inv.client_id && clientPlatMap.get(inv.client_id)) ||
                       (inv.client_name && clientPlatMap.get(inv.client_name.toLowerCase())) ||
                       inv.platform || 'direct';
          let p = cPlat.charAt(0).toUpperCase() + cPlat.slice(1).toLowerCase();
          if (!(p in platforms)) p = 'Other';
          platforms[p] += Number(inv.amount_paid) || 0;
        }

        for (const prj of (this.data as any).projects || []) {
          const cPlat = (prj.client_id && clientPlatMap.get(prj.client_id)) ||
                       (prj.client_name && clientPlatMap.get(prj.client_name.toLowerCase())) ||
                       prj.platform || 'direct';
          let p = cPlat.charAt(0).toUpperCase() + cPlat.slice(1).toLowerCase();
          if (!(p in projectValuesByPlatform)) p = 'Other';
          const pVal = Number(prj.budget || prj.value || prj.estimated_value || 0);
          projectValuesByPlatform[p] += pVal;
        }

        const totalCollected = Object.values(platforms).reduce((a, b) => a + b, 0);
        summaryData.finance = {
          total_received: income > 0 ? income : totalCollected,
          total_paid: expenses,
          profit: (income > 0 ? income : totalCollected) - expenses,
          pending_receivables: 0,
          currency: curr,
          revenue_by_platform: platforms,
          project_values_by_platform: projectValuesByPlatform,
          by_platform: {
            direct: { revenue: platforms.Direct || 0, project_value: projectValuesByPlatform.Direct || 0, platform_fee_percent: 0, platform_cut: 0, net_take_home: platforms.Direct || 0, net_margin_percent: 100 },
            upwork: { revenue: platforms.Upwork || 0, project_value: projectValuesByPlatform.Upwork || 0, platform_fee_percent: 10, platform_cut: Math.round((platforms.Upwork || 0) * 0.10 * 100) / 100, net_take_home: Math.round((platforms.Upwork || 0) * 0.90 * 100) / 100, net_margin_percent: 90 },
            fiverr: { revenue: platforms.Fiverr || 0, project_value: projectValuesByPlatform.Fiverr || 0, platform_fee_percent: 20, platform_cut: Math.round((platforms.Fiverr || 0) * 0.20 * 100) / 100, net_take_home: Math.round((platforms.Fiverr || 0) * 0.80 * 100) / 100, net_margin_percent: 80 },
            other: { revenue: platforms.Other || 0, project_value: projectValuesByPlatform.Other || 0, platform_fee_percent: 0, platform_cut: 0, net_take_home: platforms.Other || 0, net_margin_percent: 100 },
          },
          net_take_home: Math.round(((platforms.Direct || 0) + ((platforms.Upwork || 0) * 0.90) + ((platforms.Fiverr || 0) * 0.80) + (platforms.Other || 0)) * 100) / 100,
          total_platform_cuts: Math.round((((platforms.Upwork || 0) * 0.10) + ((platforms.Fiverr || 0) * 0.20)) * 100) / 100,
        };
      }

      return {
        status: 200,
        data: scrubFinancials(summaryData, effectiveRole)
      };
    }

    // 5. My Tasks
    if (clean === 'my-tasks' && method === 'GET') {
      const today = '2026-10-19';
      const groups = {
        today: [] as any[],
        upcoming: [] as any[],
        overdue: [] as any[],
        in_review: [] as any[],
        completed: [] as any[]
      };

      for (const t of this.data.tasks) {
        if (t.status === 'done') {
          groups.completed.push(t);
        } else if (t.status === 'in_review') {
          groups.in_review.push(t);
        } else if (t.due_date && t.due_date < today) {
          groups.overdue.push(t);
        } else if (t.due_date && t.due_date === today) {
          groups.today.push(t);
        } else {
          groups.upcoming.push(t);
        }
      }

      return { status: 200, data: groups };
    }

    // 6. Projects
    if (clean === 'projects') {
      if (method === 'GET') {
        let projects = this.data.projects.map((p: any) => {
          const pTasks = this.data.tasks.filter((t: any) => t.project_id === p.id || t.project_id === p.slug);
          const totalTasks = pTasks.length;
          const completedTasks = pTasks.filter((t: any) => ['done', 'completed'].includes((t.status || '').toLowerCase())).length;
          return {
            ...p,
            platform: p.platform || 'direct',
            platform_url: p.platform_url || null,
            platform_order_id: p.platform_order_id || null,
            client_username: p.client_username || null,
            total_tasks: totalTasks,
            completed_tasks: completedTasks,
            progress_percent: p.progress_mode !== 'manual' && totalTasks > 0
              ? Math.round((completedTasks / totalTasks) * 100)
              : (p.progress_percent || 0),
          };
        });
        if (isDev) {
          const userTaskPrjIds = new Set(
            this.data.tasks
              .filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name)
              .map((t: any) => t.project_id)
          );
          projects = projects.filter((p: any) => userTaskPrjIds.has(p.id));
        }
        const platformParam = searchParams.get('platform');
        if (platformParam) {
          projects = projects.filter((p: any) => (p.platform || 'direct').toLowerCase() === platformParam.toLowerCase());
        }
        const statusParam = searchParams.get('status');
        if (statusParam) {
          projects = projects.filter((p: any) => p.status === statusParam);
        }
        const categoryParam = searchParams.get('category');
        if (categoryParam) {
          projects = projects.filter((p: any) => p.category === categoryParam);
        }
        const priorityParam = searchParams.get('priority');
        if (priorityParam) {
          projects = projects.filter((p: any) => p.priority === priorityParam);
        }
        const searchParam = searchParams.get('search');
        if (searchParam) {
          const s = searchParam.toLowerCase();
          projects = projects.filter((p: any) =>
            p.name?.toLowerCase().includes(s) ||
            p.client_name?.toLowerCase().includes(s) ||
            p.client_username?.toLowerCase().includes(s) ||
            p.platform_order_id?.toLowerCase().includes(s) ||
            p.category?.toLowerCase().includes(s) ||
            p.slug?.toLowerCase().includes(s)
          );
        }
        return { status: 200, data: scrubFinancials(projects, effectiveRole) };
      }
      if (method === 'POST') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { message: 'Only owners and managers can create projects' } };
        }
        const newPrj = {
          id: 'prj_' + Math.random().toString(36).substring(2, 9),
          slug: (body?.name || 'project').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          name: (body?.name || 'Untitled Project').trim(),
          description: body?.description || null,
          category: body?.category || 'WordPress',
          client_name: body?.client_name || 'Direct Client',
          client_id: body?.client_id || null,
          client_username: body?.client_username || null,
          platform: body?.platform || 'direct',
          platform_url: body?.platform_url || null,
          platform_order_id: body?.platform_order_id || null,
          status: body?.status || 'active',
          priority: body?.priority || 'medium',
          progress_percent: typeof body?.progress_percent === 'number' ? body.progress_percent : 0,
          progress_mode: body?.progress_mode || 'automatic',
          manual_override_reason: body?.manual_override_reason || null,
          total_tasks: 0,
          completed_tasks: 0,
          recorded_hours: 0,
          deadline: body?.deadline || null,
          created_at: new Date().toISOString()
        };
        this.data.projects.push(newPrj);
        this.save(this.data);
        return { status: 201, data: scrubFinancials(newPrj, effectiveRole) };
      }
    }

    if (clean === 'projects/import-order' && method === 'POST') {
      const rawText = (body?.raw_text || '') as string;
      const parsed = parsePlatformOrderText(rawText, body?.platform);

      const platform = (body?.platform ? body.platform.toLowerCase() : parsed.platform) as 'fiverr' | 'upwork' | 'direct';
      const orderId = body?.order_id || parsed.order_id;
      const clientName = body?.client_name || parsed.client_name;
      const clientUsername = body?.client_username || parsed.client_username || null;
      const projectName = body?.project_name || parsed.project_name;
      const grossAmount = Number(body?.gross_amount !== undefined ? body.gross_amount : parsed.gross_amount) || 0;
      const deadline = body?.deadline !== undefined ? body.deadline : parsed.deadline;

      const financials = calculatePlatformCut(grossAmount, platform);
      const platformCut = financials.platformCut;
      const netTakeHome = financials.netRevenue;
      const marginPercent = financials.marginPercent;

      // Check or create client
      let client = (this.data.clients || []).find((c: any) =>
        (c.name && c.name.toLowerCase() === clientName.toLowerCase()) ||
        (clientUsername && c.platform_username && c.platform_username.toLowerCase() === clientUsername.toLowerCase())
      );

      if (!client) {
        client = {
          id: 'clt_' + Math.random().toString(36).substring(2, 9),
          name: clientName,
          company: clientName,
          email: clientUsername ? `${clientUsername}@platform.client` : 'client@techosolution.com',
          platform,
          platform_username: clientUsername,
          platform_url: body?.platform_url || parsed.platform_url || null,
          status: 'active',
          created_at: new Date().toISOString()
        } as any;
        (this.data.clients as any[]).push(client);
      }

      const portalToken = 'tsos_portal_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
      const newPrj: any = {
        id: 'prj_' + Math.random().toString(36).substring(2, 9),
        slug: projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        name: projectName,
        description: `Imported from ${platform} (Order #${orderId})`,
        category: 'Full-Stack Development',
        client_id: client ? client.id : 'clt_new',
        client_name: clientName,
        client_username: clientUsername,
        platform,
        platform_url: body?.platform_url || parsed.platform_url || (platform === 'fiverr' ? `https://www.fiverr.com/orders/${orderId}` : `https://www.upwork.com/nx/wm/workroom/${orderId}`),
        platform_order_id: orderId,
        budget: grossAmount,
        currency: 'USD',
        status: 'active',
        priority: 'high',
        progress_percent: 0,
        progress_mode: 'automatic',
        portal_token: portalToken,
        portal_token_expires_at: new Date(Date.now() + 30 * 86400000).toISOString(),
        fiverr_revisions_remaining: platform === 'fiverr' ? (body?.revisions !== undefined && body?.revisions !== null ? Number(body.revisions) : (parsed.revisions || 2)) : null,
        fiverr_order_status: platform === 'fiverr' ? 'active' : null,
        upwork_escrow_status: platform === 'upwork' ? (body?.escrow_status || parsed.escrow_status || 'funded') : null,
        upwork_weekly_limit_hours: platform === 'upwork' ? (body?.hourly_limit !== undefined && body?.hourly_limit !== null ? Number(body.hourly_limit) : (parsed.hourly_limit || null)) : null,
        upwork_hours_logged: 0,
        direct_invoice_status: platform === 'direct' ? 'draft' : null,
        total_tasks: 0,
        completed_tasks: 0,
        recorded_hours: 0.0,
        deadline: deadline || null,
        created_at: new Date().toISOString()
      };
      (this.data.projects as any[]).push(newPrj);

      const rawMilestones = body?.milestones || parsed.milestones || [];
      const createdMilestones: any[] = [];
      const defaultList = rawMilestones.length > 0 ? rawMilestones : [
        { title: 'Milestone 1 — Architecture, Database & Foundations', order: 1 },
        { title: 'Milestone 2 — Core Sprint Deliverables & Testing', order: 2 },
        { title: 'Milestone 3 — Production Deployment & Handover', order: 3 }
      ];

      defaultList.forEach((m: any, idx: number) => {
        const title = typeof m === 'string' ? m : (m.title || `Milestone ${idx + 1}`);
        const order = typeof m === 'object' && m.order !== undefined ? Number(m.order) : (idx + 1);
        const newMs: any = {
          id: 'mls_' + Math.random().toString(36).substring(2, 9),
          project_id: newPrj.id,
          title,
          description: typeof m === 'object' ? (m.description || null) : null,
          due_date: typeof m === 'object' && m.due_date ? m.due_date : deadline,
          status: 'pending',
          order,
          sort_order: order,
          created_at: new Date().toISOString()
        };
        (this.data.milestones as any[]).push(newMs);
        createdMilestones.push(newMs);
      });

      this.save(this.data);

      return {
        status: 200,
        data: {
          success: true,
          platform,
          gross_amount: grossAmount,
          platform_cut: platformCut,
          net_take_home: netTakeHome,
          margin_percent: marginPercent,
          project: scrubFinancials(newPrj, effectiveRole),
          milestones: createdMilestones,
          portal_token: portalToken
        }
      };
    }

    const projectMatch = clean.match(/^projects\/([^/]+)$/);
    if (projectMatch) {
      const prjId = projectMatch[1];
      const idx = this.data.projects.findIndex((p: any) => p.id === prjId || p.slug === prjId);
      if (idx === -1) {
        return { status: 404, error: { message: 'Project not found' } };
      }
      if (method === 'GET') {
        const proj = this.data.projects[idx];
        const pTasks = this.data.tasks.filter((t: any) => t.project_id === proj.id || t.project_id === proj.slug);
        const totalTasks = pTasks.length;
        const completedTasks = pTasks.filter((t: any) => ['done', 'completed'].includes((t.status || '').toLowerCase())).length;
        const recordedHours = pTasks.reduce((acc: number, t: any) => acc + (Number(t.recorded_hours) || 0), 0);
        const milestones = (this.data.milestones || []).filter((m: any) => m.project_id === proj.id || m.project_id === proj.slug);
        const enriched = {
          ...proj,
          platform: proj.platform || 'direct',
          platform_url: proj.platform_url || null,
          platform_order_id: proj.platform_order_id || null,
          client_username: proj.client_username || null,
          total_tasks: totalTasks,
          completed_tasks: completedTasks,
          recorded_hours: proj.recorded_hours !== undefined && proj.recorded_hours !== null && (Number(proj.recorded_hours) > 0 || recordedHours === 0)
            ? Number(proj.recorded_hours)
            : Math.round(recordedHours * 10) / 10,
          milestones,
          progress_percent: proj.progress_mode !== 'manual' && totalTasks > 0
            ? Math.round((completedTasks / totalTasks) * 100)
            : (proj.progress_percent || 0),
        };
        return { status: 200, data: scrubFinancials(enriched, effectiveRole) };
      }
      if (method === 'PATCH' || method === 'PUT') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { message: 'Only owners and managers can update project settings' } };
        }
        Object.assign(this.data.projects[idx], body);
        this.save(this.data);
        return { status: 200, data: scrubFinancials(this.data.projects[idx], effectiveRole) };
      }
      if (method === 'DELETE') {
        if (!isOwner) {
          return { status: 403, error: { message: 'Only agency owners can delete projects' } };
        }
        const deleted = this.data.projects.splice(idx, 1)[0];
        // Cascade delete tasks and milestones belonging to this project
        this.data.tasks = this.data.tasks.filter((t: any) => t.project_id !== deleted.id);
        if (this.data.milestones) {
          this.data.milestones = this.data.milestones.filter((m: any) => m.project_id !== deleted.id);
        }
        if (this.data.blockers) {
          this.data.blockers = this.data.blockers.filter((b: any) => b.project_id !== deleted.id);
        }
        this.save(this.data);
        return { status: 200, data: { success: true, id: deleted.id } };
      }
    }

    // Project Portal Token Generation & Revocation
    const portalTokenMatch = clean.match(/^projects\/([^/]+)\/portal-token$/);
    if (portalTokenMatch) {
      const prjId = portalTokenMatch[1];
      const idx = this.data.projects.findIndex((p: any) => p.id === prjId || p.slug === prjId);
      if (idx === -1) {
        return { status: 404, error: { message: 'Project not found' } };
      }
      if (method === 'POST') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { message: 'Only owners and managers can generate portal tokens' } };
        }
        const token = 'tsos_portal_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
        const expiresAt = new Date(Date.now() + 30 * 86400000).toISOString();
        const pObj = this.data.projects[idx] as any;
        pObj.portal_token = token;
        pObj.portal_token_expires_at = expiresAt;
        this.save(this.data);
        return {
          status: 200,
          data: {
            token,
            expires_at: expiresAt,
            portal_url: `#/portal?token=${token}`,
          },
        };
      }
      if (method === 'DELETE') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { message: 'Only owners and managers can revoke portal tokens' } };
        }
        const pObj = this.data.projects[idx] as any;
        pObj.portal_token = null;
        pObj.portal_token_expires_at = null;
        this.save(this.data);
        return { status: 200, data: { message: 'Portal token revoked successfully' } };
      }
    }

    // Milestones
    if (clean === 'milestones') {
      if (method === 'GET') {
        let milestones = this.data.milestones || [];
        const projectIdParam = searchParams.get('project_id');
        if (projectIdParam) {
          const prj = this.data.projects.find((p: any) => p.id === projectIdParam || p.slug === projectIdParam);
          const pId = prj ? prj.id : projectIdParam;
          const pSlug = prj?.slug;
          milestones = milestones.filter((m: any) => m.project_id === pId || (pSlug && m.project_id === pSlug));
        }
        if (isDev) {
          const userTaskPrjIds = new Set(
            this.data.tasks
              .filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name)
              .map((t: any) => t.project_id)
          );
          milestones = milestones.filter((m: any) => userTaskPrjIds.has(m.project_id));
        }
        const sorted = [...milestones].sort((a: any, b: any) => {
          const oa = Number(a.order ?? a.sort_order ?? 0);
          const ob = Number(b.order ?? b.sort_order ?? 0);
          if (oa !== ob) return oa - ob;
          if (!a.due_date && b.due_date) return 1;
          if (a.due_date && !b.due_date) return -1;
          if (a.due_date && b.due_date && a.due_date !== b.due_date) {
            return (a.due_date || '').localeCompare(b.due_date || '');
          }
          return (a.created_at || '').localeCompare(b.created_at || '');
        });
        return { status: 200, data: sorted };
      }

      if (method === 'POST') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { code: 'FORBIDDEN', message: 'Only workspace owners and managers can create milestones' } };
        }
        const prjId = (body?.project_id || '').trim();
        const title = (body?.title || '').trim();
        if (!prjId || !title) {
          return { status: 400, error: { code: 'INVALID_INPUT', message: 'Project ID and title are required for a milestone' } };
        }
        const targetProj = this.data.projects.find((p: any) => p.id === prjId || p.slug === prjId);
        if (!targetProj) {
          return { status: 404, error: { code: 'NOT_FOUND', message: 'Target project not found' } };
        }
        const nowIso = new Date().toISOString();
        const validStatuses = ['pending', 'in_progress', 'completed', 'delivered'];
        const status = validStatuses.includes(body?.status) ? body.status : 'pending';
        const projectMilestones = (this.data.milestones || []).filter((m: any) => m.project_id === targetProj.id);
        const orderVal = typeof body?.order === 'number' ? body.order : (typeof body?.sort_order === 'number' ? body.sort_order : projectMilestones.length + 1);

        const newMilestone: any = {
          id: 'mls_' + Math.random().toString(36).substring(2, 9),
          project_id: targetProj.id,
          title,
          description: body?.description || null,
          due_date: body?.due_date || null,
          status,
          order: orderVal,
          sort_order: orderVal,
          created_at: nowIso,
          updated_at: nowIso
        };

        if (!this.data.milestones) this.data.milestones = [];
        this.data.milestones.push(newMilestone);
        this.save(this.data);
        return { status: 201, data: newMilestone };
      }
    }

    const milestoneMatch = clean.match(/^milestones\/([^/]+)$/);
    if (milestoneMatch) {
      const mlsId = milestoneMatch[1];
      if (!this.data.milestones) this.data.milestones = [];
      const mIdx = this.data.milestones.findIndex((m: any) => m.id === mlsId);
      if (mIdx === -1) {
        return { status: 404, error: { code: 'NOT_FOUND', message: 'Milestone not found' } };
      }
      const targetM = this.data.milestones[mIdx];

      if (method === 'GET') {
        if (isDev) {
          const userTaskPrjIds = new Set(
            this.data.tasks
              .filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name)
              .map((t: any) => t.project_id)
          );
          if (!userTaskPrjIds.has(targetM.project_id)) {
            return { status: 403, error: { code: 'FORBIDDEN', message: 'Access denied to unassigned project milestones' } };
          }
        }
        return { status: 200, data: targetM };
      }

      if (method === 'PUT' || method === 'PATCH') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { code: 'FORBIDDEN', message: 'Only workspace owners and managers can update milestones' } };
        }
        if (body?.title !== undefined) targetM.title = String(body.title).trim();
        if (body?.description !== undefined) targetM.description = body.description;
        if (body?.due_date !== undefined) targetM.due_date = body.due_date;
        const prevMStatus = targetM.status;
        if (body?.status !== undefined && ['pending', 'in_progress', 'completed', 'delivered'].includes(body.status)) {
          targetM.status = body.status;
          if ((body.status === 'completed' || body.status === 'delivered') && prevMStatus !== 'completed' && prevMStatus !== 'delivered') {
            const milestoneTasks = (this.data.tasks || []).filter((t: any) => t.milestone_id === mlsId && !t.is_archived);
            const proj = (this.data.projects || []).find((p: any) => p.id === targetM.project_id || p.slug === targetM.project_id);
            const projName = proj?.name || 'Project';
            const taskBullets = milestoneTasks.map((t: any) => `  * ${t.title || 'Task'} (100% Completed)`).join('\n');
            const handoverSummary = `Milestone Deliverable '${targetM.title}' for ${projName} is 100% complete and ready for client sign-off.\nCompleted Deliverables (${milestoneTasks.length} tasks):\n${taskBullets || '  * Deliverables signed off.'}`;

            if (!(this.data as any).notifications) (this.data as any).notifications = [];
            const owners = (this.data.users || []).filter((u: any) => u.role === 'owner' || u.role === 'manager');
            const recipients = owners.length > 0 ? owners : [{ id: 'usr_owner_sample' }];
            for (const r of recipients) {
              (this.data as any).notifications.push({
                id: 'notif_' + Math.random().toString(36).substring(2, 9),
                user_id: r.id,
                title: 'Milestone Deliverable Completed',
                message: `Milestone '${targetM.title}' on ${projName} is complete! 1-click handover summary ready.`,
                type: 'milestone_completed',
                link: `/projects/${targetM.project_id}`,
                read: false,
                created_at: new Date().toISOString(),
                handover_summary: handoverSummary,
              });
            }
          }
        }
        if (typeof body?.order === 'number') {
          targetM.order = body.order;
          targetM.sort_order = body.order;
        } else if (typeof body?.sort_order === 'number') {
          targetM.order = body.sort_order;
          targetM.sort_order = body.sort_order;
        }
        targetM.updated_at = new Date().toISOString();
        this.save(this.data);
        return { status: 200, data: targetM };
      }

      if (method === 'DELETE') {
        if (!isOwner && !isManager) {
          return { status: 403, error: { code: 'FORBIDDEN', message: 'Only workspace owners and managers can delete milestones' } };
        }
        this.data.milestones.splice(mIdx, 1);
        if (this.data.tasks) {
          this.data.tasks.forEach((t: any) => {
            if (t.milestone_id === mlsId) {
              t.milestone_id = null;
            }
          });
        }
        this.save(this.data);
        return { status: 200, data: { success: true, id: mlsId } };
      }
    }

    // 6.5 Blockers & AI Resolution Studio
    if (clean === 'blockers') {
      if (method === 'GET') {
        let blockers = this.data.blockers || [];
        const taskIdParam = searchParams.get('task_id');
        const projectIdParam = searchParams.get('project_id');
        const userIdParam = searchParams.get('user_id');
        const statusParam = searchParams.get('status');
        const searchParam = (searchParams.get('search') || '').toLowerCase().trim();

        if (taskIdParam) {
          blockers = blockers.filter((b: any) => b.task_id === taskIdParam);
        }
        if (projectIdParam) {
          const prj = this.data.projects.find((p: any) => p.id === projectIdParam || p.slug === projectIdParam);
          const pId = prj ? prj.id : projectIdParam;
          const pSlug = prj?.slug;
          blockers = blockers.filter((b: any) => b.project_id === pId || (pSlug && b.project_id === pSlug));
        }
        if (userIdParam) {
          blockers = blockers.filter((b: any) => b.user_id === userIdParam);
        }
        if (statusParam && statusParam !== 'all') {
          blockers = blockers.filter((b: any) => b.status === statusParam);
        }
        if (searchParam) {
          blockers = blockers.filter((b: any) =>
            (b.blocker_description && b.blocker_description.toLowerCase().includes(searchParam)) ||
            (b.ai_diagnosis && b.ai_diagnosis.toLowerCase().includes(searchParam)) ||
            (b.resolution_notes && b.resolution_notes.toLowerCase().includes(searchParam)) ||
            (b.task_title && b.task_title.toLowerCase().includes(searchParam)) ||
            (b.user_name && b.user_name.toLowerCase().includes(searchParam))
          );
        }

        const sorted = [...blockers].sort((a: any, b: any) =>
          (b.created_at || '').localeCompare(a.created_at || '')
        );
        return { status: 200, data: sorted };
      }

      if (method === 'POST') {
        if (isViewer) {
          return { status: 403, error: { code: 'FORBIDDEN', message: 'Viewers have strictly read-only access' } };
        }
        const description = (
          body?.blocker_description ||
          body?.description ||
          body?.what_was_blocker ||
          body?.blocker ||
          ''
        ).trim();

        if (!description) {
          return { status: 400, error: { code: 'ERR_VALIDATION', message: 'Blocker description is required' } };
        }

        const aiDiagnosis = (
          body?.ai_diagnosis ||
          body?.ai_guidance ||
          body?.what_ai_said ||
          body?.ai_response ||
          ''
        ).trim();

        const resolutionNotes = (
          body?.resolution_notes ||
          body?.next_steps ||
          body?.what_after_that ||
          body?.developer_response ||
          body?.resolution ||
          ''
        ).trim();

        let taskId = body?.task_id || null;
        let taskTitle = body?.task_title || null;
        let projectId = body?.project_id || null;
        let projectName = body?.project_name || null;

        if (taskId) {
          const task: any = this.data.tasks.find((t: any) => t.id === taskId);
          if (task) {
            taskTitle = task.title;
            if (!projectId) projectId = task.project_id;
            if (!projectName) projectName = task.project_name;
            const blkStatus = body?.status || 'active';
            if (blkStatus !== 'resolved') {
              task.status = 'blocked';
              task.blocked_reason = description;
              task.updated_at = new Date().toISOString();
            }
          }
        }

        if (projectId && !projectName) {
          const prj = this.data.projects.find((p: any) => p.id === projectId || p.slug === projectId);
          if (prj) {
            projectName = prj.name;
            projectId = prj.id;
          }
        }

        const nowIso = new Date().toISOString();
        const blkStatus = (['active', 'investigating', 'resolved'].includes(body?.status) ? body.status : 'active');
        const blkSeverity = (['low', 'medium', 'high', 'critical'].includes(body?.severity) ? body.severity : 'high');

        const newBlocker: any = {
          id: 'blk_' + Math.random().toString(36).substring(2, 9),
          task_id: taskId,
          task_title: taskTitle,
          project_id: projectId,
          project_name: projectName,
          user_id: currentUser.id,
          user_name: currentUser.name,
          user_email: currentUser.email || null,
          user_avatar: currentUser.avatar_url || null,
          blocker_description: description,
          ai_diagnosis: aiDiagnosis,
          resolution_notes: resolutionNotes,
          status: blkStatus,
          severity: blkSeverity,
          resolved_at: blkStatus === 'resolved' ? nowIso : null,
          resolved_by_id: blkStatus === 'resolved' ? currentUser.id : null,
          resolved_by_name: blkStatus === 'resolved' ? currentUser.name : null,
          created_at: nowIso,
          updated_at: nowIso,
        };

        if (!this.data.blockers) this.data.blockers = [];
        this.data.blockers.unshift(newBlocker);

        this.data.activity_logs.unshift({
          id: 'act_' + Math.random().toString(36).substring(2, 9),
          actor_id: currentUser.id,
          actor_name: currentUser.name,
          action: 'log_blocker',
          entity_type: 'blocker',
          entity_id: newBlocker.id,
          entity_name: taskTitle ? `Blocker on ${taskTitle}` : (projectName ? `Blocker on ${projectName}` : 'Team Blocker'),
          description: `Logged blocker: "${description.substring(0, 80)}" with AI diagnosis.`,
          metadata: {
            task_id: taskId,
            project_id: projectId,
            severity: blkSeverity,
            status: blkStatus,
          } as any,
          created_at: nowIso,
        });

        if (['high', 'critical'].includes(blkSeverity) && blkStatus !== 'resolved') {
          this.sentEmails.push({
            to: 'techosolution9@gmail.com',
            subject: `[${blkSeverity.toUpperCase()} BLOCKER ALERT] ${projectName || 'Agency Project'}${taskTitle ? ' - Task: ' + taskTitle : ''}`,
            body: description,
            type: 'blocker_alert',
            timestamp: nowIso,
          });

          if (!(this.data as any).notifications) (this.data as any).notifications = [];
          const owners = (this.data.users || []).filter((u: any) => u.role === 'owner' || u.role === 'manager');
          const recipients = owners.length > 0 ? owners : [{ id: 'usr_owner_sample' }];
          for (const r of recipients) {
            (this.data as any).notifications.push({
              id: 'notif_' + Math.random().toString(36).substring(2, 9),
              user_id: r.id,
              title: `${blkSeverity.charAt(0).toUpperCase() + blkSeverity.slice(1)} Blocker Alert: ${projectName || 'Team Blocker'}`,
              message: `${currentUser.name} reported a ${blkSeverity} blocker on ${projectName || 'task'}: "${description.substring(0, 80)}"`,
              type: 'blocker_alert',
              link: '/blockers',
              read: false,
              created_at: nowIso,
            });
          }
        }

        this.save(this.data);
        return { status: 201, data: newBlocker };
      }
    }

    const blockerMatch = clean.match(/^blockers\/([^/]+)$/);
    if (blockerMatch) {
      const blkId = blockerMatch[1];
      if (!this.data.blockers) this.data.blockers = [];
      const bIdx = this.data.blockers.findIndex((b: any) => b.id === blkId);
      if (bIdx === -1) {
        return { status: 404, error: { code: 'NOT_FOUND', message: 'Blocker not found' } };
      }
      const targetB = this.data.blockers[bIdx];

      if (method === 'GET') {
        return { status: 200, data: targetB };
      }

      if (method === 'PUT' || method === 'PATCH') {
        if (isViewer) {
          return { status: 403, error: { code: 'FORBIDDEN', message: 'Viewers have strictly read-only access' } };
        }
        if (!isOwner && !isManager) {
          const isAuthor = targetB.user_id === currentUser.id;
          let isAssignedDev = false;
          if (targetB.task_id) {
            const task = this.data.tasks.find((t: any) => t.id === targetB.task_id);
            if (task && (task.assignee_id === currentUser.id || task.assignee_name === currentUser.name)) {
              isAssignedDev = true;
            }
          }
          if (!isAuthor && !isAssignedDev) {
            return { status: 403, error: { code: 'FORBIDDEN', message: 'Developers can only update their own blockers or blockers on their assigned tasks' } };
          }
        }

        const nowIso = new Date().toISOString();
        if (body?.blocker_description !== undefined) targetB.blocker_description = String(body.blocker_description).trim();
        if (body?.description !== undefined) targetB.blocker_description = String(body.description).trim();
        if (body?.what_was_blocker !== undefined) targetB.blocker_description = String(body.what_was_blocker).trim();
        if (body?.blocker !== undefined) targetB.blocker_description = String(body.blocker).trim();

        if (body?.ai_diagnosis !== undefined) targetB.ai_diagnosis = String(body.ai_diagnosis).trim();
        if (body?.ai_guidance !== undefined) targetB.ai_diagnosis = String(body.ai_guidance).trim();
        if (body?.what_ai_said !== undefined) targetB.ai_diagnosis = String(body.what_ai_said).trim();
        if (body?.ai_response !== undefined) targetB.ai_diagnosis = String(body.ai_response).trim();

        if (body?.resolution_notes !== undefined) targetB.resolution_notes = String(body.resolution_notes).trim();
        if (body?.next_steps !== undefined) targetB.resolution_notes = String(body.next_steps).trim();
        if (body?.what_after_that !== undefined) targetB.resolution_notes = String(body.what_after_that).trim();
        if (body?.developer_response !== undefined) targetB.resolution_notes = String(body.developer_response).trim();
        if (body?.resolution !== undefined) targetB.resolution_notes = String(body.resolution).trim();

        if (body?.severity && ['low', 'medium', 'high', 'critical'].includes(body.severity)) {
          targetB.severity = body.severity;
        }

        if (body?.status && ['active', 'investigating', 'resolved'].includes(body.status)) {
          const prevStatus = targetB.status;
          targetB.status = body.status;
          if (body.status === 'resolved' && prevStatus !== 'resolved') {
            targetB.resolved_at = nowIso;
            targetB.resolved_by_id = currentUser.id;
            targetB.resolved_by_name = currentUser.name;

            if (targetB.task_id) {
              const task: any = this.data.tasks.find((t: any) => t.id === targetB.task_id);
              if (task && task.status === 'blocked') {
                const otherActive = this.data.blockers.some(
                  (b: any) => b.task_id === targetB.task_id && b.id !== targetB.id && b.status !== 'resolved'
                );
                if (!otherActive) {
                  task.status = 'in_progress';
                  task.blocked_reason = null;
                  task.updated_at = nowIso;
                }
              }
            }
          } else if (body.status !== 'resolved' && prevStatus === 'resolved') {
            targetB.resolved_at = null;
            targetB.resolved_by_id = null;
            targetB.resolved_by_name = null;

            if (targetB.task_id) {
              const task: any = this.data.tasks.find((t: any) => t.id === targetB.task_id);
              if (task && task.status !== 'done') {
                task.status = 'blocked';
                task.blocked_reason = targetB.blocker_description;
                task.updated_at = nowIso;
              }
            }
          }
        }

        // Synchronize updated blocker description with task blocked_reason if task is currently blocked
        if (targetB.task_id && targetB.status !== 'resolved') {
          const task: any = this.data.tasks.find((t: any) => t.id === targetB.task_id);
          if (task && task.status === 'blocked') {
            task.blocked_reason = targetB.blocker_description;
            task.updated_at = nowIso;
          }
        }

        targetB.updated_at = nowIso;

        if (['high', 'critical'].includes(targetB.severity) && targetB.status !== 'resolved') {
          this.sentEmails.push({
            to: 'techosolution9@gmail.com',
            subject: `[${targetB.severity.toUpperCase()} BLOCKER ALERT] ${targetB.project_name || 'Agency Project'}${targetB.task_title ? ' - Task: ' + targetB.task_title : ''}`,
            body: targetB.blocker_description,
            type: 'blocker_alert',
            timestamp: nowIso,
          });

          if (!(this.data as any).notifications) (this.data as any).notifications = [];
          const owners = (this.data.users || []).filter((u: any) => u.role === 'owner' || u.role === 'manager');
          const recipients = owners.length > 0 ? owners : [{ id: 'usr_owner_sample' }];
          for (const r of recipients) {
            (this.data as any).notifications.push({
              id: 'notif_' + Math.random().toString(36).substring(2, 9),
              user_id: r.id,
              title: `${targetB.severity.charAt(0).toUpperCase() + targetB.severity.slice(1)} Blocker Updated: ${targetB.project_name || 'Team Blocker'}`,
              message: `Blocker updated on ${targetB.project_name || 'task'}: "${(targetB.blocker_description || '').substring(0, 80)}"`,
              type: 'blocker_alert',
              link: '/blockers',
              read: false,
              created_at: nowIso,
            });
          }
        }

        this.save(this.data);
        return { status: 200, data: targetB };
      }

      if (method === 'DELETE') {
        if (!isOwner && !isManager) {
          const isAuthor = targetB.user_id === currentUser.id;
          if (!isAuthor) {
            return { status: 403, error: { code: 'FORBIDDEN', message: 'Only workspace owners, managers, or author can delete blockers' } };
          }
        }
        const taskId = targetB.task_id;
        this.data.blockers.splice(bIdx, 1);

        if (taskId) {
          const task: any = this.data.tasks.find((t: any) => t.id === taskId);
          if (task && task.status === 'blocked') {
            const hasOtherActive = (this.data.blockers || []).some(
              (b: any) => b.task_id === taskId && b.status !== 'resolved'
            );
            if (!hasOtherActive) {
              task.status = 'in_progress';
              task.blocked_reason = null;
              task.updated_at = new Date().toISOString();
            }
          }
        }

        this.save(this.data);
        return { status: 200, data: { success: true, id: blkId } };
      }
    }

    // 7. Tasks
    if (clean === 'tasks') {
      if (method === 'GET') {
        let rawTasks = isDev
          ? this.data.tasks.filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name)
          : this.data.tasks;
        const projectIdParam = searchParams.get('project_id');
        if (projectIdParam) {
          const prj = this.data.projects.find((p: any) => p.id === projectIdParam || p.slug === projectIdParam);
          const pId = prj ? prj.id : projectIdParam;
          const pSlug = prj?.slug;
          rawTasks = rawTasks.filter((t: any) => t.project_id === pId || (pSlug && t.project_id === pSlug));
        }
        const milestoneIdParam = searchParams.get('milestone_id');
        if (milestoneIdParam) {
          if (milestoneIdParam === 'unlinked' || milestoneIdParam === 'null') {
            rawTasks = rawTasks.filter((t: any) => !t.milestone_id);
          } else {
            rawTasks = rawTasks.filter((t: any) => t.milestone_id === milestoneIdParam || String(t.milestone_id) === String(milestoneIdParam));
          }
        }
        const prjMap = new Map(this.data.projects.map((p: any) => [p.id, p.platform || 'direct']));
        this.data.projects.forEach((p: any) => {
          if (p.slug) prjMap.set(p.slug, p.platform || 'direct');
        });
        const tasks = rawTasks.map((t: any) => {
          const tBlockers = (this.data.blockers || []).filter((b: any) => b.task_id === t.id);
          return {
            ...t,
            project_platform: t.project_platform || prjMap.get(t.project_id) || 'direct',
            blockers_count: tBlockers.length,
            active_blockers_count: tBlockers.filter((b: any) => b.status !== 'resolved').length,
          };
        });
        return { status: 200, data: scrubFinancials(tasks, effectiveRole) };
      }
      if (method === 'POST') {
        if (isViewer) {
          return { status: 403, error: { message: 'Viewers have strictly read-only access' } };
        }
        let assigneeId = body?.assignee_id || null;
        let assigneeName = body?.assignee_name || null;
        if (isDev) {
          assigneeId = currentUser.id;
          assigneeName = currentUser.name;
        }
        const targetProj = body?.project_id ? this.data.projects.find((p: any) => p.id === body.project_id) : null;
        const resolvedProjectId = body?.project_id || (this.data.projects[0]?.id || 'prj_ecommerce');
        const resolvedProjectName = body?.project_name || targetProj?.name || 'General Project';

        // Validate milestone if provided
        let assignedMilestoneId = body?.milestone_id || null;
        let targetMilestone: any = null;
        if (assignedMilestoneId) {
          targetMilestone = (this.data.milestones || []).find((m: any) => m.id === assignedMilestoneId);
          if (!targetMilestone || targetMilestone.project_id !== resolvedProjectId) {
            return { status: 400, error: { code: 'INVALID_MILESTONE', message: 'Milestone does not belong to the selected project' } };
          }
          if (isDev) {
            const userTaskPrjIds = new Set(this.data.tasks.filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name).map((t: any) => t.project_id));
            if (!userTaskPrjIds.has(resolvedProjectId)) {
              return { status: 403, error: { code: 'FORBIDDEN', message: 'Developers can only create tasks in assigned projects' } };
            }
          }
        }

        const newTask: any = {
          id: 'tsk_' + Math.random().toString(36).substring(2, 9),
          project_id: resolvedProjectId,
          milestone_id: assignedMilestoneId,
          project_name: resolvedProjectName,
          title: (body?.title || 'New Task').trim(),
          description: body?.description || '',
          status: body?.status || 'todo',
          priority: body?.priority || 'medium',
          assignee_id: assigneeId,
          assignee_name: assigneeName,
          progress_percent: typeof body?.progress_percent === 'number' ? body.progress_percent : 0,
          due_date: body?.due_date || null,
          estimated_hours: Number(body?.estimated_hours) || 2.0,
          recorded_hours: typeof body?.recorded_hours === 'number' ? body.recorded_hours : (Number(body?.recorded_hours) || 0),
          checklist: [],
          comments: [],
          created_at: new Date().toISOString()
        };
        this.data.tasks.push(newTask);
        if (newTask.status === 'done' && assignedMilestoneId) {
          this.checkAndTriggerMilestoneCompletion(assignedMilestoneId, currentUser);
        }
        if (assignedMilestoneId && targetMilestone) {
          if (!this.data.activity_logs) this.data.activity_logs = [];
          this.data.activity_logs.unshift({
            id: 'act_' + Math.random().toString(36).substring(2, 9),
            actor_id: currentUser.id,
            actor_name: currentUser.name,
            actor_avatar: currentUser.avatar_url || null,
            action: 'add_milestone_task',
            entity_type: 'milestone',
            entity_id: targetMilestone.id,
            entity_name: targetMilestone.title,
            description: `Added task "${newTask.title}" to milestone "${targetMilestone.title}"`,
            metadata: {
              project_id: resolvedProjectId,
              task_id: newTask.id,
              milestone_id: targetMilestone.id,
            },
            created_at: new Date().toISOString()
          });
        }
        if (assigneeId && assigneeId !== currentUser.id) {
          if (!(this.data as any).notifications) (this.data as any).notifications = [];
          (this.data as any).notifications.push({
            id: 'notif_' + Math.random().toString(36).substring(2, 9),
            user_id: assigneeId,
            title: 'Task Assigned to You',
            message: `You were assigned to: ${newTask.title} (${newTask.project_name})`,
            type: 'assignment',
            link: `/tasks/${newTask.id}`,
            read: false,
            created_at: new Date().toISOString()
          });
        }
        this.save(this.data);
        return { status: 201, data: scrubFinancials(newTask, effectiveRole) };
      }
    }

    const taskMatch = clean.match(/^tasks\/([^/]+)(?:\/(comments|checklist|attachments)(?:\/([^/]+))?)?$/);
    if (taskMatch) {
      const taskId = taskMatch[1];
      const sub = taskMatch[2];
      const itemId = taskMatch[3];
      const task = this.data.tasks.find((t: any) => t.id === taskId);

      if (!task) {
        return { status: 404, error: { message: 'Task not found' } };
      }

      if (method === 'DELETE' && !sub) {
        if (!isOwner) {
          return { status: 403, error: { message: 'Only agency owners can delete tasks' } };
        }
        const idx = this.data.tasks.findIndex((t: any) => t.id === taskId);
        if (idx !== -1) {
          const delMId = this.data.tasks[idx].milestone_id;
          this.data.tasks.splice(idx, 1);
          if (this.data.blockers) {
            this.data.blockers = this.data.blockers.filter((b: any) => b.task_id !== taskId);
          }
          if (delMId) {
            this.checkAndTriggerMilestoneCompletion(delMId, currentUser);
          }
          this.save(this.data);
          return { status: 200, data: { success: true, id: taskId } };
        }
      }

      if (method === 'GET' && !sub) {
        const taskBlockers = (this.data.blockers || []).filter((b: any) => b.task_id === taskId);
        const enriched = {
          ...task,
          blockers: taskBlockers,
          blockers_count: taskBlockers.length,
        };
        return { status: 200, data: scrubFinancials(enriched, effectiveRole) };
      }

      if (method === 'PATCH' && !sub) {
        if (isDev) {
          if (body?.status === 'done') {
            return { status: 403, error: { message: 'Only owners and managers can approve tasks to done' } };
          }
          if (task.assignee_id !== currentUser.id && task.assignee_name !== currentUser.name) {
            return { status: 403, error: { message: 'Team members can only update their own assigned tasks' } };
          }
        }
        if (body?.project_id && !body?.project_name) {
          const targetProj = this.data.projects.find((p: any) => p.id === body.project_id);
          if (targetProj) {
            body.project_name = targetProj.name;
          }
        }
        const prevAssignee = task.assignee_id;
        const prevStatus = task.status;
        const prevMilestoneId = task.milestone_id || null;

        // If project_id changes and milestone_id was not provided, unlink existing milestone
        if (body?.project_id && body.project_id !== task.project_id && !('milestone_id' in (body || {}))) {
          if (task.milestone_id) {
            body.milestone_id = null;
          }
        }

        // Milestone change validation & audit
        if ('milestone_id' in (body || {})) {
          const newMilestoneId = body.milestone_id ? body.milestone_id : null;
          if (newMilestoneId) {
            const targetM = (this.data.milestones || []).find((m: any) => m.id === newMilestoneId);
            const targetProjId = body?.project_id || task.project_id;
            const targetProjObj = (this.data.projects || []).find((p: any) => p.id === targetProjId || p.slug === targetProjId);
            const matchesProj = targetM && (
              targetM.project_id === targetProjId ||
              (targetProjObj?.slug && targetM.project_id === targetProjObj.slug) ||
              (targetProjObj?.id && targetM.project_id === targetProjObj.id)
            );
            if (!targetM || !matchesProj) {
              return { status: 400, error: { code: 'INVALID_MILESTONE', message: 'Milestone does not belong to this project' } };
            }
            if (isDev) {
              const userTasks = this.data.tasks.filter((t: any) => t.assignee_id === currentUser.id || t.assignee_name === currentUser.name);
              const userTaskPrjIds = new Set<string>();
              userTasks.forEach((t: any) => {
                userTaskPrjIds.add(t.project_id);
                const prjObj = (this.data.projects || []).find((p: any) => p.id === t.project_id || p.slug === t.project_id);
                if (prjObj?.id) userTaskPrjIds.add(prjObj.id);
                if (prjObj?.slug) userTaskPrjIds.add(prjObj.slug);
              });
              if (!userTaskPrjIds.has(task.project_id)) {
                return { status: 403, error: { code: 'FORBIDDEN', message: 'Developers can only link milestones for assigned projects' } };
              }
            }
          }
        }

        Object.assign(task, body);
        if ('milestone_id' in (body || {})) {
          task.milestone_id = body.milestone_id ? body.milestone_id : null;
        }

        // Milestone change audit log
        if ('milestone_id' in (body || {}) && prevMilestoneId !== task.milestone_id) {
          if (!this.data.activity_logs) this.data.activity_logs = [];
          const allM = this.data.milestones || [];
          const oldM = prevMilestoneId ? allM.find((m: any) => m.id === prevMilestoneId) : null;
          const newM = task.milestone_id ? allM.find((m: any) => m.id === task.milestone_id) : null;

          if (!prevMilestoneId && task.milestone_id && newM) {
            this.data.activity_logs.unshift({
              id: 'act_' + Math.random().toString(36).substring(2, 9),
              actor_id: currentUser.id,
              actor_name: currentUser.name,
              actor_avatar: currentUser.avatar_url || null,
              action: 'link_milestone',
              entity_type: 'milestone',
              entity_id: newM.id,
              entity_name: newM.title,
              description: `Linked task "${task.title}" to milestone "${newM.title}"`,
              metadata: { project_id: task.project_id, task_id: task.id, milestone_id: newM.id },
              created_at: new Date().toISOString()
            });
          } else if (prevMilestoneId && !task.milestone_id) {
            this.data.activity_logs.unshift({
              id: 'act_' + Math.random().toString(36).substring(2, 9),
              actor_id: currentUser.id,
              actor_name: currentUser.name,
              actor_avatar: currentUser.avatar_url || null,
              action: 'unlink_milestone',
              entity_type: 'milestone',
              entity_id: prevMilestoneId,
              entity_name: oldM?.title || 'Milestone',
              description: `Unlinked task "${task.title}" from milestone "${oldM?.title || prevMilestoneId}"`,
              metadata: { project_id: task.project_id, task_id: task.id, milestone_id: prevMilestoneId },
              created_at: new Date().toISOString()
            });
          } else if (prevMilestoneId && task.milestone_id && newM) {
            this.data.activity_logs.unshift({
              id: 'act_' + Math.random().toString(36).substring(2, 9),
              actor_id: currentUser.id,
              actor_name: currentUser.name,
              actor_avatar: currentUser.avatar_url || null,
              action: 'move_milestone',
              entity_type: 'milestone',
              entity_id: newM.id,
              entity_name: newM.title,
              description: `Moved task "${task.title}" from milestone "${oldM?.title || prevMilestoneId}" to "${newM.title}"`,
              metadata: { project_id: task.project_id, task_id: task.id, from_milestone_id: prevMilestoneId, to_milestone_id: newM.id },
              created_at: new Date().toISOString()
            });
          }
        }
        if (body.status === 'done') {
          task.progress_percent = 100;
          task.completed_at = new Date().toISOString();
          if (task.milestone_id) {
            this.checkAndTriggerMilestoneCompletion(task.milestone_id, currentUser);
          }
        }
        if (body?.milestone_id !== undefined) {
          if (task.status === 'done' && task.milestone_id) {
            this.checkAndTriggerMilestoneCompletion(task.milestone_id, currentUser);
          }
          if (prevMilestoneId && prevMilestoneId !== task.milestone_id) {
            this.checkAndTriggerMilestoneCompletion(prevMilestoneId, currentUser);
          }
        }

        if (!(this.data as any).notifications) (this.data as any).notifications = [];
        if (task.assignee_id && task.assignee_id !== prevAssignee && task.assignee_id !== currentUser.id) {
          (this.data as any).notifications.push({
            id: 'notif_' + Math.random().toString(36).substring(2, 9),
            user_id: task.assignee_id,
            title: 'Task Assigned to You',
            message: `You were assigned to: ${task.title}`,
            type: 'assignment',
            link: `/tasks/${taskId}`,
            read: false,
            created_at: new Date().toISOString()
          });
        }
        if (body?.status && body.status !== prevStatus && task.assignee_id && task.assignee_id !== currentUser.id) {
          (this.data as any).notifications.push({
            id: 'notif_' + Math.random().toString(36).substring(2, 9),
            user_id: task.assignee_id,
            title: 'Task Status Updated',
            message: `Status changed to ${body.status.replace('_', ' ')} on: ${task.title}`,
            type: 'task_completed',
            link: `/tasks/${taskId}`,
            read: false,
            created_at: new Date().toISOString()
          });
        }

        this.save(this.data);
        return { status: 200, data: scrubFinancials(task, effectiveRole) };
      }

      if (method === 'POST' && sub === 'comments') {
        const u = this.getActiveUser() || this.data.users[0];
        const newCmt = {
          id: 'cmt_' + Math.random().toString(36).substring(2, 9),
          user_name: u.name,
          content: body?.content || '',
          created_at: new Date().toISOString()
        };
        task.comments = task.comments || [];
        (task.comments as any).push(newCmt);

        // Check for @mentions in comment content
        if (body?.content) {
          const mentionMatches = (body.content as string).match(/@([a-zA-Z0-9_]+(?:\s+[a-zA-Z0-9_]+)?)/g);
          if (mentionMatches) {
            for (const m of mentionMatches) {
              const name = m.replace(/^@/, '').trim().toLowerCase();
              const matchedU = this.data.users.find((user: any) => user.name.toLowerCase().includes(name));
              if (matchedU) {
                if (!this.data.notifications) (this.data as any).notifications = [];
                (this.data as any).notifications.push({
                  id: 'notif_' + Math.random().toString(36).substring(2, 9),
                  user_id: matchedU.id,
                  title: 'New Mention in Task',
                  message: `${u.name} mentioned you in ${task.title}`,
                  type: 'mention',
                  link: `/tasks/${taskId}`,
                  read: false,
                  created_at: new Date().toISOString()
                });
              }
            }
          }
        }

        this.save(this.data);
        return { status: 201, data: newCmt };
      }

      if (method === 'POST' && sub === 'checklist') {
        const newChk = {
          id: 'chk_' + Math.random().toString(36).substring(2, 9),
          text: body?.text || 'New item',
          completed: false
        };
        task.checklist = task.checklist || [];
        (task.checklist as any).push(newChk);
        this.save(this.data);
        return { status: 201, data: newChk };
      }

      if (method === 'PATCH' && sub === 'checklist' && itemId) {
        const item = (task.checklist || []).find((c: any) => c.id === itemId);
        if (item) {
          if (body.completed !== undefined) item.completed = body.completed;
          if (body.text !== undefined) item.text = body.text;
        }
        this.save(this.data);
        return { status: 200, data: task };
      }

      if (method === 'GET' && sub === 'attachments') {
        return { status: 200, data: (task as any).attachments || [] };
      }

      if (method === 'POST' && sub === 'attachments') {
        if (isViewer) {
          return { status: 403, error: { message: 'Viewers cannot upload attachments' } };
        }
        const size = Number(body?.size) || 1024;
        if (size > 10485760) {
          return { status: 400, error: { code: 'FILE_TOO_LARGE', message: 'Attachment exceeds the 10 MB limit.' } };
        }
        const u = this.getActiveUser() || this.data.users[0];
        const newAtt = {
          id: 'att_' + Math.random().toString(36).substring(2, 9),
          name: (body?.name || 'attachment.dat').trim(),
          size,
          size_formatted: body?.size_formatted || (size < 1048576 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1048576).toFixed(1)} MB`),
          mime_type: body?.mime_type || 'application/octet-stream',
          url: body?.url || '#',
          uploaded_by_id: u.id,
          uploaded_by_name: u.name,
          created_at: new Date().toISOString()
        };
        (task as any).attachments = (task as any).attachments || [];
        (task as any).attachments.push(newAtt);
        this.save(this.data);
        return { status: 201, data: newAtt };
      }

      if (method === 'DELETE' && sub === 'attachments' && itemId) {
        (task as any).attachments = (task as any).attachments || [];
        const attIdx = (task as any).attachments.findIndex((a: any) => a.id === itemId);
        if (attIdx === -1) {
          return { status: 404, error: { message: 'Attachment not found' } };
        }
        const targetAtt = (task as any).attachments[attIdx];
        if (!isOwner && !isManager && targetAtt.uploaded_by_id !== currentUser?.id) {
          return { status: 403, error: { message: 'Only workspace owners, managers, or file uploaders can delete attachments' } };
        }
        (task as any).attachments.splice(attIdx, 1);
        this.save(this.data);
        return { status: 200, data: { success: true, id: itemId } };
      }
    }

    // 8. Timer
    if (clean === 'time/timer' && method === 'GET') {
      return { status: 200, data: this.data.runningTimer };
    }
    if (clean === 'time/timer/start' && method === 'POST') {
      const u = this.getActiveUser() || this.data.users[0];
      const tsk = this.data.tasks.find((t: any) => t.id === body?.task_id);
      this.data.runningTimer = {
        user_id: u.id,
        task_id: body?.task_id,
        task_title: tsk ? tsk.title : 'General Task Work',
        project_name: tsk ? tsk.project_name : 'General',
        started_at: new Date().toISOString()
      };
      this.save(this.data);
      return { status: 200, data: this.data.runningTimer };
    }
    if (clean === 'time/timer/stop' && method === 'POST') {
      const u = this.getActiveUser() || this.data.users[0];
      const timer = this.data.runningTimer;
      this.data.runningTimer = null;
      if (timer) {
        const start = new Date(timer.started_at).getTime();
        const duration = Math.max(60, Math.floor((Date.now() - start) / 1000));
        const entry = {
          id: 'te_' + Math.random().toString(36).substring(2, 9),
          user_id: u.id,
          user_name: u.name,
          task_id: timer.task_id,
          task_title: timer.task_title,
          project_name: timer.project_name,
          duration_seconds: duration,
          duration_formatted: `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`,
          karachi_date: '2026-10-19',
          work_note: body?.work_note || 'Recorded timer session',
          is_manual: false,
          review_state: 'draft',
          created_at: new Date().toISOString()
        };
        this.data.timeEntries.push(entry);
      }
      this.save(this.data);
      return { status: 200, data: { stopped: true } };
    }

    // 9. Time entries
    if (clean === 'time-entries') {
      if (method === 'GET') {
        const entries = isDev
          ? this.data.timeEntries.filter((te: any) => te.user_id === currentUser.id)
          : this.data.timeEntries;
        return { status: 200, data: entries };
      }
      if (method === 'POST') {
        const entry = {
          id: 'te_' + Math.random().toString(36).substring(2, 9),
          user_id: currentUser.id,
          user_name: currentUser.name,
          task_id: body?.task_id || null,
          task_title: body?.task_title || 'Manual Work Entry',
          project_name: body?.project_name || 'General',
          duration_seconds: Math.round((Number(body?.duration_hours) || 1) * 3600),
          duration_formatted: `${body?.duration_hours || 1}h 00m`,
          karachi_date: body?.date || '2026-10-19',
          work_note: body?.work_note || '',
          is_manual: true,
          review_state: 'draft',
          created_at: new Date().toISOString()
        };
        this.data.timeEntries.push(entry);
        this.save(this.data);
        return { status: 201, data: entry };
      }
    }

    const timeEntryMatch = clean.match(/^time-entries\/([^/]+)$/);
    if (timeEntryMatch) {
      const teId = timeEntryMatch[1];
      const entry = (this.data.timeEntries || []).find((e: any) => e.id === teId);
      if (!entry) return { status: 404, error: { message: 'Time entry not found' } };
      if (method === 'DELETE') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can delete time entries' } };
        this.data.timeEntries = this.data.timeEntries.filter((e: any) => e.id !== teId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: teId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        Object.assign(entry, body);
        this.save(this.data);
        return { status: 200, data: entry };
      }
    }

    // 10. Timesheets submit
    if (clean === 'timesheets/submit' && method === 'POST') {
      for (const te of this.data.timeEntries) {
        if (te.user_id === currentUser.id && te.review_state === 'draft') {
          te.review_state = 'submitted';
        }
      }
      this.save(this.data);
      return { status: 200, data: { success: true } };
    }

    // 11. Team & Users
    if ((clean === 'team' || clean === 'users') && method === 'GET') {
      const team = this.data.users.map((u: any) => {
        const assigned = this.data.tasks.filter((t: any) =>
          t.assignee_id === u.id ||
          (t.assignee_name && (
            t.assignee_name.toLowerCase() === u.name.toLowerCase() ||
            u.name.toLowerCase().includes(t.assignee_name.toLowerCase()) ||
            t.assignee_name.toLowerCase().includes(u.name.toLowerCase())
          ))
        );
        const activeAssigned = assigned.filter((t: any) => !['done', 'completed'].includes((t.status || '').toLowerCase()));
        const assignedPrjIds = new Set(assigned.map((t: any) => t.project_id).filter(Boolean));
        return {
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          timezone: u.timezone || 'Asia/Karachi',
          is_active: u.is_active ?? true,
          last_login_at: u.last_login_at || null,
          last_login_ip: u.last_login_ip || null,
          last_login_user_agent: u.last_login_user_agent || null,
          active_tasks_count: activeAssigned.length,
          projects_count: assignedPrjIds.size,
          weekly_capacity_hours: 40,
          allocated_hours: 32.5
        };
      });
      return { status: 200, data: team };
    }
    if ((clean === 'team' || clean === 'users') && method === 'POST') {
      if (!isOwner) {
        return { status: 403, error: { message: 'Only workspace owners can invite team members' } };
      }
      const newUser: any = {
        id: 'usr_' + Math.random().toString(36).substring(2, 9),
        name: body?.name || 'New Member',
        email: (body?.email || '').toLowerCase().trim(),
        role: body?.role || 'team_member',
        password: body?.password || 'TempPass123!',
        avatar_url: null,
        timezone: body?.timezone || 'Asia/Karachi',
        is_active: true,
        created_at: new Date().toISOString()
      };
      this.data.users.push(newUser);
      this.save(this.data);
      return { status: 201, data: newUser };
    }

    // Team Workload & Capacity Heatmap
    if (clean === 'team/capacity' && method === 'GET') {
      const activeUsers = (this.data.users || []).filter((u: any) => u.is_active !== false);
      const capacityList = activeUsers.map((u: any) => {
        const userTasks = (this.data.tasks || []).filter(
          (t: any) =>
            (t.assignee_id === u.id ||
              (t.assignee_name && t.assignee_name.toLowerCase() === u.name.toLowerCase())) &&
            !['done', 'completed'].includes((t.status || '').toLowerCase())
        );
        const fourteenDaysAgo = new Date(Date.now() - 14 * 86400000).toISOString().substring(0, 10);
        const userEntries = (this.data.timeEntries || []).filter((e: any) => {
          if (e.user_id !== u.id) return false;
          const eDate = e.karachi_date || (e.created_at ? e.created_at.substring(0, 10) : '');
          return !eDate || eDate >= fourteenDaysAgo;
        });
        const totalSec = userEntries.reduce((acc: number, e: any) => acc + (Number(e.duration_seconds) || 0), 0);
        const weeklyHours = totalSec > 0 ? Math.round((totalSec / 3600) * 10) / 10 : Math.round(userTasks.length * 4.5 * 10) / 10;

        let capacityStatus: 'Optimal' | 'Heavy' | 'Overloaded' = 'Optimal';
        if (userTasks.length >= 7 || weeklyHours > 42) {
          capacityStatus = 'Overloaded';
        } else if (userTasks.length >= 4 || weeklyHours >= 35) {
          capacityStatus = 'Heavy';
        }

        const utilizationPercent = Math.min(150, Math.round((weeklyHours / 40) * 100));

        return {
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          active_tasks: userTasks.length,
          weekly_hours: weeklyHours,
          baseline_hours: 40,
          capacity_status: capacityStatus,
          utilization_percent: utilizationPercent,
        };
      });

      const optimalCount = capacityList.filter((c: any) => c.capacity_status === 'Optimal').length;
      const heavyCount = capacityList.filter((c: any) => c.capacity_status === 'Heavy').length;
      const overloadedCount = capacityList.filter((c: any) => c.capacity_status === 'Overloaded').length;
      const totalActiveTasks = capacityList.reduce((acc: number, c: any) => acc + c.active_tasks, 0);
      const totalWeeklyHours = Math.round(capacityList.reduce((acc: number, c: any) => acc + c.weekly_hours, 0) * 10) / 10;

      return {
        status: 200,
        data: capacityList,
        summary: {
          total_members: capacityList.length,
          optimal_count: optimalCount,
          heavy_count: heavyCount,
          overloaded_count: overloadedCount,
          total_active_tasks: totalActiveTasks,
          total_weekly_hours: totalWeeklyHours,
        },
      };
    }

    const teamUserMatch = clean.match(/^(?:team|users)\/([^/]+)$/);
    if (teamUserMatch) {
      const uId = teamUserMatch[1];
      const targetUser = this.data.users.find((u: any) => u.id === uId);
      if (!targetUser) return { status: 404, error: { message: 'User not found' } };

      if (method === 'DELETE') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can remove team members' } };
        if (targetUser.role === 'owner') {
          const activeOwners = this.data.users.filter((u: any) => u.role === 'owner' && u.is_active !== false);
          if (activeOwners.length <= 1) {
            return { status: 400, error: { code: 'ERR_LAST_ACTIVE_OWNER', message: 'Cannot delete the last active owner.' } };
          }
        }
        this.data.users = this.data.users.filter((u: any) => u.id !== uId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: uId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can manage team roles and status' } };
        const newRole = body?.role || targetUser.role;
        const newActive = body?.is_active !== undefined ? body.is_active : targetUser.is_active;
        if (targetUser.role === 'owner' && (newRole !== 'owner' || newActive === false)) {
          const activeOwners = this.data.users.filter((u: any) => u.role === 'owner' && u.is_active !== false);
          if (activeOwners.length <= 1) {
            return { status: 400, error: { code: 'ERR_LAST_ACTIVE_OWNER', message: 'Cannot demote or deactivate the last active owner.' } };
          }
        }
        Object.assign(targetUser, body);
        this.save(this.data);
        return { status: 200, data: targetUser };
      }
    }

    // 12. Reports
    if (clean === 'reports/executive-brief' && method === 'GET') {
      const invoices = this.data.invoices || [];
      const paidInvoices = invoices.filter((i: any) => i.status === 'paid');
      const revenueCollected = invoices.reduce((acc: number, i: any) => acc + (Number(i.amount_paid) || 0), 0) ||
        paidInvoices.reduce((acc: number, i: any) => acc + (Number(i.total_amount) || 0), 0);

      const overdueInvoices = invoices.filter((i: any) => i.status === 'overdue' || (i.due_date && i.due_date < new Date().toISOString().substring(0, 10) && i.status !== 'paid'));
      const overdueAmount = overdueInvoices.reduce((acc: number, i: any) => acc + (Number(i.balance_due || i.total_amount) || 0), 0);

      const milestones = this.data.milestones || [];
      const completedMilestones = milestones.filter((m: any) => m.status === 'completed');
      const pendingMilestones = milestones.filter((m: any) => m.status !== 'completed');
      const milestoneVelocityRate = milestones.length > 0 ? Math.round((completedMilestones.length / milestones.length) * 100) : 0;
      const upcomingMilestones = pendingMilestones.slice(0, 5).map((m: any) => ({
        id: m.id,
        title: m.title,
        due_date: m.due_date,
        status: m.status,
      }));

      const blockers = this.data.blockers || [];
      const activeBlockers = blockers.filter((b: any) => b.status === 'active' || !b.status);
      const criticalBlockers = activeBlockers.filter((b: any) => b.severity === 'critical');
      const highBlockers = activeBlockers.filter((b: any) => b.severity === 'high');

      const timeEntries = this.data.timeEntries || [];
      const totalSec = timeEntries.reduce((acc: number, e: any) => acc + (Number(e.duration_seconds) || 0), 0);
      const weeklyHours = totalSec > 0 ? Math.round((totalSec / 3600) * 10) / 10 : 38.5;

      const dateStr = new Date().toISOString().substring(0, 10);
      const briefMarkdown = `# Techosolution Project OS — Weekly Executive Brief
**Report Generated:** ${dateStr}
**Executive Recipient:** techosolution9@gmail.com

---

### 1. Revenue & Invoicing
- **Weekly Revenue Collected:** $${revenueCollected.toLocaleString()}
- **Overdue Invoices:** ${overdueInvoices.length} ($${overdueAmount.toLocaleString()})

### 2. Milestone Deliverables Velocity
- **Completed Milestones:** ${completedMilestones.length}/${milestones.length} (${milestoneVelocityRate}%)
- **Upcoming Deliverables:**
${upcomingMilestones.map((m: any) => `  - [${m.status.toUpperCase()}] ${m.title} (Due: ${m.due_date || 'No Date'})`).join('\n') || '  - None'}

### 3. Operational Roadblocks & Health
- **Active Blockers:** ${activeBlockers.length} (${criticalBlockers.length} Critical, ${highBlockers.length} High)
${criticalBlockers.map((b: any) => `  - 🔴 [CRITICAL] ${b.blocker_description || b.title}`).join('\n') || '  - No critical blockers currently logged.'}

### 4. Team Billable Hours
- **Total Hours Recorded:** ${weeklyHours} hrs across active client projects.
`;

      const briefHtml = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: sans-serif; color: #1e293b; background: #f8fafc; padding: 24px;">
  <div style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 24px;">
    <h2 style="color: #0f766e; margin-top: 0;">Weekly Executive Brief</h2>
    <p style="color: #64748b; font-size: 13px;">Generated for Techosolution Agency Management (${dateStr})</p>
    <div style="margin: 16px 0; padding: 12px; background: #f0fdf4; border-radius: 6px; border-left: 4px solid #10b981;">
      <strong>Weekly Revenue Collected:</strong> $${revenueCollected.toLocaleString()}<br>
      <strong>Overdue Invoices:</strong> ${overdueInvoices.length} ($${overdueAmount.toLocaleString()})
    </div>
    <div style="margin: 16px 0; padding: 12px; background: #f8fafc; border-radius: 6px; border-left: 4px solid #0f766e;">
      <strong>Milestone Velocity:</strong> ${completedMilestones.length}/${milestones.length} (${milestoneVelocityRate}% Completed)
    </div>
    <div style="margin: 16px 0; padding: 12px; background: #fef2f2; border-radius: 6px; border-left: 4px solid #ef4444;">
      <strong>Active Blockers:</strong> ${activeBlockers.length} (${criticalBlockers.length} Critical)
    </div>
    <div style="margin: 16px 0; padding: 12px; background: #f8fafc; border-radius: 6px;">
      <strong>Team Billable Hours:</strong> ${weeklyHours} hours
    </div>
    <p style="font-size: 11px; color: #94a3b8; text-align: center; margin-top: 24px;">Techosolution Agency OS Automated Intelligence</p>
  </div>
</body>
</html>`;

      return {
        status: 200,
        data: {
          generated_at: new Date().toISOString(),
          revenue_collected: revenueCollected,
          overdue_invoices: {
            count: overdueInvoices.length,
            amount: overdueAmount,
          },
          milestone_velocity: {
            total: milestones.length,
            completed: completedMilestones.length,
            completion_rate: milestoneVelocityRate,
            upcoming: upcomingMilestones,
          },
          active_blockers: {
            total: activeBlockers.length,
            critical: criticalBlockers.length,
            high: highBlockers.length,
            items: activeBlockers,
          },
          billable_hours: weeklyHours,
          brief_markdown: briefMarkdown,
          brief_html: briefHtml,
        },
      };
    }

    if (clean === 'reports/executive-brief/email' && method === 'POST') {
      const briefRes = this.handle('GET', 'reports/executive-brief');
      const briefData = briefRes.data;
      const emailRecord = {
        to: 'techosolution9@gmail.com',
        subject: `[EXECUTIVE BRIEF] Weekly Agency Performance & Operations Summary — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
        body: briefData.brief_markdown,
        type: 'executive_brief',
        timestamp: new Date().toISOString(),
      };
      this.sentEmails.push(emailRecord);
      return {
        status: 200,
        data: {
          message: 'Executive brief emailed successfully to techosolution9@gmail.com',
          sent_at: emailRecord.timestamp,
          recipient: 'techosolution9@gmail.com',
        },
      };
    }

    if (clean.startsWith('reports/team') && method === 'GET') {
      const month = '2026-10';
      let reports = this.data.users.map((u: any) => {
        const userEntries = (this.data.timeEntries || []).filter((e: any) => e.user_id === u.id);
        const totalSec = userEntries.reduce((acc: number, e: any) => acc + (Number(e.duration_seconds) || 0), 0);
        const apprSec = userEntries.filter((e: any) => e.review_state === 'approved').reduce((acc: number, e: any) => acc + (Number(e.duration_seconds) || 0), 0);
        const manSec = userEntries.filter((e: any) => e.is_manual).reduce((acc: number, e: any) => acc + (Number(e.duration_seconds) || 0), 0);

        const userTasks = (this.data.tasks || []).filter((t: any) => t.assignee_id === u.id || t.assignee_name === u.name);
        const completedTasks = userTasks.filter((t: any) => t.status === 'done').length;
        const onTime = userTasks.filter((t: any) => t.status === 'done' && (!t.due_date || !t.completed_at || t.completed_at.slice(0, 10) <= t.due_date)).length;
        const late = completedTasks - onTime;

        const totalHrs = totalSec > 0 ? Math.round((totalSec / 3600) * 10) / 10 : (completedTasks > 0 ? Math.round(completedTasks * 4.2 * 10) / 10 : 0);
        const apprHrs = apprSec > 0 ? Math.round((apprSec / 3600) * 10) / 10 : Math.round(totalHrs * 0.9 * 10) / 10;
        const manHrs = manSec > 0 ? Math.round((manSec / 3600) * 10) / 10 : 0;
        const onTimeRate = completedTasks > 0 ? Math.round((onTime / completedTasks) * 100) : 100;

        return {
          user_id: u.id,
          user_name: u.name,
          role: u.role,
          month,
          total_recorded_hours: totalHrs,
          recorded_hours: totalHrs,
          approved_hours: apprHrs,
          manual_hours: manHrs,
          tasks_completed: completedTasks,
          on_time_completions: onTime,
          late_completions: late,
          on_time_rate: {
            rate: onTimeRate,
            percentage: onTimeRate,
            display: onTimeRate + '%'
          },
          employee: u,
          reopened_cycles: 0
        };
      });
      if (isDev) {
        reports = reports.filter((r: any) => r.user_id === currentUser.id);
      }
      return { status: 200, data: reports };
    }

    // Reports: Channel Profitability Breakdown
    if (clean === 'reports/channel-profitability' && method === 'GET') {
      const channelStats: Record<'fiverr' | 'upwork' | 'direct', { gross: number; hours: number; projects_count: number; active_count: number }> = {
        fiverr: { gross: 0, hours: 0, projects_count: 0, active_count: 0 },
        upwork: { gross: 0, hours: 0, projects_count: 0, active_count: 0 },
        direct: { gross: 0, hours: 0, projects_count: 0, active_count: 0 },
      };

      for (const inv of (this.data.invoices || [])) {
        const p = ((inv.platform || 'direct') as string).toLowerCase();
        const key: 'fiverr' | 'upwork' | 'direct' = p.includes('fiverr') ? 'fiverr' : p.includes('upwork') ? 'upwork' : 'direct';
        channelStats[key].gross += Number(inv.amount_paid || inv.total_amount || 0);
      }

      for (const p of (this.data.projects || [])) {
        const plat = ((p.platform || 'direct') as string).toLowerCase();
        const key: 'fiverr' | 'upwork' | 'direct' = plat.includes('fiverr') ? 'fiverr' : plat.includes('upwork') ? 'upwork' : 'direct';
        channelStats[key].projects_count++;
        if (!['completed', 'cancelled'].includes(p.status || '')) {
          channelStats[key].active_count++;
        }
        channelStats[key].hours += Number(p.recorded_hours || 0);
        if (channelStats[key].gross === 0) {
          (channelStats[key] as any).project_budget_sum = ((channelStats[key] as any).project_budget_sum || 0) + Number((p as any).budget || (p as any).value || 0);
        }
      }

      for (const k of ['fiverr', 'upwork', 'direct'] as const) {
        if (channelStats[k].gross === 0 && (channelStats[k] as any).project_budget_sum) {
          channelStats[k].gross = (channelStats[k] as any).project_budget_sum;
        }
      }

      const commRates = { fiverr: 0.20, upwork: 0.10, direct: 0.00 };
      const breakdown: Record<string, any> = {};
      let totalGross = 0;
      let totalCuts = 0;
      let totalNet = 0;
      let totalHours = 0;

      for (const k of ['fiverr', 'upwork', 'direct'] as const) {
        const d = channelStats[k];
        const rate = commRates[k];
        const gross = Math.round(d.gross * 100) / 100;
        const cut = Math.round(gross * rate * 100) / 100;
        const net = Math.round((gross - cut) * 100) / 100;
        const margin = gross > 0 ? Math.round((net / gross) * 1000) / 10 : 100.0;
        const hours = Math.round(d.hours * 10) / 10;
        const yieldVal = hours > 0 ? Math.round((net / hours) * 100) / 100 : 0.0;

        totalGross += gross;
        totalCuts += cut;
        totalNet += net;
        totalHours += hours;

        breakdown[k] = {
          channel: k,
          commission_rate: rate,
          commission_percent: Math.round(rate * 100),
          gross_revenue: gross,
          platform_cut: cut,
          net_take_home: net,
          net_margin_percent: margin,
          engineering_hours: hours,
          effective_hourly_yield: yieldVal,
          projects_count: d.projects_count,
          active_count: d.active_count,
        };
      }

      totalGross = Math.round(totalGross * 100) / 100;
      totalCuts = Math.round(totalCuts * 100) / 100;
      totalNet = Math.round(totalNet * 100) / 100;
      totalHours = Math.round(totalHours * 10) / 10;
      const overallMargin = totalGross > 0 ? Math.round((totalNet / totalGross) * 1000) / 10 : 100.0;
      const overallYield = totalHours > 0 ? Math.round((totalNet / totalHours) * 100) / 100 : 0.0;

      const resp = {
        agency_totals: {
          gross_revenue: totalGross,
          platform_cuts_deducted: totalCuts,
          net_take_home: totalNet,
          net_margin_percent: overallMargin,
          engineering_hours: totalHours,
          effective_hourly_yield: overallYield,
        },
        channels: breakdown,
      };

      return { status: 200, data: scrubFinancials(resp, effectiveRole) };
    }

    // TOS Compliance Guard Check
    if (clean === 'compliance/check-tos' && method === 'POST') {
      const text = (body?.text || '') as string;
      const platform = (body?.platform || 'fiverr') as string;
      const result = checkTosCompliance(text, platform);
      return { status: 200, data: result };
    }

    // 13. Clients
    if (clean === 'clients') {
      if (method === 'GET') {
        const projects = this.data.projects || [];
        let clients = this.data.clients.map((c: any) => {
          const cId = c.id || '';
          const cName = c.name || '';
          const matchingProjects = projects.filter((p: any) =>
            (cId && p.client_id === cId) || (cName && p.client_name?.toLowerCase() === cName.toLowerCase())
          );
          const activeProjects = matchingProjects.filter((p: any) =>
            !p.is_archived && p.status !== 'completed' && p.status !== 'archived'
          );
          return {
            ...c,
            platform: c.platform || 'direct',
            platform_username: c.platform_username || null,
            platform_url: c.platform_url || null,
            projects_count: matchingProjects.length,
            active_projects_count: activeProjects.length,
          };
        });
        const platformParam = searchParams.get('platform');
        if (platformParam) {
          clients = clients.filter((c: any) => (c.platform || 'direct').toLowerCase() === platformParam.toLowerCase());
        }
        const searchParam = searchParams.get('search');
        if (searchParam) {
          const s = searchParam.toLowerCase();
          clients = clients.filter((c: any) =>
            c.name?.toLowerCase().includes(s) ||
            c.email?.toLowerCase().includes(s) ||
            c.secondary_email?.toLowerCase().includes(s) ||
            c.company?.toLowerCase().includes(s) ||
            c.country?.toLowerCase().includes(s) ||
            c.mobile?.toLowerCase().includes(s) ||
            c.phone?.toLowerCase().includes(s) ||
            c.address?.toLowerCase().includes(s) ||
            c.platform_username?.toLowerCase().includes(s)
          );
        }
        return { status: 200, data: scrubFinancials(clients, effectiveRole) };
      }
      if (method === 'POST') {
        const cName = body?.name?.trim();
        const cEmail = body?.email?.trim();
        if (!cName || !cEmail) {
          return {
            status: 400,
            error: { code: 'ERR_VALIDATION', message: 'Name and email are required for client.' },
          };
        }

        const clt = {
          id: 'clt_' + Math.random().toString(36).substring(2, 9),
          name: cName,
          company: body?.company || '',
          country: body?.country || '',
          platform: body?.platform || 'direct',
          platform_username: body?.platform_username || '',
          platform_url: body?.platform_url || '',
          email: cEmail,
          secondary_email: body?.secondary_email || '',
          phone: body?.phone || '',
          mobile: body?.mobile || '',
          address: body?.address || '',
          timezone: body?.timezone || '',
          currency: body?.currency || 'USD',
          notes: body?.notes || '',
          status: body?.status || 'active',
          projects_count: 0,
          active_projects_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        this.data.clients.push(clt);
        this.save(this.data);
        return { status: 201, data: scrubFinancials(clt, effectiveRole) };
      }
    }
    const clientMatch = clean.match(/^clients\/([^/]+)$/);
    if (clientMatch) {
      const cId = clientMatch[1];
      const clt = this.data.clients.find((c: any) => c.id === cId);
      if (!clt) return { status: 404, error: { message: 'Client not found' } };

      if (method === 'GET') {
        const projects = this.data.projects || [];
        const linkedProjects = projects.filter((p: any) =>
          p.client_id === cId || (clt.name && p.client_name?.toLowerCase() === clt.name.toLowerCase())
        );
        const activeProjects = linkedProjects.filter((p: any) =>
          !p.is_archived && p.status !== 'completed' && p.status !== 'archived'
        );
        const enriched = {
          ...clt,
          projects: scrubFinancials(linkedProjects, effectiveRole),
          projects_count: linkedProjects.length,
          active_projects_count: activeProjects.length,
        };
        return { status: 200, data: scrubFinancials(enriched, effectiveRole) };
      }

      if (method === 'DELETE') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can delete clients' } };
        this.data.clients = this.data.clients.filter((c: any) => c.id !== cId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: cId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        const oldName = clt.name;
        Object.assign(clt, body, { updated_at: new Date().toISOString() });

        // If client name was changed, sync linked projects to prevent breaking relationship
        if (body.name && oldName && oldName !== body.name && this.data.projects) {
          for (const p of (this.data.projects as any[])) {
            if (p.client_id === cId || (p.client_name && p.client_name.toLowerCase() === oldName.toLowerCase())) {
              p.client_id = cId;
              p.client_name = body.name;
            }
          }
        }

        this.save(this.data);

        const projects = this.data.projects || [];
        const linkedProjects = projects.filter((p: any) =>
          p.client_id === cId || (clt.name && p.client_name?.toLowerCase() === clt.name.toLowerCase())
        );
        const activeProjects = linkedProjects.filter((p: any) =>
          !p.is_archived && p.status !== 'completed' && p.status !== 'archived'
        );
        const enriched = {
          ...clt,
          projects: scrubFinancials(linkedProjects, effectiveRole),
          projects_count: linkedProjects.length,
          active_projects_count: activeProjects.length,
        };
        return { status: 200, data: scrubFinancials(enriched, effectiveRole) };
      }
    }

    const clientPortalAccessMatch = clean.match(/^clients\/([^/]+)\/portal-access$/);
    if (clientPortalAccessMatch && method === 'POST') {
      if (!isOwner && !isManager) {
        return { status: 403, error: { code: 'FORBIDDEN', message: 'Only workspace owners and managers can generate client portal credentials' } };
      }
      const cId = clientPortalAccessMatch[1];
      const clt = (this.data.clients || []).find((c: any) => c.id === cId) as any;
      if (!clt) {
        return { status: 404, error: { code: 'NOT_FOUND', message: 'Client not found' } };
      }
      const token = 'tsos_clt_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
      const password = body?.password || ('Client!' + Math.random().toString(36).substring(2, 8) + '#26');
      const expiresAt = new Date(Date.now() + 90 * 86400000).toISOString();

      if (body?.email) clt.email = body.email;
      clt.portal_token = token;
      clt.portal_password = password;
      clt.portal_token_expires_at = expiresAt;
      clt.portal_enabled = true;
      clt.updated_at = new Date().toISOString();

      this.save(this.data);

      return {
        status: 200,
        data: {
          success: true,
          client_id: clt.id,
          client_name: clt.name,
          client_email: clt.email || '',
          portal_token: token,
          portal_url: `#/portal?token=${token}`,
          password_display: password,
          expires_at: expiresAt,
          message: 'Client portal credentials generated and onboarding invite prepared successfully'
        }
      };
    }

    // 14. Leads

    if (clean === 'leads') {
      if (method === 'GET') return { status: 200, data: scrubFinancials(this.data.leads, effectiveRole) };
      if (method === 'POST') {
        const ld = {
          id: 'ld_' + Math.random().toString(36).substring(2, 9),
          contact_name: body?.contact_name || 'New Lead',
          company: body?.company || '',
          email: body?.email || '',
          platform: body?.platform || 'direct',
          platform_username: body?.platform_username || null,
          status: body?.status || 'new',
          estimated_value: Number(body?.estimated_value) || 0,
          notes: body?.notes || '',
          created_at: new Date().toISOString()
        };
        this.data.leads.push(ld);
        this.save(this.data);
        return { status: 201, data: scrubFinancials(ld, effectiveRole) };
      }
    }
    const leadMatch = clean.match(/^leads\/([^/]+)$/);
    if (leadMatch) {
      const ldId = leadMatch[1];
      const ld = this.data.leads.find((l: any) => l.id === ldId);
      if (!ld) return { status: 404, error: { message: 'Lead not found' } };
      if (method === 'DELETE') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can delete leads' } };
        this.data.leads = this.data.leads.filter((l: any) => l.id !== ldId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: ldId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        Object.assign(ld, body);
        this.save(this.data);
        return { status: 200, data: scrubFinancials(ld, effectiveRole) };
      }
    }

    const leadConvertMatch = clean.match(/^leads\/([^/]+)\/convert$/);
    if (leadConvertMatch && method === 'POST') {
      const leadId = leadConvertMatch[1];
      const matched = this.data.leads.find((l: any) => l.id === leadId);
      if (matched) {
        matched.status = 'won';
        const leadPlatform = (matched as any).platform || 'direct';
        const leadUsername = (matched as any).platform_username || '';
        const clt = {
          id: 'clt_' + Math.random().toString(36).substring(2, 9),
          name: matched.contact_name,
          company: matched.company || '',
          country: '',
          platform: leadPlatform,
          platform_username: leadUsername || '',
          platform_url: '',
          email: matched.email || '',
          secondary_email: '',
          phone: '',
          mobile: '',
          address: '',
          timezone: '',
          currency: 'USD',
          notes: matched.notes || '',
          projects_count: 1,
          active_projects_count: 1,
          status: 'active',
          created_at: new Date().toISOString()
        };
        this.data.clients.push(clt);
        const prj = {
          id: 'prj_' + Math.random().toString(36).substring(2, 9),
          slug: matched.company.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-onboarding',
          name: matched.company + ' Client Onboarding',
          client_name: clt.name,
          client_id: clt.id,
          client_username: leadUsername,
          platform: leadPlatform,
          platform_url: null,
          platform_order_id: null,
          status: 'active',
          priority: 'high',
          progress_percent: 0,
          progress_mode: 'automatic',
          manual_override_reason: null,
          total_tasks: 0,
          completed_tasks: 0,
          recorded_hours: 0,
          deadline: '2026-11-20',
          created_at: new Date().toISOString()
        };
        this.data.projects.push(prj);
        this.save(this.data);
        return { status: 200, data: { client: scrubFinancials(clt, effectiveRole), project: scrubFinancials(prj, effectiveRole) } };
      }
    }

    // 15. Finances & Invoices
    if ((clean === 'finances/summary' || clean === 'finance/summary') && method === 'GET') {
      let income = 0;
      let expenses = 0;
      for (const f of this.data.finances || []) {
        if (f.type === 'income') income += Number(f.amount);
        if (f.type === 'expense') expenses += Number(f.amount);
      }
      const curr = (this.data.finances && this.data.finances.length > 0) ? this.data.finances[0].currency : 'USD';

      let totalInvoiced = 0;
      let collectedRevenue = 0;
      let outstandingReceivables = 0;
      let overdueCount = 0;
      let overdueAmount = 0;
      const todayStr = new Date().toISOString().substring(0, 10);
      const platforms: Record<string, number> = { Fiverr: 0, Upwork: 0, Direct: 0, Other: 0 };
      const projectValuesByPlatform: Record<string, number> = { Fiverr: 0, Upwork: 0, Direct: 0, Other: 0 };

      const clientPlatMap = new Map<string, string>();
      for (const c of (this.data as any).clients || []) {
        const plat = (c.platform || 'direct').toLowerCase();
        if (c.id) clientPlatMap.set(c.id, plat);
        if (c.name) clientPlatMap.set(c.name.toLowerCase(), plat);
      }

      for (const inv of (this.data as any).invoices || []) {
        const invTotal = Number(inv.total_amount) || 0;
        const invPaid = Number(inv.amount_paid) || 0;
        const invDue = Number(inv.balance_due) ?? Math.max(0, invTotal - invPaid);
        const dueDate = inv.due_date;
        const status = inv.status || 'unpaid';

        if (status !== 'draft') {
          totalInvoiced += invTotal;
        }
        collectedRevenue += invPaid;

        if (status !== 'paid' && status !== 'draft') {
          outstandingReceivables += invDue;
        }

        const isOverdue = status === 'overdue' || (status !== 'paid' && status !== 'draft' && !!dueDate && dueDate < todayStr);
        if (isOverdue) {
          overdueCount++;
          overdueAmount += invDue;
        }

        const cPlat = (inv.client_id && clientPlatMap.get(inv.client_id)) ||
                     (inv.client_name && clientPlatMap.get(inv.client_name.toLowerCase())) ||
                     inv.platform || 'direct';
        let p = cPlat.charAt(0).toUpperCase() + cPlat.slice(1).toLowerCase();
        if (!(p in platforms)) p = 'Other';
        platforms[p] += invPaid;
      }

      for (const prj of (this.data as any).projects || []) {
        const cPlat = (prj.client_id && clientPlatMap.get(prj.client_id)) ||
                     (prj.client_name && clientPlatMap.get(prj.client_name.toLowerCase())) ||
                     prj.platform || 'direct';
        let p = cPlat.charAt(0).toUpperCase() + cPlat.slice(1).toLowerCase();
        if (!(p in projectValuesByPlatform)) p = 'Other';
        const pVal = Number(prj.budget || prj.value || prj.estimated_value || 0);
        projectValuesByPlatform[p] += pVal;
      }

      const effectiveIncome = income > 0 ? income : collectedRevenue;
      const netProfit = effectiveIncome - expenses;
      const profitMargin = effectiveIncome > 0 ? Math.round(((effectiveIncome - expenses) / effectiveIncome) * 1000) / 10 : 0;

      return {
        status: 200,
        data: {
          currency: curr,
          total_income: effectiveIncome,
          total_received: effectiveIncome,
          total_expenses: expenses,
          total_paid: expenses,
          net_profit: netProfit,
          profit: netProfit,
          pending_receivables: Math.round(outstandingReceivables * 100) / 100,
          profit_margin_percent: profitMargin,
          total_invoiced: Math.round(totalInvoiced * 100) / 100,
          collected_revenue: Math.round(collectedRevenue * 100) / 100,
          outstanding_receivables: Math.round(outstandingReceivables * 100) / 100,
          overdue_invoices_count: overdueCount,
          overdue_amount: Math.round(overdueAmount * 100) / 100,
          revenue_by_platform: platforms,
          project_values_by_platform: projectValuesByPlatform
        }
      };
    }
    if (clean === 'settings' && method === 'GET') {
      return { status: 200, data: { status: 'ok', app: 'Techosolution Project OS', version: '2.1.0' } };
    }
    if (clean === 'finances' || clean === 'finance') {
      if (method === 'GET') return { status: 200, data: this.data.finances };
      if (method === 'POST') {
        const tx = {
          id: 'fn_' + Math.random().toString(36).substring(2, 9),
          type: body?.type || 'income',
          amount: Number(body?.amount) || 0,
          currency: body?.currency || 'USD',
          category: body?.category || 'General',
          description: body?.description || '',
          date: body?.date || '2026-10-19',
          created_at: new Date().toISOString()
        };
        this.data.finances.push(tx);
        this.save(this.data);
        return { status: 201, data: tx };
      }
    }
    const financeMatch = clean.match(/^finances\/([^/]+)$/);
    if (financeMatch) {
      const fId = financeMatch[1];
      const tx = this.data.finances.find((f: any) => f.id === fId);
      if (!tx) return { status: 404, error: { message: 'Transaction not found' } };
      if (method === 'DELETE') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can delete transactions' } };
        this.data.finances = this.data.finances.filter((f: any) => f.id !== fId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: fId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        Object.assign(tx, body);
        this.save(this.data);
        return { status: 200, data: tx };
      }
    }

    // Invoices Export
    if (clean === 'invoices/export' && method === 'GET') {
      const sanitizeCsv = (val: any) => {
        if (val === null || val === undefined) return '';
        let str = String(val);
        const dangerous = ['=', '+', '-', '@', '\t', '\r', '%'];
        for (const d of dangerous) {
          if (str.startsWith(d)) {
            str = "'" + str;
            break;
          }
        }
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
          str = '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const headers = ['Invoice Number', 'Client Name', 'Project Name', 'Platform', 'Issue Date', 'Due Date', 'Subtotal', 'Discount', 'Tax Amount', 'Total Amount', 'Amount Paid', 'Balance Due', 'Currency', 'Status', 'Payment Notes'];
      const rows = [headers.join(',')];
      for (const inv of (this.data as any).invoices || []) {
        rows.push([
          sanitizeCsv(inv.invoice_number),
          sanitizeCsv(inv.client_name),
          sanitizeCsv(inv.project_name || ''),
          sanitizeCsv(inv.platform || 'Direct'),
          sanitizeCsv(inv.issue_date),
          sanitizeCsv(inv.due_date || ''),
          sanitizeCsv(inv.subtotal),
          sanitizeCsv(inv.discount),
          sanitizeCsv(inv.tax_amount),
          sanitizeCsv(inv.total_amount),
          sanitizeCsv(inv.amount_paid),
          sanitizeCsv(inv.balance_due),
          sanitizeCsv(inv.currency),
          sanitizeCsv(inv.status),
          sanitizeCsv(inv.payment_notes || '')
        ].join(','));
      }
      return { status: 200, data: rows.join('\n') };
    }

    // Invoices List & Create
    if (clean === 'invoices') {
      if (!(this.data as any).invoices) {
        (this.data as any).invoices = getDefaultInvoices();
      }

      if (method === 'GET') {
        let invoices = [...(this.data as any).invoices];
        const todayStr = new Date().toISOString().substring(0, 10);

        for (const inv of invoices) {
          if (inv.status !== 'paid' && inv.status !== 'draft') {
            if (inv.due_date && inv.due_date < todayStr) {
              inv.status = 'overdue';
            }
          }
        }

        const statusParam = searchParams.get('status');
        if (statusParam && statusParam !== 'all') {
          const statuses = statusParam.toLowerCase().split(',').map((s: string) => s.trim());
          invoices = invoices.filter((i: any) => statuses.includes((i.status || '').toLowerCase()));
        }

        const clientId = searchParams.get('client_id');
        if (clientId && clientId !== 'all') {
          invoices = invoices.filter((i: any) => i.client_id === clientId);
        }

        const projectId = searchParams.get('project_id');
        if (projectId && projectId !== 'all') {
          invoices = invoices.filter((i: any) => i.project_id === projectId);
        }

        const dateFrom = searchParams.get('date_from');
        if (dateFrom) {
          invoices = invoices.filter((i: any) => i.issue_date >= dateFrom);
        }

        const dateTo = searchParams.get('date_to');
        if (dateTo) {
          invoices = invoices.filter((i: any) => i.issue_date <= dateTo);
        }

        const q = searchParams.get('search')?.toLowerCase().trim();
        if (q) {
          invoices = invoices.filter((i: any) =>
            (i.invoice_number && i.invoice_number.toLowerCase().includes(q)) ||
            (i.client_name && i.client_name.toLowerCase().includes(q)) ||
            (i.project_name && i.project_name.toLowerCase().includes(q)) ||
            (i.payment_notes && i.payment_notes.toLowerCase().includes(q))
          );
        }

        invoices.sort((a: any, b: any) => {
          const cmp = (b.issue_date || '').localeCompare(a.issue_date || '');
          if (cmp !== 0) return cmp;
          return (b.created_at || '').localeCompare(a.created_at || '');
        });

        return { status: 200, data: invoices };
      }

      if (method === 'POST') {
        const clientId = body?.client_id?.trim();
        if (!clientId) {
          return { status: 400, error: { code: 'ERR_VALIDATION', message: 'Client ID is required' } };
        }
        if (!Array.isArray(body?.line_items) || body.line_items.length === 0) {
          return { status: 400, error: { code: 'ERR_VALIDATION', message: 'At least one line item is required' } };
        }

        let clientName = body.client_name !== undefined ? String(body.client_name) : '';
        let clientEmail = body.client_email !== undefined ? String(body.client_email) : '';
        const client = this.data.clients.find((c: any) => c.id === clientId);
        if (client) {
          if (!clientName) clientName = client.name;
          if (!clientEmail) clientEmail = client.email || '';
        }

        const projectId = body.project_id || null;
        let projectName = body.project_name !== undefined ? String(body.project_name) : '';
        if (projectId && !projectName) {
          const proj = this.data.projects.find((p: any) => p.id === projectId);
          if (proj) projectName = proj.name;
        }

        let subtotal = 0;
        const processedItems = body.line_items.map((it: any, idx: number) => {
          const qty = Math.max(0.01, Number(it.quantity) || 1);
          const uPrice = Number(it.unit_price) || 0;
          const amt = Math.round(qty * uPrice * 100) / 100;
          subtotal += amt;
          return {
            id: it.id || `li_${idx + 1}_${Math.random().toString(36).substring(2, 7)}`,
            description: it.description?.trim() || 'Service Item',
            quantity: qty,
            unit_price: uPrice,
            amount: amt
          };
        });

        subtotal = Math.round(subtotal * 100) / 100;
        const discount = Math.max(0, Math.round((Number(body.discount) || 0) * 100) / 100);
        const taxPercent = Math.max(0, Math.round((Number(body.tax_percent) || 0) * 100) / 100);
        const taxBase = Math.max(0, subtotal - discount);
        const taxAmount = Math.round(taxBase * (taxPercent / 100) * 100) / 100;
        const totalAmount = Math.max(0, Math.round((subtotal - discount + taxAmount) * 100) / 100);

        let invNumber = body.invoice_number?.trim();
        if (!invNumber) {
          const year = new Date().getFullYear();
          let maxSeq = 0;
          for (const existing of (this.data as any).invoices) {
            const match = (existing.invoice_number || '').match(new RegExp(`^INV-${year}-(\\d+)$`));
            if (match) {
              const seq = parseInt(match[1], 10);
              if (seq > maxSeq) maxSeq = seq;
            }
          }
          invNumber = `INV-${year}-${String(maxSeq + 1).padStart(3, '0')}`;
        }

        const reqStatus = body.status?.trim();
        const dueDate = body.due_date || null;
        const todayStr = new Date().toISOString().substring(0, 10);
        let status = reqStatus === 'draft' ? 'draft' : 'unpaid';
        if (status !== 'draft' && dueDate && dueDate < todayStr) {
          status = 'overdue';
        }

        const nowIso = new Date().toISOString();
        const newInvoice: Invoice = {
          id: 'inv_' + Math.random().toString(36).substring(2, 9),
          invoice_number: invNumber,
          client_id: clientId,
          client_name: clientName || 'Unknown Client',
          client_email: clientEmail,
          project_id: projectId,
          project_name: projectName || null,
          platform: body.platform || 'Direct',
          line_items: processedItems,
          subtotal,
          discount,
          tax_percent: taxPercent,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          amount_paid: 0,
          balance_due: totalAmount,
          currency: body.currency || 'USD',
          status: status as any,
          issue_date: body.issue_date || todayStr,
          due_date: dueDate,
          payment_notes: body.payment_notes || '',
          payments: [],
          created_at: nowIso,
          updated_at: nowIso
        };

        (this.data as any).invoices.unshift(newInvoice);
        this.save(this.data);
        return { status: 201, data: newInvoice };
      }
    }

    // Invoices Record Payment
    const paymentMatch = clean.match(/^invoices\/([^/]+)\/payments$/);
    if (paymentMatch && method === 'POST') {
      const invId = paymentMatch[1];
      const inv = ((this.data as any).invoices || []).find((i: any) => i.id === invId);
      if (!inv) return { status: 404, error: { message: 'Invoice not found' } };

      const payAmount = Number(body?.amount) || 0;
      if (payAmount <= 0) {
        return { status: 400, error: { code: 'ERR_VALIDATION', message: 'Payment amount must be greater than zero' } };
      }

      const nowIso = new Date().toISOString();
      const newPayment: InvoicePayment = {
        id: 'pay_' + Math.random().toString(36).substring(2, 9),
        payment_date: body?.payment_date || nowIso.substring(0, 10),
        amount: Math.round(payAmount * 100) / 100,
        payment_method: body?.payment_method || 'Bank Transfer',
        transaction_ref: body?.transaction_ref || '',
        notes: body?.notes || '',
        created_at: nowIso
      };

      if (!inv.payments) inv.payments = [];
      inv.payments.push(newPayment);

      const totalPaid = Math.round(inv.payments.reduce((sum: number, p: any) => sum + Number(p.amount), 0) * 100) / 100;
      inv.amount_paid = totalPaid;
      inv.balance_due = Math.max(0, Math.round((Number(inv.total_amount) - totalPaid) * 100) / 100);

      if (inv.balance_due <= 0 && Number(inv.total_amount) > 0) {
        inv.status = 'paid';
      } else if (totalPaid > 0) {
        inv.status = 'partial';
      } else {
        inv.status = 'unpaid';
      }
      inv.updated_at = nowIso;

      // Sync to Finances
      this.data.finances.push({
        id: 'fn_' + Math.random().toString(36).substring(2, 9),
        type: 'income',
        amount: newPayment.amount,
        currency: inv.currency || 'USD',
        category: 'Invoice Payment',
        description: `Payment for ${inv.invoice_number} (${inv.client_name})`,
        date: newPayment.payment_date,
        project_name: inv.project_name || null,
        created_at: nowIso
      } as any);

      this.save(this.data);
      return { status: 201, data: inv, payment: newPayment };
    }

    // Invoices Dispatch via Email
    const sendMatch = clean.match(/^(?:invoices|invoice)\/([^/]+)\/(?:send|send-email)$/);
    if (sendMatch && method === 'POST') {
      const invId = sendMatch[1];
      const inv = ((this.data as any).invoices || []).find((i: any) => i.id === invId);
      if (!inv) return { status: 404, error: { message: 'Invoice not found' } };

      let recipientEmail = (body?.email || inv.client_email || '').replace(/[\r\n]/g, '').trim();
      if (!recipientEmail && inv.client_id) {
        const client = (this.data.clients || []).find((c: any) => c.id === inv.client_id);
        if (client?.email) recipientEmail = client.email.replace(/[\r\n]/g, '').trim();
      }
      if (!recipientEmail) {
        return {
          status: 400,
          error: { code: 'ERR_VALIDATION', message: 'Client has no email address configured' },
        };
      }

      // Status transition: draft -> unpaid if not already paid/partial
      const totalPaid = Number(inv.amount_paid) || 0;
      const balanceDue = Number(inv.balance_due ?? inv.total_amount) || 0;
      if (inv.status === 'draft') {
        if (balanceDue <= 0 && Number(inv.total_amount) > 0) {
          inv.status = 'paid';
        } else if (totalPaid > 0) {
          inv.status = 'partial';
        } else if (inv.due_date && new Date(inv.due_date) < new Date(new Date().toISOString().substring(0, 10))) {
          inv.status = 'overdue';
        } else {
          inv.status = 'unpaid';
        }
      }

      const nowIso = new Date().toISOString();
      inv.sent_at = nowIso;
      inv.sent_to_email = recipientEmail;
      inv.updated_at = nowIso;

      const actUser = this.getActiveUser() || { id: 'usr_techosolution9', name: 'Techosolution Admin' };
      if (!this.data.activity_logs) this.data.activity_logs = [];
      this.data.activity_logs.unshift({
        id: 'act_' + Math.random().toString(36).substring(2, 9),
        actor_id: actUser.id,
        actor_name: actUser.name,
        action: 'send_invoice',
        entity_type: 'invoice',
        entity_id: inv.id,
        entity_name: inv.invoice_number,
        description: `Dispatched invoice ${inv.invoice_number} to ${recipientEmail}`,
        metadata: {
          client_id: inv.client_id,
          recipient_email: recipientEmail,
          total_amount: inv.total_amount,
          balance_due: inv.balance_due,
          mail_sent: true,
        },
        created_at: nowIso,
      });

      this.save(this.data);
      return {
        status: 200,
        data: inv,
        message: 'Invoice dispatched successfully',
        mail_sent: true,
      };
    }

    // Invoices by ID
    const invIdMatch = clean.match(/^invoices\/([^/]+)$/);
    if (invIdMatch) {
      const invId = invIdMatch[1];
      const invIdx = ((this.data as any).invoices || []).findIndex((i: any) => i.id === invId);
      if (invIdx === -1) return { status: 404, error: { message: 'Invoice not found' } };
      const inv = (this.data as any).invoices[invIdx];

      if (method === 'GET') {
        return { status: 200, data: inv };
      }
      if (method === 'DELETE') {
        (this.data as any).invoices.splice(invIdx, 1);
        this.save(this.data);
        return { status: 200, data: { success: true, id: invId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        if (Array.isArray(body?.line_items)) {
          let subtotal = 0;
          inv.line_items = body.line_items.map((it: any, idx: number) => {
            const qty = Math.max(0.01, Number(it.quantity) || 1);
            const uPrice = Number(it.unit_price) || 0;
            const amt = Math.round(qty * uPrice * 100) / 100;
            subtotal += amt;
            return {
              id: it.id || `li_${idx + 1}_${Math.random().toString(36).substring(2, 7)}`,
              description: it.description?.trim() || 'Service Item',
              quantity: qty,
              unit_price: uPrice,
              amount: amt
            };
          });
          inv.subtotal = Math.round(subtotal * 100) / 100;
        }

        if (body?.discount !== undefined) inv.discount = Math.max(0, Math.round(Number(body.discount) * 100) / 100);
        if (body?.tax_percent !== undefined) inv.tax_percent = Math.max(0, Math.round(Number(body.tax_percent) * 100) / 100);

        const sub = Number(inv.subtotal) || 0;
        const disc = Number(inv.discount) || 0;
        const taxP = Number(inv.tax_percent) || 0;
        const taxBase = Math.max(0, sub - disc);
        inv.tax_amount = Math.round(taxBase * (taxP / 100) * 100) / 100;
        inv.total_amount = Math.max(0, Math.round((sub - disc + inv.tax_amount) * 100) / 100);

        const totalPaid = Math.round(((inv.payments || []).reduce((s: number, p: any) => s + Number(p.amount), 0)) * 100) / 100;
        inv.amount_paid = totalPaid;
        inv.balance_due = Math.max(0, Math.round((Number(inv.total_amount) - totalPaid) * 100) / 100);

        if (body?.status) {
          inv.status = body.status;
        } else if (inv.status === 'draft') {
          if (totalPaid > 0) {
            inv.status = (inv.balance_due <= 0 && Number(inv.total_amount) > 0) ? 'paid' : 'partial';
          }
        } else {
          if (inv.balance_due <= 0 && Number(inv.total_amount) > 0) {
            inv.status = 'paid';
          } else if (totalPaid > 0) {
            inv.status = 'partial';
          } else if (inv.due_date && inv.due_date < new Date().toISOString().substring(0, 10)) {
            inv.status = 'overdue';
          } else {
            inv.status = 'unpaid';
          }
        }

        if (body?.client_id !== undefined) inv.client_id = body.client_id;
        if (body?.client_name !== undefined) inv.client_name = body.client_name;
        if (body?.client_email !== undefined) inv.client_email = body.client_email;
        if (body?.project_id !== undefined) inv.project_id = body.project_id;
        if (body?.project_name !== undefined) inv.project_name = body.project_name;
        if (body?.platform !== undefined) inv.platform = body.platform;
        if (body?.currency !== undefined) inv.currency = body.currency;
        if (body?.issue_date !== undefined) inv.issue_date = body.issue_date;
        if (body?.due_date !== undefined) inv.due_date = body.due_date;
        if (body?.payment_notes !== undefined) inv.payment_notes = body.payment_notes;
        if (body?.invoice_number !== undefined) inv.invoice_number = body.invoice_number;
        inv.updated_at = new Date().toISOString();

        this.save(this.data);
        return { status: 200, data: inv };
      }
    }

    // 16. SEO
    if (clean === 'seo') {
      if (method === 'GET') return { status: 200, data: this.data.seo };
      if (method === 'POST') {
        const titleVal = body?.title || body?.item || '';
        const urlVal = body?.url || body?.target_url || '';
        const item = {
          id: 'seo_' + Math.random().toString(36).substring(2, 9),
          title: titleVal,
          item: titleVal,
          url: urlVal,
          target_url: urlVal,
          type: body?.type || 'on_page',
          category: body?.category || body?.type || 'General SEO',
          status: body?.status || 'pending',
          priority: body?.priority || 'medium',
          notes: body?.notes || ''
        };
        this.data.seo.push(item);
        this.save(this.data);
        return { status: 201, data: item };
      }
    }
    const seoMatch = clean.match(/^seo\/([^/]+)$/);
    if (seoMatch) {
      const sId = seoMatch[1];
      const item = this.data.seo.find((s: any) => s.id === sId);
      if (!item) return { status: 404, error: { message: 'SEO item not found' } };
      if (method === 'DELETE') {
        if (!isOwner) return { status: 403, error: { message: 'Only workspace owners can delete SEO items' } };
        this.data.seo = this.data.seo.filter((s: any) => s.id !== sId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: sId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        Object.assign(item, body);
        if (body.title) item.item = body.title;
        if (body.item) item.title = body.item;
        if (body.url) item.target_url = body.url;
        if (body.target_url) item.url = body.target_url;
        this.save(this.data);
        return { status: 200, data: item };
      }
    }

    // 17. Standups
    if ((clean === 'standups/digest' || clean === 'standup/digest') && method === 'GET') {
      const targetDate = searchParams.get('date') || ((this.data.standups && (this.data.standups[0] as any)?.karachi_date) || '2026-10-19');

      const dayStandups = (this.data.standups || []).filter((s: any) => s.karachi_date === targetDate);
      const teamMembers = (this.data.users || []).filter((u: any) => u.is_active && ['developer', 'team_member', 'manager', 'owner'].includes(u.role));
      const activeBlockers = ((this.data as any).blockers || []).filter((b: any) => ['active', 'investigating'].includes(b.status || 'active'));
      const atRiskMilestones = ((this.data as any).milestones || []).filter((m: any) => m.status !== 'completed');

      const submittedCount = dayStandups.length;
      const totalTeamCount = teamMembers.length;
      const participationRate = totalTeamCount > 0 ? Math.round((submittedCount / totalTeamCount) * 100) : 0;

      const yesterdayShipped: { user: string; content: string }[] = [];
      const todayPriorities: { user: string; content: string }[] = [];
      const reportedRoadblocks: { user: string; content: string }[] = [];

      for (const s of dayStandups) {
        const uName = s.user_name || 'Team Member';
        if (s.yesterday) yesterdayShipped.push({ user: uName, content: s.yesterday });
        if (s.today) todayPriorities.push({ user: uName, content: s.today });
        if (s.blockers && s.blockers.toLowerCase() !== 'none' && s.blockers.toLowerCase() !== 'no') {
          reportedRoadblocks.push({ user: uName, content: s.blockers });
        }
      }

      const criticalBlockers = activeBlockers.filter((b: any) => (b.severity || '').toLowerCase() === 'critical');
      const highBlockers = activeBlockers.filter((b: any) => (b.severity || '').toLowerCase() === 'high');

      let healthStatus = 'Optimal Velocity';
      let healthBadge = 'healthy';
      if (criticalBlockers.length > 0) {
        healthStatus = 'Critical Attention';
        healthBadge = 'critical';
      } else if (reportedRoadblocks.length > 0 || highBlockers.length > 0) {
        healthStatus = 'Needs Attention';
        healthBadge = 'warning';
      }

      const briefLines = [
        `🚀 *TECHOSOLUTION OS — EXECUTIVE DAILY STANDUP DIGEST*`,
        `📅 *Date:* ${targetDate} (Asia/Karachi PKT)`,
        `📊 *Team Capacity:* ${submittedCount}/${totalTeamCount} Engineers Logged (${participationRate}%) | *Health:* ${healthBadge === 'healthy' ? '🟢' : healthBadge === 'warning' ? '🟡' : '🔴'} ${healthStatus}`,
        ``,
        `*🎯 COMPLETED YESTERDAY:*`,
        ...(yesterdayShipped.length === 0
          ? ['• No completions recorded for this date.']
          : yesterdayShipped.map(y => `• *${y.user}:* ${y.content.trim()}`)),
        ``,
        `*⚡ TODAY'S FOCUS & SPRINT DELIVERABLES:*`,
        ...(todayPriorities.length === 0
          ? ["• No priorities recorded for this date."]
          : todayPriorities.map(t => `• *${t.user}:* ${t.content.trim()}`)),
        ``,
        `*⚠️ ROADBLOCKS & SYSTEM BLOCKERS:*`,
        ...(reportedRoadblocks.length === 0 && activeBlockers.length === 0
          ? ['• None. Clear execution runway.']
          : [
              ...reportedRoadblocks.map(r => `• *[Standup Blocker]* ${r.user}: ${r.content.trim()}`),
              ...activeBlockers.map((b: any) => `• *[${(b.severity || 'HIGH').toUpperCase()} Alert]* ${b.project_name || 'Agency'}: ${(b.blocker_description || '').trim()}`)
            ]),
        ``,
        `— Generated by Techosolution OS AI Synthesis Engine`
      ];

      const briefMarkdown = briefLines.join('\n');
      const digest = {
        date: targetDate,
        timezone: 'Asia/Karachi (PKT, UTC+5)',
        participation: {
          submitted_count: submittedCount,
          total_team_count: totalTeamCount,
          completion_rate_percent: participationRate
        },
        submitted_count: submittedCount,
        total_members: totalTeamCount,
        participation_rate: participationRate,
        health: (healthBadge === 'warning' ? 'attention_required' : healthBadge) as 'healthy' | 'attention_required' | 'critical',
        health_status: healthStatus,
        health_badge: healthBadge,
        summary: `${submittedCount} of ${totalTeamCount} engineers submitted updates. ${reportedRoadblocks.length + activeBlockers.length} roadblocks/blockers tracked.`,
        shipped_yesterday: yesterdayShipped.map(y => `${y.user}: ${y.content.trim()}`),
        tackling_today: todayPriorities.map(t => `${t.user}: ${t.content.trim()}`),
        active_blocker_strings: [
          ...reportedRoadblocks.map(r => `[Standup] ${r.user}: ${r.content.trim()}`),
          ...activeBlockers.map((b: any) => `[${(b.severity || 'ALERT').toUpperCase()}] ${b.project_name || 'Agency'}: ${(b.blocker_description || '').trim()}`)
        ],
        yesterday_highlights: yesterdayShipped,
        today_priorities: todayPriorities,
        reported_roadblocks: reportedRoadblocks,
        active_blockers: activeBlockers,
        at_risk_milestones: atRiskMilestones,
        member_entries: dayStandups,
        executive_brief_markdown: briefMarkdown,
        formatted_brief: briefMarkdown,
      };

      return { status: 200, data: digest };
    }

    if (clean === 'standups') {
      if (method === 'GET') return { status: 200, data: this.data.standups };
      if (method === 'POST') {
        const std = {
          id: 'std_' + Math.random().toString(36).substring(2, 9),
          user_id: currentUser.id,
          user_name: currentUser.name,
          yesterday: body?.yesterday || '',
          today: body?.today || '',
          blockers: body?.blockers || 'None',
          karachi_date: body?.karachi_date || body?.date || new Date().toISOString().substring(0, 10),
          created_at: new Date().toISOString()
        };
        this.data.standups.push(std);
        this.save(this.data);
        return { status: 201, data: std };
      }
    }
    const standupMatch = clean.match(/^standups\/([^/]+)$/);
    if (standupMatch) {
      const stdId = standupMatch[1];
      const std = this.data.standups.find((s: any) => s.id === stdId);
      if (!std) return { status: 404, error: { message: 'Standup not found' } };
      if (method === 'DELETE') {
        if (!isOwner && std.user_id !== currentUser.id) {
          return { status: 403, error: { message: 'Only workspace owners or author can delete standup' } };
        }
        this.data.standups = this.data.standups.filter((s: any) => s.id !== stdId);
        this.save(this.data);
        return { status: 200, data: { success: true, id: stdId } };
      }
      if (method === 'PATCH' || method === 'PUT') {
        Object.assign(std, body);
        this.save(this.data);
        return { status: 200, data: std };
      }
    }

    // 18. API Keys
    if (clean === 'api-keys' && method === 'GET') {
      const keys = (this.data as any).apiKeys || [
        {
          id: 'key_01',
          name: 'Claude Desktop Integration (Rotated)',
          key_prefix: 'tsos_live_e9d',
          masked_key: 'tsos_live_e9d...7a1',
          scopes: ['projects:read', 'time:read', 'team:read', 'reports:read'],
          created_at: new Date().toISOString(),
          last_used_at: null
        },
        {
          id: 'key_02',
          name: 'Cursor IDE Agent (Rotated)',
          key_prefix: 'tsos_live_4f2',
          masked_key: 'tsos_live_4f2...3c9',
          scopes: ['projects:read', 'projects:write', 'tasks:write', 'time:read'],
          created_at: new Date().toISOString(),
          last_used_at: null
        }
      ];
      return { status: 200, data: keys };
    }
    if (clean === 'api-keys' && method === 'POST') {
      const id = 'key_' + Math.random().toString(36).substring(2, 9);
      const secret = 'tsos_live_' + Array.from({ length: 24 }, () => Math.random().toString(36)[2]).join('');
      const newKey = {
        id,
        name: body?.name || 'New API Key',
        key_prefix: secret.substring(0, 13),
        masked_key: secret.substring(0, 13) + '...' + secret.slice(-3),
        raw_key: secret,
        scopes: body?.scopes || ['projects:read', 'time:read'],
        created_at: new Date().toISOString(),
        last_used_at: null
      };
      if (!(this.data as any).apiKeys) (this.data as any).apiKeys = [];
      (this.data as any).apiKeys.push(newKey);
      this.save(this.data);
      return { status: 201, data: newKey };
    }
    const keyMatch = clean.match(/^api-keys\/([^/]+)$/);
    if (keyMatch && method === 'DELETE') {
      const id = keyMatch[1];
      if ((this.data as any).apiKeys) {
        (this.data as any).apiKeys = (this.data as any).apiKeys.filter((k: any) => k.id !== id);
        this.save(this.data);
      }
      return { status: 200, data: { success: true } };
    }

    // 19. Notifications
    if (clean === 'notifications' && method === 'GET') {
      const u = this.getActiveUser();
      const notifs = ((this.data as any).notifications || []).filter(
        (n: any) => n.user_id === u?.id
      );
      return { status: 200, data: notifs.length > 0 ? notifs : (this.data as any).notifications || [] };
    }
    if ((clean === 'notifications/read-all' || clean === 'notifications/mark-all-read') && (method === 'PATCH' || method === 'POST')) {
      const u = this.getActiveUser();
      if ((this.data as any).notifications) {
        (this.data as any).notifications.forEach((n: any) => {
          if (n.user_id === u?.id || isOwner) {
            n.read = true;
          }
        });
        this.save(this.data);
      }
      return { status: 200, data: { success: true } };
    }
    const notifMatch = clean.match(/^notifications\/([^/]+)\/read$/);
    if (notifMatch && (method === 'PATCH' || method === 'POST')) {
      const id = notifMatch[1];
      if ((this.data as any).notifications) {
        const item = (this.data as any).notifications.find((n: any) => n.id === id);
        if (item) item.read = true;
        this.save(this.data);
      }
      return { status: 200, data: { success: true, id } };
    }
    if (clean === 'notifications' && method === 'POST') {
      const u = this.getActiveUser();
      const newNotif = {
        id: 'notif_' + Math.random().toString(36).substring(2, 9),
        user_id: body?.user_id || u?.id,
        title: (body?.title || 'Notification').trim(),
        message: (body?.message || '').trim(),
        type: body?.type || 'info',
        link: body?.link || '/tasks',
        read: false,
        created_at: new Date().toISOString()
      };
      if (!(this.data as any).notifications) (this.data as any).notifications = [];
      (this.data as any).notifications.push(newNotif);
      this.save(this.data);
      return { status: 201, data: newNotif };
    }

    // 20. MCP Tools Configuration (Owner ONLY)
    if ((clean === 'mcp/tools' || clean === 'mcp') && method === 'GET') {
      const states = (this.data as any).mcp_tool_states || {};
      const tools = CANONICAL_MCP_TOOLS.map((t) => ({
        ...t,
        is_active: states[t.name] !== undefined ? Boolean(states[t.name]) : true
      }));
      const activeCount = tools.filter((t) => t.is_active).length;
      return {
        status: 200,
        data: tools,
        tools,
        summary: {
          total: tools.length,
          active_count: activeCount,
          deactivated_count: tools.length - activeCount
        }
      };
    }

    if (clean === 'mcp/tools/batch' && method === 'POST') {
      const preset = (body?.preset || body?.action || 'enable_all').trim();
      if (!(this.data as any).mcp_tool_states) {
        (this.data as any).mcp_tool_states = {};
      }
      CANONICAL_MCP_TOOLS.forEach((t) => {
        if (preset === 'enable_all') {
          (this.data as any).mcp_tool_states[t.name] = true;
        } else if (preset === 'disable_destructive') {
          (this.data as any).mcp_tool_states[t.name] = t.safety !== 'destructive';
        } else if (preset === 'read_only') {
          (this.data as any).mcp_tool_states[t.name] = t.safety === 'read_only';
        } else if (preset === 'disable_all') {
          (this.data as any).mcp_tool_states[t.name] = false;
        }
      });
      this.save(this.data);
      const states = (this.data as any).mcp_tool_states;
      const tools = CANONICAL_MCP_TOOLS.map((t) => ({
        ...t,
        is_active: states[t.name] !== undefined ? Boolean(states[t.name]) : true
      }));
      return {
        status: 200,
        data: {
          preset,
          tools,
          active_count: tools.filter((t) => t.is_active).length,
          message: `Preset '${preset}' applied successfully.`
        }
      };
    }

    const mcpToolMatch = clean.match(/^mcp\/tools\/([a-zA-Z0-9_\-]+)$/);
    if (mcpToolMatch && method === 'PATCH') {
      const toolName = mcpToolMatch[1];
      const isActive = body?.is_active !== undefined ? Boolean(body.is_active) : true;
      if (!(this.data as any).mcp_tool_states) {
        (this.data as any).mcp_tool_states = {};
      }
      (this.data as any).mcp_tool_states[toolName] = isActive;
      this.save(this.data);
      return {
        status: 200,
        data: {
          name: toolName,
          is_active: isActive,
          updated_at: new Date().toISOString()
        }
      };
    }

    if (clean.startsWith('mcp/execute') && method === 'POST') {
      const toolName = body?.tool || body?.name || '';
      const states = (this.data as any).mcp_tool_states || {};
      if (states[toolName] === false) {
        return {
          status: 403,
          error: {
            code: 'ERR_TOOL_DEACTIVATED',
            message: `Tool '${toolName}' has been deactivated by the workspace administrator in MCP Settings.`
          }
        };
      }
      if (toolName === 'import_platform_order') {
        const args = body?.args || body?.arguments || body || {};
        return this.handle('POST', 'projects/import-order', args);
      }
      if (toolName === 'get_channel_profitability_breakdown') {
        return this.handle('GET', 'reports/channel-profitability');
      }
      if (toolName === 'check_channel_tos_compliance') {
        const args = body?.args || body?.arguments || body || {};
        return this.handle('POST', 'compliance/check-tos', args);
      }
      if (toolName === 'create_client_onboarding_invite') {
        const args = body?.args || body?.arguments || body || {};
        return this.handle('POST', `clients/${args.client_id}/portal-access`, args);
      }
      if (toolName === 'get_client_account_portal_overview') {
        const args = body?.args || body?.arguments || body || {};
        const query = args.token ? `?token=${encodeURIComponent(args.token)}` : (args.client_id ? `?client_id=${encodeURIComponent(args.client_id)}` : '');
        return this.handle('GET', `client-portal/dashboard${query}`);
      }
    }


    // 21. Branding Configuration Endpoints (Owner ONLY)
    if (clean === 'branding' && method === 'POST') {
      const current = (this.data as any).branding || {};
      for (const [key, val] of Object.entries({ logo_url: body?.logo_url, dark_logo_url: body?.dark_logo_url, favicon_url: body?.favicon_url })) {
        if (typeof val === 'string' && (val.includes('svg') || val.startsWith('data:image/svg+xml'))) {
          let text = val;
          if (val.includes('base64,')) {
            try { text = Buffer.from(val.split('base64,')[1], 'base64').toString('utf-8'); } catch {}
          }
          if (/<script[\s>]/i.test(text) || /javascript:/i.test(text) || /on[a-z]+\s*=/i.test(text)) {
            return { status: 400, error: { code: 'INVALID_SVG', message: `The uploaded ${key} contains forbidden script or event handler elements.` } };
          }
        }
      }
      const updated = {
        workspace_name: typeof body?.workspace_name === 'string' ? body.workspace_name.trim() : (current.workspace_name || 'Techosolution'),
        logo_url: body?.logo_url !== undefined ? body.logo_url : (current.logo_url ?? null),
        dark_logo_url: body?.dark_logo_url !== undefined ? body.dark_logo_url : (current.dark_logo_url ?? null),
        favicon_url: body?.favicon_url !== undefined ? body.favicon_url : (current.favicon_url ?? null),
        updated_at: new Date().toISOString()
      };
      (this.data as any).branding = updated;
      this.save(this.data);
      return { status: 200, data: updated, message: 'Workspace branding updated successfully.' };
    }

    if (clean === 'branding/reset' && method === 'POST') {
      const reset = {
        workspace_name: 'Techosolution',
        logo_url: null,
        dark_logo_url: null,
        favicon_url: null,
        updated_at: new Date().toISOString()
      };
      (this.data as any).branding = reset;
      this.save(this.data);
      return { status: 200, data: reset, message: 'Workspace branding reset to default.' };
    }

    if (clean === 'branding/upload' && method === 'POST') {
      const data = body?.data || body?.url || '';
      const type = body?.type || 'image/png';
      const filename = body?.filename || '';

      if ((type && type.includes('svg')) || (filename && filename.toLowerCase().endsWith('.svg'))) {
        let text = typeof data === 'string' ? data : '';
        if (text.includes('base64,')) {
          try { text = Buffer.from(text.split('base64,')[1], 'base64').toString('utf-8'); } catch {}
        } else {
          try {
            const decoded = Buffer.from(text, 'base64').toString('utf-8');
            if (decoded.includes('<svg') || decoded.includes('<')) {
              text = decoded;
            }
          } catch {}
        }
        if (/<script[\s>]/i.test(text) || /javascript:/i.test(text) || /on[a-z]+\s*=/i.test(text)) {
          return { status: 400, error: { code: 'INVALID_SVG', message: 'SVG file contains forbidden script or active event handlers.' } };
        }
      }

      const url = typeof data === 'string' && data.startsWith('data:') ? data : `data:${type};base64,${data}`;
      return { status: 200, data: { url, message: 'Asset uploaded successfully' } };
    }

    // 22. Activity & Audit Logs
    if (clean === 'activity' || clean === 'activity-logs') {
      if (!(this.data as any).activity_logs) {
        (this.data as any).activity_logs = getDefaultActivityLogs(new Date().toISOString());
      }
      if (method === 'GET') {
        let logs = [...(this.data as any).activity_logs];
        // Non-owners cannot see financial entity activity
        if (!isOwner) {
          logs = logs.filter((l: any) => l.entity_type !== 'finance');
        }
        // Team members and viewers cannot see lead or api_key activities
        if (isDev || isViewer) {
          logs = logs.filter((l: any) => l.entity_type !== 'lead' && l.entity_type !== 'api_key');
        }
        const entityType = searchParams.get('entity_type');
        if (entityType && entityType !== 'all') {
          logs = logs.filter((l: any) => l.entity_type === entityType);
        }
        const entityId = searchParams.get('entity_id');
        if (entityId) {
          logs = logs.filter((l: any) => l.entity_id === entityId);
        }
        const projectId = searchParams.get('project_id');
        if (projectId) {
          const prj = this.data.projects.find((p: any) => p.id === projectId || p.slug === projectId);
          const pId = prj ? prj.id : projectId;
          const pSlug = prj?.slug;
          const taskIds = new Set(
            this.data.tasks
              .filter((t: any) => t.project_id === pId || (pSlug && t.project_id === pSlug))
              .map((t: any) => t.id)
          );
          logs = logs.filter((l: any) =>
            l.entity_id === pId ||
            (pSlug && l.entity_id === pSlug) ||
            l.metadata?.project_id === pId ||
            (pSlug && l.metadata?.project_id === pSlug) ||
            l.project_id === pId ||
            (pSlug && l.project_id === pSlug) ||
            (l.entity_id && taskIds.has(l.entity_id))
          );
        }
        const actorId = searchParams.get('actor_id');
        if (actorId && actorId !== 'all') {
          logs = logs.filter((l: any) => l.actor_id === actorId);
        }
        const searchQ = searchParams.get('search');
        if (searchQ) {
          const q = searchQ.toLowerCase();
          logs = logs.filter((l: any) =>
            (l.description || '').toLowerCase().includes(q) ||
            (l.actor_name || '').toLowerCase().includes(q) ||
            (l.action || '').toLowerCase().includes(q) ||
            (l.entity_name || '').toLowerCase().includes(q)
          );
        }
        logs.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const sanitized = logs.map((l: any) => ({
          ...l,
          metadata: isOwner ? l.metadata : scrubFinancials(l.metadata, effectiveRole)
        }));
        return { status: 200, data: sanitized };
      }

      if (method === 'POST') {
        const u = this.getActiveUser() || this.data.users[0];
        const newLog = {
          id: 'act_' + Math.random().toString(36).substring(2, 9),
          actor_id: u.id,
          actor_name: u.name,
          actor_avatar: u.avatar_url || null,
          action: body?.action || 'custom_action',
          entity_type: body?.entity_type || 'system',
          entity_id: body?.entity_id || null,
          entity_name: body?.entity_name || null,
          description: body?.description || 'Workspace event recorded',
          metadata: isOwner ? (body?.metadata || null) : scrubFinancials(body?.metadata || null, effectiveRole),
          created_at: new Date().toISOString()
        };
        (this.data as any).activity_logs.unshift(newLog);
        this.save(this.data);
        return { status: 201, data: newLog };
      }
    }

    // 23. Promotions & Holiday Marketing Module
    if (clean === 'promotions/holidays' && method === 'GET') {
      const regionParam = searchParams.get('region');
      const monthParam = searchParams.get('month');
      const quarterParam = searchParams.get('quarter');
      const categoryParam = searchParams.get('category');
      const searchParam = searchParams.get('search');
      const refDateParam = searchParams.get('reference_date');

      let holidays = ALL_HOLIDAYS.map((h) => {
        const countdown = calculateCountdown(h.date, refDateParam || undefined);
        return {
          ...h,
          days_until: countdown.daysUntil,
          countdown_badge: countdown.badge,
        };
      });

      if (regionParam && regionParam.toLowerCase() !== 'all') {
        const r = regionParam.toLowerCase();
        holidays = holidays.filter((h) => h.region.toLowerCase() === r);
      }
      if (monthParam) {
        const m = parseInt(monthParam, 10);
        if (!isNaN(m)) holidays = holidays.filter((h) => h.month === m);
      }
      if (quarterParam && quarterParam.toLowerCase() !== 'all') {
        const q = quarterParam.toUpperCase();
        holidays = holidays.filter((h) => h.quarter === q);
      }
      if (categoryParam && categoryParam.toLowerCase() !== 'all') {
        const c = categoryParam.toLowerCase();
        holidays = holidays.filter((h) => h.category.toLowerCase() === c);
      }
      if (searchParam) {
        const s = searchParam.toLowerCase();
        holidays = holidays.filter(
          (h) =>
            h.title.toLowerCase().includes(s) ||
            h.description.toLowerCase().includes(s) ||
            h.suggested_offer.toLowerCase().includes(s) ||
            h.region_label.toLowerCase().includes(s)
        );
      }

      return { status: 200, data: holidays };
    }

    if (clean === 'promotions/templates' && method === 'GET') {
      const holidayId = searchParams.get('holiday_id');
      const category = searchParams.get('category');
      const search = searchParams.get('search');

      let templates = [...PROMOTION_TEMPLATES];
      if (holidayId && holidayId !== 'all') {
        templates = templates.filter((t) => t.holiday_id === holidayId);
      }
      if (category && category.toLowerCase() !== 'all') {
        const c = category.toLowerCase();
        templates = templates.filter((t) => t.category.toLowerCase() === c);
      }
      if (search) {
        const s = search.toLowerCase();
        templates = templates.filter(
          (t) =>
            t.title.toLowerCase().includes(s) ||
            t.subject.toLowerCase().includes(s) ||
            t.body.toLowerCase().includes(s)
        );
      }

      return { status: 200, data: templates };
    }

    if ((clean === 'promotions/send' || clean === 'promotions/log') && method === 'POST') {
      const clientId = body?.client_id;
      if (!clientId) {
        return {
          status: 400,
          error: { code: 'ERR_VALIDATION', message: 'Client ID is required.' },
        };
      }

      const client = (this.data.clients || []).find((c: any) => c.id === clientId);
      if (!client) {
        return {
          status: 404,
          error: { code: 'ERR_NOT_FOUND', message: 'Client not found.' },
        };
      }

      const holiday = body?.holiday_id ? ALL_HOLIDAYS.find((h) => h.id === body.holiday_id) : null;
      const template = body?.template_id
        ? PROMOTION_TEMPLATES.find((t) => t.id === body.template_id)
        : null;
      const holidayTitle = holiday?.title || body?.holiday_title || 'General Holiday Promotion';
      const templateTitle = template?.title || body?.template_title || 'Promotional Pitch';
      const u = this.getActiveUser() || this.data.users[0];
      const channel = body?.channel || 'email';
      const discountPercent = body?.discount_percent || template?.suggested_discount || '25%';
      const deadline = body?.deadline || holiday?.date || '';

      if (!(this.data as any).promotions_history) {
        (this.data as any).promotions_history = [];
      }

      let sentSubject = body?.sent_subject;
      let sentContent = body?.sent_content;
      if (template && (!sentSubject || !sentContent)) {
        const interpolated = interpolateTemplate(
          { subject: template.subject, body: template.body },
          {
            clientName: client.name,
            companyName: client.company || client.name,
            discountPercent,
            deadline,
            senderName: u.name,
          }
        );
        if (!sentSubject) sentSubject = interpolated.subject;
        if (!sentContent) sentContent = interpolated.body;
      }

      const newRecord = {
        id: 'promo_' + Math.random().toString(36).substring(2, 9),
        client_id: client.id,
        client_name: client.name,
        client_company: client.company || null,
        holiday_id: holiday?.id || body?.holiday_id || null,
        holiday_title: holidayTitle,
        template_id: template?.id || body?.template_id || null,
        template_title: templateTitle,
        channel,
        sent_subject: sentSubject || '',
        sent_content: sentContent || '',
        discount_percent: discountPercent,
        deadline,
        sent_by_id: u.id,
        sent_by_name: u.name,
        sent_at: new Date().toISOString(),
        status: 'sent',
      };

      (this.data as any).promotions_history.unshift(newRecord);

      // Append to Activity Logs
      if (!(this.data as any).activity_logs) {
        (this.data as any).activity_logs = getDefaultActivityLogs(new Date().toISOString());
      }
      (this.data as any).activity_logs.unshift({
        id: 'act_' + Math.random().toString(36).substring(2, 9),
        actor_id: u.id,
        actor_name: u.name,
        actor_avatar: u.avatar_url || null,
        action: 'send_promotion',
        entity_type: 'promotion',
        entity_id: newRecord.id,
        entity_name: `${holidayTitle} — ${client.name}`,
        description: `Dispatched holiday promotion "${holidayTitle}" to client ${client.name} via ${channel}.`,
        metadata: {
          client_id: client.id,
          holiday_id: newRecord.holiday_id,
          channel: newRecord.channel,
          discount_percent: newRecord.discount_percent,
        },
        created_at: new Date().toISOString(),
      });

      this.save(this.data);
      return { status: 201, data: newRecord, message: 'Promotion dispatched successfully.' };
    }

    if (clean === 'promotions/history' && method === 'GET') {
      if (!(this.data as any).promotions_history) {
        (this.data as any).promotions_history = [];
      }
      let history = [...(this.data as any).promotions_history];
      const clientId = searchParams.get('client_id');
      if (clientId && clientId !== 'all') {
        history = history.filter((h: any) => h.client_id === clientId);
      }
      const channel = searchParams.get('channel');
      if (channel && channel !== 'all') {
        history = history.filter((h: any) => h.channel === channel);
      }
      history.sort(
        (a: any, b: any) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
      );
      return { status: 200, data: history };
    }

    return { status: 404, error: { message: `Not found: ${clean}` } };
  }
}

export const mockStore = new MockStore();
